import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Container, Row, Col, Modal, ModalBody } from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  icon: string;
  content?: string;
}

interface Category {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  articleCount: number;
  color: string;
}

const HelpCenter: React.FC = () => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);

  const categories: Category[] = [
    {
      id: 'getting-started',
      name: 'Getting Started',
      icon: <i className="ri-play-circle-line" style={{ fontSize: '28px' }} />,
      description: 'Everything you need to set up your Coderom account and start selling',
      articleCount: 1,
      color: '#10b981'
    },
    {
      id: 'ai-listing',
      name: 'AI-Powered Product Listing',
      icon: <i className="ri-robot-line" style={{ fontSize: '28px' }} />,
      description: 'Create optimised eBay listings from Amazon ASINs using GPT-4o',
      articleCount: 1,
      color: '#6366f1'
    },
    {
      id: 'bulk-import',
      name: 'Bulk Product Import',
      icon: <i className="ri-upload-2-line" style={{ fontSize: '28px' }} />,
      description: 'Import hundreds of products at once using CSV or ASIN lists',
      articleCount: 1,
      color: '#f59e0b'
    },
    {
      id: 'templates',
      name: 'Professional Listing Templates',
      icon: <i className="ri-palette-line" style={{ fontSize: '28px' }} />,
      description: 'Choose, customise and manage 9 professional HTML listing templates',
      articleCount: 1,
      color: '#ec4899'
    },
    {
      id: 'pricing',
      name: 'Smart Repricing & Pricing Rules',
      icon: <i className="ri-money-pound-circle-line" style={{ fontSize: '28px' }} />,
      description: 'Configure pricing formulas, margins, and automatic repricing',
      articleCount: 1,
      color: '#f97316'
    },
    {
      id: 'safe-repricing',
      name: 'Safe Repricing Protection',
      icon: <i className="ri-shield-check-line" style={{ fontSize: '28px' }} />,
      description: 'Automatic protection against sudden Amazon price spikes',
      articleCount: 1,
      color: '#14b8a6'
    },
    {
      id: 'stock',
      name: 'Stock Synchronization & Inventory',
      icon: <i className="ri-stack-line" style={{ fontSize: '28px' }} />,
      description: '24/7 stock monitoring, safety buffers, and smart scheduling',
      articleCount: 1,
      color: '#8b5cf6'
    },
    {
      id: 'amazon-filters',
      name: 'Amazon Supplier Filters',
      icon: <i className="ri-amazon-line" style={{ fontSize: '28px' }} />,
      description: 'Filter Amazon suppliers by Prime, price, delivery, and condition',
      articleCount: 1,
      color: '#ef4444'
    },
    {
      id: 'orders',
      name: 'Order Management & Fulfillment',
      icon: <i className="ri-file-list-3-line" style={{ fontSize: '28px' }} />,
      description: 'Track orders, manage fulfillment, and monitor order status',
      articleCount: 1,
      color: '#3b82f6'
    },
    {
      id: 'safety-protection',
      name: 'Safety & Protection',
      icon: <i className="ri-shield-check-line" style={{ fontSize: '28px' }} />,
      description: 'Protect your store from VeRO claims with blocked brands and keywords',
      articleCount: 1,
      color: '#dc2626'
    },
    {
      id: 'auto-messages',
      name: 'Automated Buyer Messages',
      icon: <i className="ri-message-2-line" style={{ fontSize: '28px' }} />,
      description: 'Send automatic messages to buyers on order and shipment',
      articleCount: 1,
      color: '#a855f7'
    },
    {
      id: 'gmail-tracking',
      name: 'Gmail Integration & Tracking',
      icon: <i className="ri-mail-line" style={{ fontSize: '28px' }} />,
      description: 'Automatically capture tracking numbers from carrier emails',
      articleCount: 1,
      color: '#e11d48'
    },
    {
      id: 'ebay-multistore',
      name: 'eBay Policies & Multi-Store',
      icon: <i className="ri-store-2-line" style={{ fontSize: '28px' }} />,
      description: 'Manage eBay policies, multiple accounts, and marketplace settings',
      articleCount: 1,
      color: '#0ea5e9'
    },
    {
      id: 'reports',
      name: 'Daily Reports & Notifications',
      icon: <i className="ri-bar-chart-box-line" style={{ fontSize: '28px' }} />,
      description: 'Morning business summaries, stock alerts, and real-time notifications',
      articleCount: 1,
      color: '#84cc16'
    },
    {
      id: 'account-billing',
      name: 'Account, Subscription & Billing',
      icon: <i className="ri-user-settings-line" style={{ fontSize: '28px' }} />,
      description: 'Manage your plan, payments, invoices, and account settings',
      articleCount: 1,
      color: '#f59e0b'
    },
    {
      id: 'migration',
      name: 'Switch to Coderom',
      icon: <i className="ri-refresh-line" style={{ fontSize: '28px' }} />,
      description: 'How to migrate from AutoDS, Easync, SaleFreaks and other platforms',
      articleCount: 1,
      color: '#6366f1'
    }
  ];

  const popularArticles: Article[] = [
    {
      id: 'ai-listing',
      title: 'AI-Powered Product Listing — From ASIN to Live eBay Listing',
      excerpt: 'Learn how Coderom uses GPT-4o AI to automatically create complete eBay listings from Amazon ASINs — category matching, SEO-optimized titles, professional descriptions, and item specifics in under 10 seconds.',
      category: 'ai-listing',
      readTime: '10 min',
      icon: '🤖',
      content: `
# AI-Powered Product Listing

Discover how Coderom transforms Amazon ASINs into complete, optimized eBay listings using AI automation.

---

## Overview

Coderom uses **GPT-4o** (OpenAI's most advanced AI model) to transform Amazon product ASINs into complete, optimized eBay listings automatically.

**The entire process takes under 10 seconds:**
1. ✅ Fetch product data from Amazon (title, images, price, stock, features)
2. ✅ AI detects the best eBay category from 20,000+ options (98% accuracy)
3. ✅ AI generates an SEO-optimized eBay title (80-character limit)
4. ✅ AI creates a professional product description
5. ✅ AI fills item specifics automatically (brand, color, size, etc.)
6. ✅ Ready to publish with one click

**Why AI matters:**
- ⚡ 100x faster than manual listing creation
- 🎯 Better keyword optimization = higher search rankings
- 🛡️ Automatic VeRO brand detection to protect your account
- 📊 Consistent, professional listings across your entire catalog

---

## Adding Products

### Single ASIN Entry

1. Go to **Products → Add Product**
2. Paste an Amazon ASIN (10-character product ID)
   - Example: B07ZPKBL9V
   - Found in the Amazon product URL or "Product Information" section
3. Select source marketplace (UK, US, DE, FR, IT, ES, AU, CA)
4. Click **"Fetch Product"**

### What the Scraper Fetches

Coderom's Amazon scraper automatically extracts:

**Basic Information:**
- Product title (full Amazon title)
- Brand name
- Model number
- ASIN and product identifiers

**Visual Content:**
- All product images (up to 12 images)
- Image URLs optimized for eBay
- Automatic image compression and resizing

**Pricing & Stock:**
- Current Amazon price
- Shipping cost (if applicable)
- Stock availability status (In Stock / Out of Stock / Limited)
- Prime eligibility badge

**Product Details:**
- "About this item" bullet points (key features)
- Full product description
- Technical specifications (dimensions, weight, color, size)
- Variation information (if product has multiple options)

**Processing time:** 5-10 seconds per product

---

## AI Category Matching

One of the most powerful AI features — automatic eBay category detection.

### How It Works

**1. Category Analysis**
The AI analyzes:
- Product title and brand
- Amazon category path
- Product features and specifications
- Image recognition patterns

**2. eBay Category Database**
- Scans through **20,000+ eBay categories**
- Compares product attributes to category requirements
- Identifies the most specific category (not generic parent categories)

**3. Accuracy & Confidence**
- **98% accuracy** on first attempt
- Confidence score: High (90%+), Medium (70-90%), Low (<70%)
- Falls back to human review for uncertain matches

### Example Matches

| Product Type | AI-Detected Category |
|--------------|---------------------|
| iPhone 15 Pro Case | Mobile Phone Cases, Covers & Skins → Apple iPhone |
| Cordless Drill | Power Tools → Cordless Drills |
| Gaming Mouse | PC & Laptop Components & Parts → Mice |
| Yoga Mat | Sporting Goods → Exercise & Fitness → Yoga |

### Manual Override

If AI suggests the wrong category:
1. Click **"Change Category"** in the draft editor
2. Browse eBay's category tree or search
3. Select the correct category
4. Save — your choice is remembered for similar products

**Pro tip:** Once you correct a category, the AI learns from your feedback and improves future suggestions for similar items.

---

## AI Title Optimization

Coderom uses GPT-4o to transform long, keyword-stuffed Amazon titles into clean, eBay-optimized titles.

### eBay Title Rules (AI Follows These)

1. **80-character maximum** (strictly enforced)
2. **Most important keywords first** (product type, brand, model)
3. **No ALL CAPS** (use Title Case instead)
4. **No filler words** ("Brand New", "High Quality", "Best")
5. **Include key specs** (size, color, quantity, compatibility)
6. **UK English spelling** (colour not color, etc.)
7. **No keyword repetition**

### VeRO & Trademark Protection

The AI automatically **removes risky brand names** that could trigger eBay VeRO (Verified Rights Owner) claims:

**Banned terms the AI removes:**
- Arduino, Raspberry Pi, Lego, Dyson, iPhone, iPad
- "Compatible with [brand]" → Replaced with generic descriptions
- "For [brand]" → Removed or generalized
- OEM brand references → Deleted

**Example transformations:**
- ❌ "Arduino Nano Compatible Board"
- ✅ "ATmega328P Microcontroller Development Board"

- ❌ "Dyson Compatible Vacuum Filter"
- ✅ "Cordless Vacuum HEPA Filter Replacement"

- ❌ "iPhone 15 Pro Max Leather Case"
- ✅ "Phone Case 6.7 inch Leather Cover 2023 Model"

### Real Title Examples

**Example 1 — Electronics**
- **Amazon:** "SAMSUNG 970 EVO Plus SSD 2TB, M.2 NVMe Internal Solid State Hard Drive with V-NAND Technology, Storage and Memory Expansion for Gaming, Graphics w/ Heat Control, Max Speed, MZ-V7S2T0B/AM"
- **AI Optimized:** "Samsung 970 EVO Plus 2TB M.2 NVMe SSD Solid State Drive MZ-V7S2T0B"
- **Improvements:** Removed marketing fluff, kept model number, under 80 chars

**Example 2 — Fashion**
- **Amazon:** "Men's Casual Long Sleeve Button Down Shirts Regular Fit Cotton Plaid Flannel Shirt with Pocket Classic Dress Shirt for Work Business"
- **AI Optimized:** "Men's Cotton Flannel Shirt Long Sleeve Button Down Regular Fit Plaid"
- **Improvements:** Removed redundant words, prioritized fabric and style

**Example 3 — Tools**
- **Amazon:** "DEWALT 20V MAX* Cordless Drill and Impact Driver Combo Kit, 2-Tool (DCK240C2), Yellow"
- **AI Optimized:** "DeWalt DCK240C2 20V MAX Cordless Drill Impact Driver Combo Kit 2-Tool"
- **Improvements:** Model first for searchability, removed color (shown in images)

### Bulk Title Optimization

Optimize multiple products at once:
1. Go to **Products → Drafts**
2. Select products (checkboxes)
3. Click **"Bulk Optimize with AI"**
4. Wait ~2-3 seconds per product
5. Review suggestions before publishing

**AI optimization credits:**
- Advanced plan: 500/month
- Business plan: 2,000/month
- Enterprise+: Unlimited

---

## AI Description Generation

The AI creates professional, conversion-optimized product descriptions.

### Description Structure

**1. Hook (First Sentence)**
- Highlights the main product benefit
- Grabs attention in search results
- Example: "Transform your home workspace with this ergonomic mesh office chair featuring lumbar support and adjustable armrests."

**2. Key Features Section**
- Bullet points from Amazon "About this item"
- Reformatted for eBay readability
- Prioritizes most important features first

**3. Technical Specifications**
- Dimensions, weight, material
- Model number and compatibility
- Package contents

**4. Benefits & Use Cases**
- Who is this product for?
- What problems does it solve?
- How does it improve the buyer's life?

**5. Trust Signals**
- Brand reputation (if applicable)
- Quality materials/construction
- Warranty information (if mentioned)

### HTML Formatting

Descriptions include:
- \`<h3>\` for section headers
- \`<ul>\` for bullet points
- \`<p>\` for paragraphs
- Mobile-responsive layout
- **No external links** (eBay TOS compliant)

---

## Item Specifics Auto-Fill

eBay requires item-specific attributes for most categories (brand, color, size, etc.). The AI automatically fills these.

### Common Item Specifics

**Electronics:**
- Brand → Samsung
- Model → 970 EVO Plus
- Capacity → 2TB
- Interface → M.2 NVMe
- Condition → New

**Fashion:**
- Brand → Nike
- Size → M (Medium)
- Color → Black
- Material → Cotton
- Fit Type → Regular

**Home & Garden:**
- Brand → Unbranded
- Color → White
- Dimensions → 30cm x 20cm x 15cm
- Material → Plastic
- Room → Bedroom

### Category-Specific Attributes

The AI knows which fields are **required vs optional** for each eBay category and prioritizes filling required fields first.

**Example: Mobile Phone Cases**
- Brand (required) ✅
- Compatible Model (required) ✅
- Color (required) ✅
- Material (optional)
- Type (optional)

**Example: Power Tools**
- Brand (required) ✅
- Model (required) ✅
- Voltage (required) ✅
- Tool Type (required) ✅
- Battery Included (optional)

### Manual Editing

If the AI misses a field or gets it wrong:
1. Open the draft in the **Edit Draft** page
2. Scroll to **"Item Specifics"** section
3. Click **"Add Specific"** or edit existing values
4. eBay will validate required fields before publishing

---

## VeRO Protection

Coderom's AI includes built-in VeRO (Verified Rights Owner) protection to keep your account safe.

### What is VeRO?

VeRO is eBay's program where brand owners can report listings that:
- Use their trademark without authorization
- Claim to be "compatible with" their products
- Infringe on their intellectual property

**Consequences of VeRO strikes:**
- Listing removed immediately
- Account warning/suspension
- Potential permanent ban (3+ strikes)

### How AI Protects You

**1. Risky Brand Detection**
The AI maintains a database of **high-risk brands** known for aggressive VeRO enforcement:
- Apple (iPhone, iPad, AirPods)
- Disney (characters, movies)
- Lego (sets, minifigures)
- Dyson (vacuum cleaners)
- Nike, Adidas (sports apparel)
- And 200+ more...

**2. Automatic Warnings**
When adding a product, if the AI detects a risky brand:
- 🚨 **Red warning badge** appears in the draft
- Recommendation: "Review carefully before publishing"
- Suggests generic alternatives

**3. Smart Replacement**
For flagged products, the AI automatically:
- Removes brand from title (if risky)
- Describes the product function instead
- Uses generic terminology

**Example:**
- ❌ Risky: "Apple iPhone 15 Pro Max Charger Cable"
- ✅ Safe: "USB-C to Lightning Fast Charging Cable 1m for Smartphones 2023"

### Whitelist Override

If you have **authorization** to sell certain brands (e.g., official reseller):
1. Go to **Settings → Brand Safety**
2. Add brands to your **Whitelist**
3. AI won't flag these brands anymore

---

## Auto Brand Cleaner

The Auto Brand Cleaner automatically removes or sanitizes brand names from listings.

### When to Use

**Remove brand when:**
- Selling generic/unbranded products
- Product is "compatible with" a brand but not made by them
- Avoiding VeRO risk
- Focusing on product function over brand

**Keep brand when:**
- Official branded product (you have authorization)
- Brand adds value (e.g., Samsung, Bosch, IKEA)
- Whitelisted in your settings

### How It Works

**Step 1: Brand Detection**
AI identifies brand names in:
- Product title
- Description
- Item specifics

**Step 2: Risk Assessment**
Checks if brand is:
- High-risk (VeRO prone)
- Medium-risk (sometimes flagged)
- Low-risk (safe)

**Step 3: Cleaning Action**
- **High-risk**: Remove completely
- **Medium-risk**: Replace with generic ("Professional Power Drill" instead of "Bosch Power Drill")
- **Low-risk**: Keep as-is

### Settings

Configure Auto Brand Cleaner in **Settings → Listing Defaults**:
- **Always remove brands** (safest)
- **Remove risky brands only** (recommended)
- **Keep all brands** (use if you're an authorized reseller)

---

## Publishing

Once AI has optimized your listing, you're ready to publish.

### Single Product Publish

1. Review the draft in **Products → Drafts**
2. Check AI-generated content:
   - Title (edit if needed)
   - Category (change if incorrect)
   - Description (customize)
   - Item specifics (fill any missing required fields)
3. Select a listing template (optional)
4. Click **"Publish to eBay"**
5. Done! Live on eBay in ~10 seconds

### Bulk Publish

Publish multiple products at once:
1. Go to **Products → Drafts**
2. Select products using checkboxes
3. Click **"Bulk Publish"**
4. Confirm — publishes ~50 products every 5 minutes
5. Monitor progress in the notification center

### Marketplace Selection

Choose which eBay marketplace to publish to:
- 🇬🇧 eBay UK (ebay.co.uk)
- 🇺🇸 eBay US (ebay.com)
- 🇩🇪 eBay Germany (ebay.de)
- 🇫🇷 eBay France (ebay.fr)
- 🇮🇹 eBay Italy (ebay.it)
- 🇪🇸 eBay Spain (ebay.es)
- 🇦🇺 eBay Australia (ebay.com.au)
- 🇨🇦 eBay Canada (ebay.ca)

Set your default marketplace in **Settings → General**. You can override per-product in the draft editor.

### Variation Listings

For products with multiple options (size, color):
1. Enable **"Fetch Variations"** when adding the ASIN
2. AI creates a multi-variation listing
3. Each variation has its own:
   - SKU (based on ASIN + variant)
   - Price (calculated from Amazon)
   - Stock quantity
   - Variant-specific image

**Example:** T-shirt with 3 sizes (S, M, L) and 4 colors = 12 variations in one listing

### Item Specifics Check

Before publishing, eBay validates required item specifics. If any are missing:
- ⚠️ **Warning icon** appears
- Click to see which fields need filling
- Add the missing info
- Publish again

**Common missing fields:**
- Brand (required in most categories)
- Size (fashion)
- Model (electronics)
- Condition (New, Used, Refurbished)

---

## Best Practices

### 1. Review AI Suggestions

AI is 98% accurate, but always review:
- ✅ Category — is it specific enough?
- ✅ Title — does it include your product's USP?
- ✅ Item specifics — are all required fields filled?

### 2. Customize Descriptions

AI generates a good baseline, but personalization boosts conversions:
- Add your unique selling points
- Include delivery estimates
- Mention return policy
- Add trust badges (if applicable)

### 3. Use Templates

Choose a professional listing template to stand out:
- **Settings → Templates** to set your default
- Override per-product if needed
- Mobile-optimized templates convert 30% better

### 4. Monitor VeRO Warnings

Don't ignore red warning badges:
- Review flagged products carefully
- Remove risky brands
- Use generic descriptions
- When in doubt, skip the listing

### 5. Optimize Images

AI fetches Amazon images, but you can improve:
- Re-order images (best angle first)
- Remove Amazon logos/watermarks
- Add lifestyle shots if available
- White background for main image (eBay best practice)

### 6. Test & Iterate

Track which AI-optimized listings perform best:
- Monitor click-through rates
- Compare sales velocity
- A/B test titles (edit and republish)
- Refine your whitelist/blacklist over time

---

## Troubleshooting

**Q: AI chose the wrong category. How do I fix it?**
A: Click "Change Category" in the draft editor, select the correct one, and save. AI will learn from this.

**Q: Title is too short (under 60 characters). Can I make it longer?**
A: Yes! Edit the title in the draft page. AI aims for ~70-75 chars but you can extend to 80.

**Q: Brand name was removed but I want to keep it. How?**
A: Add the brand to your whitelist in **Settings → Brand Safety**. Then re-optimize the listing.

**Q: Item specifics are missing. Why?**
A: Some Amazon products lack detailed specs. Manually add them in the "Item Specifics" section before publishing.

**Q: Can I turn off AI optimization?**
A: Yes. In **Settings → Listing Defaults**, set "Auto-optimize new products" to OFF. You'll need to fill everything manually.

**Q: Does AI work for all Amazon marketplaces?**
A: Yes! Supports UK, US, DE, FR, IT, ES, AU, CA. Select your source marketplace when adding products.

---

**Ready to automate your listing creation?** Head to **Products → Add Product** and enter your first ASIN!
      `
    },
    {
      id: 'smart-repricing',
      title: 'Smart Repricing: Maximize Your Profits',
      excerpt: 'Master Coderom\'s intelligent pricing engine — margin-based formulas, automatic fee calculations, smart rounding, minimum profit guarantees, and auto-revise triggers. Configure your pricing strategy in Settings → Pricing to maximize profits while staying competitive.',
      category: 'pricing',
      readTime: '8 min',
      icon: '💰',
      content: `
# Smart Repricing System

Coderom's intelligent repricing engine automatically calculates optimal eBay prices based on Amazon supplier costs, eBay fees, your profit margins, and market conditions.

---

## Overview

Every time your Amazon supplier price changes, Coderom recalculates your eBay price using a sophisticated formula that:

✅ **Applies your profit margin** (e.g., 20%)
✅ **Adds a flat fee per item** (e.g., £0.50)
✅ **Accounts for all eBay fees** (Fixed Fee + Final Value Fee + Payment Processing)
✅ **Guarantees minimum net profit** (e.g., minimum £1 profit per sale)
✅ **Rounds prices psychologically** (.99, .90, nearest £0.05, or exact)
✅ **Respects min/max price limits** (e.g., £1 - £1000)
✅ **Auto-revises on eBay only when needed** (threshold-based updates)

This guide covers all pricing settings available in **Settings → Pricing** tab.

---

## The Pricing Formula Explained

Coderom uses a **reverse fee calculation** to ensure you always hit your target margin after eBay takes its cut:

\`\`\`
Step 1: Apply Margin
with_margin = amazon_price × (1 + margin%)

Step 2: Add Flat Fee
with_flat = with_margin + flat_fee

Step 3: Reverse eBay Fees
draft_price = (with_flat + ebay_fixed_fee) / (1 - fee_percent)

Where:
fee_percent = (ebay_final_value_fee% + payment_processing_fee%) / 100

Step 4: Guarantee Minimum Profit
If net_profit < min_profit_gbp:
  increase price until profit >= min_profit_gbp

Step 5: Apply Rounding
Round to .99, .90, nearest £0.05, or exact £0.00

Step 6: Apply Min/Max Limits
Clamp price between min_price and max_price
\`\`\`

**Why reverse calculation?** Because eBay fees are percentage-based, if you simply add margin to cost, the final profit after fees will be lower than expected. Reversing the formula guarantees your net profit.

---

## Pricing Tab Settings

Access these settings in **Settings → Pricing**:

### 1. Margin Percent (marginPercent)

**What it does:** Your profit markup as a percentage of Amazon cost.

**Example:**
- Amazon price: £10.00
- Margin: 20%
- After margin: £10.00 × 1.20 = £12.00

**Recommended values:**
- Electronics/High competition: 15-20%
- Home & Garden: 20-30%
- Niche products: 30-50%

---

### 2. Flat Fee (flatFee)

**What it does:** Fixed amount added to every product, regardless of price.

**Use case:** Cover fixed costs like packaging materials, labels, or handling time.

**Example:**
- After margin: £12.00
- Flat fee: £0.50
- Subtotal: £12.50

**Tip:** Set to £0.00 if you prefer margin-only pricing.

---

### 3. Rounding Options (rounding)

**What it does:** Psychologically optimize prices for better conversion.

**Options:**

| Rounding | Example | Best For |
|----------|---------|----------|
| **.99** | £12.49 → **£12.99** | Retail psychology (most popular) |
| **.90** | £12.49 → **£12.90** | Alternative to .99 |
| **nearest_0.05** | £12.49 → **£12.50** | Clean rounding |
| **0.00** | £12.49 → **£12.49** | Exact price (no rounding) |

**How .99/.90 rounding works:**
- If calculated price is £12.49, rounds UP to £12.99 (not down to £11.99)
- Ensures rounding never reduces profit below minimum

---

### 4. eBay Fees

Coderom accounts for three types of eBay fees:

#### a) Fixed Fee (ebayFixedFee)
- Default: **£0.30** per listing
- Charged by eBay for every listing sold

#### b) Final Value Fee (ebayFinalValueFee)
- Default: **13%** of total sale price (item + shipping)
- eBay's main commission on sales

#### c) Payment Processing Fee (paymentProcessingFee)
- Default: **0%** (many sellers now have 0% under Managed Payments)
- Previously ~2.9% under PayPal

**Example Fee Calculation:**
\`\`\`
Selling price: £15.00
Fixed fee: £0.30
Final value fee: £15.00 × 13% = £1.95
Payment processing: £15.00 × 0% = £0.00
Total eBay fees: £0.30 + £1.95 = £2.25
\`\`\`

---

### 5. VAT Included (vatIncluded)

**What it does:** Toggle whether your prices include VAT.

**Options:**
- **false** (default): Prices are net (VAT not included in calculations)
- **true**: Prices include VAT (currently informational only)

**Note:** Currently used for reporting. Future updates may calculate VAT-exclusive supplier costs.

---

### 6. Minimum Profit Guarantee (minProfitGBP)

**What it does:** Ensures you ALWAYS make at least this much profit per sale, regardless of margin settings.

**How it works:**
\`\`\`
Net Profit Formula:
profit = selling_price - amazon_cost - ebay_fees - flat_fee

If profit < min_profit_gbp:
  → Increase selling_price until profit >= min_profit_gbp
\`\`\`

**Example:**
- Amazon cost: £8.00
- Margin 20% + flat fee £0.50 = £10.40 calculated price
- eBay fees: £1.39
- Net profit: £10.40 - £8.00 - £1.39 - £0.50 = **£0.51**
- But minProfitGBP = £1.00
- ❌ Price too low!
- ✅ Coderom increases price to **£11.29** to guarantee £1 profit

**Recommended:** £0.50 - £2.00 depending on product category

---

### 7. Price Limits

#### Min Price (minPrice)
- Default: **£1.00**
- Prevents selling items below this threshold
- Useful to avoid unprofitable listings

#### Max Price (maxPrice)
- Default: **£1000**
- Prevents accidentally listing items at extreme prices
- Protects against Amazon price errors

**Example:**
- Calculated price: £0.75
- Min price: £1.00
- ✅ Price set to £1.00 (clamped to minimum)

---

### 8. Auto-Revise Settings

Control when Coderom pushes price updates to eBay:

#### Auto Revise on Scrape (autoReviseOnScrape)
- **true**: Automatically revise eBay prices when Amazon price changes
- **false**: Update database only, don't revise on eBay (manual control)

**Recommended:** Enable for active repricing

#### Minimum Price Delta (Absolute)
**Setting:** \`minPriceDeltaAbs\`
**Default:** £0.05
**What it does:** Only revise eBay if new price differs by at least this amount

**Example:**
- Current eBay price: £15.00
- New calculated price: £15.03
- Delta: £0.03
- ❌ Skip revise (below £0.05 threshold)

**Why?** Reduces eBay API calls, avoids "Revise Item" charges for tiny changes

#### Minimum Price Delta (Percentage)
**Setting:** \`minPriceDeltaPct\`
**Default:** 0.3%
**What it does:** Only revise if price changes by at least this percentage

**Example:**
- Current eBay price: £100.00
- New price: £100.20
- Delta: 0.2%
- ❌ Skip revise (below 0.3% threshold)

**Note:** Coderom uses **whichever threshold is LARGER** (absolute OR percentage)

---

## Real-World Pricing Example

Let's calculate the final eBay price for a product:

**Inputs:**
- Amazon price: £25.00
- Margin: 20%
- Flat fee: £0.50
- eBay fixed fee: £0.30
- eBay final value fee: 13%
- Payment processing: 0%
- Min profit: £2.00
- Rounding: .99
- Min price: £1.00
- Max price: £1000

**Step-by-Step Calculation:**

\`\`\`
1. Apply margin:
   £25.00 × 1.20 = £30.00

2. Add flat fee:
   £30.00 + £0.50 = £30.50

3. Reverse eBay fees:
   fee_percent = (13% + 0%) / 100 = 0.13
   draft_price = (£30.50 + £0.30) / (1 - 0.13)
   draft_price = £30.80 / 0.87 = £35.40

4. Check minimum profit:
   Net profit = £35.40 - £25.00 - (£35.40 × 0.13) - £0.30 - £0.50
   Net profit = £35.40 - £25.00 - £4.60 - £0.30 - £0.50 = £5.00
   ✅ £5.00 >= £2.00 (profit requirement met)

5. Apply .99 rounding:
   £35.40 → £35.99

6. Check min/max limits:
   £35.99 is between £1.00 and £1000 ✅

Final eBay Price: £35.99
Net Profit: £5.09
\`\`\`

---

## Best Practices

### Starting Values (Recommended)

For most dropshippers:
- **Margin:** 20-25%
- **Flat fee:** £0.50
- **Rounding:** .99
- **Min profit:** £1.00
- **Auto-revise:** Enabled
- **Min delta (abs):** £0.05
- **Min delta (%):** 0.3%

### Category-Specific Strategies

**Electronics (High Competition):**
- Margin: 15-18%
- Min profit: £0.50
- Tight margins, high volume

**Home & Garden:**
- Margin: 25-35%
- Min profit: £2.00
- More room for markup

**Collectibles/Niche:**
- Margin: 40-60%
- Min profit: £5.00
- Low competition, premium pricing

### Testing Your Formula

Before publishing 1000 listings:

1. **Import 10-20 test products** in different price ranges
2. **Review calculated prices** in the Drafts table
3. **Check profit column** — is it acceptable?
4. **Adjust margin/flat fee** if needed
5. **Publish test batch** and monitor sales for 7 days
6. **Refine settings** based on conversion rates

---

## Auto-Revise Triggers

Coderom automatically revises eBay prices when:

✅ **Amazon price changes** (if autoReviseOnScrape = true)
✅ **Price delta exceeds thresholds** (£0.05 OR 0.3%)
✅ **Stock becomes available** after OOS period
✅ **Manual "Revise Price" action** from Products page

Coderom SKIPS revise when:
- ❌ Price change below both thresholds (£0.05 AND 0.3%)
- ❌ Product is out of stock (quantity = 0)
- ❌ Listing is in Safe Repricing protection mode
- ❌ eBay account token expired

---

## Troubleshooting

### "Price too low" warnings

**Problem:** Products showing £0.00 or very low prices

**Solutions:**
1. Check Amazon cost is fetching correctly
2. Increase margin % or flat fee
3. Set higher minProfitGBP
4. Check eBay fee % settings (13% is standard)

### Prices not updating on eBay

**Problem:** Database shows new price, eBay listing still shows old price

**Check:**
1. **Auto-revise enabled?** Settings → autoReviseOnScrape = true
2. **Delta thresholds met?** Price must change by >£0.05 OR >0.3%
3. **Check needs_ebay_update flag** — Products page → "Needs Update" filter
4. **eBay API rate limits** — Coderom pushes ~50 updates every 5 minutes

### Profit margins lower than expected

**Problem:** Calculated profit doesn't match margin %

**Explanation:**
- eBay fees are % of selling price, not cost
- 20% margin ≠ 20% net profit
- Use minProfitGBP to guarantee minimum profit

**Example:**
- Amazon: £10, Margin 20% = £12 selling price
- eBay fees (13%): £1.56
- Net profit: £12 - £10 - £1.56 = **£0.44** (4.4%, not 20%)

---

## Related Articles

- **Safe Repricing Protection** — Protect against Amazon price spikes
- **Stock Synchronization** — Auto-adjust prices when stock changes
- **Amazon Filters** — Control which suppliers Coderom sources from

---

**Need help?** Contact support or use the in-app chat widget.
      `
    },
    {
      id: 'safe-repricing-guide',
      title: 'Safe Repricing Protection — Automatic Price Spike Defense',
      excerpt: 'Protect your profit margins from sudden Amazon price volatility. When supplier prices spike by more than your threshold (default 50%), Coderom automatically sets listings to OOS, monitors stability, and restores stock when prices normalize.',
      category: 'safe-repricing',
      readTime: '7 min',
      icon: '🛡️',
      content: `
# Safe Repricing Protection

Automatic protection against sudden Amazon supplier price changes that could destroy your profit margins.

---

## Overview

**The Problem:**
Your Amazon supplier price is £15. You're selling on eBay for £22 with a healthy £4 profit. Suddenly, Amazon price jumps to £35 (133% increase). If Coderom auto-reprices to £45, your listing becomes uncompetitive. If it keeps the £22 price, you lose £16 per sale.

**The Solution:**
Safe Repricing monitors Amazon price volatility over the last N days. When a price changes by more than your threshold %, Coderom:

1. ✅ **Sets eBay quantity to 0** (Out of Stock)
2. ✅ **Flags the listing with OOS reason** ("Safe Repricing (133% change)")
3. ✅ **Waits for stability period** (default 3 days)
4. ✅ **Automatically restores stock** when price normalizes

This guide covers **Settings → Safety** tab configuration and backend implementation.

---

## How Safe Repricing Works

### Step 1: Monitoring (Every 6 Hours)

Coderom's scheduler job **check_safe_repricing** runs every 6 hours for each managed product:

1. **Fetch price history** for last **monitoringDays** (default: 3 days)
2. **Calculate volatility:**
   \`\`\`
   min_price = lowest price in period
   max_price = highest price in period
   price_change_% = ((max - min) / min) × 100
   \`\`\`
3. **Compare to threshold** (default: 50%)

**Example:**
- Day 0: £15.00
- Day 1: £15.20
- Day 2: £35.00 (spike!)
- min_price = £15.00
- max_price = £35.00
- change = ((35 - 15) / 15) × 100 = **133%**
- ✅ **133% > 50% threshold → TRIGGER**

---

### Step 2: Triggering OOS Protection

When threshold is exceeded, Coderom:

**Backend Actions** (\`services/safe_repricing.py:trigger_safe_repricing\`):

1. **Revise eBay listing to quantity = 0**
   - Uses eBay Trading API \`ReviseInventoryStatus\`
   - Sets \`OutOfStockControl = true\`

2. **Update database:**
   \`\`\`sql
   UPDATE products SET
     safe_repricing_triggered = true,
     safe_repricing_triggered_at = NOW(),
     safe_repricing_original_qty = 5,  -- Remember original qty
     safe_repricing_reason = 'price_spike',
     safe_repricing_price_change_pct = 133.0
   \`\`\`

3. **Update eBay listing record:**
   \`\`\`sql
   UPDATE ebay_listings SET
     quantity = 0,
     oos_since = NOW(),
     oos_reason = 'Safe Repricing (133% change)'
   \`\`\`

**Frontend Visibility:**

In your Products page, you'll see:
- 🔴 **OOS badge** on the listing
- **OOS Reason:** "Safe Repricing (133% change)"
- **OOS Since:** timestamp
- **Product remains in database** (not deleted)

---

### Step 3: Stability Monitoring

Once triggered, the product enters **stability monitoring** mode.

Every 6 hours, Coderom checks:

**Condition 1: Enough Time Passed?**
\`\`\`
days_since_trigger = (NOW - triggered_at).days
if days_since_trigger < stabilityDays:
  → WAIT (not ready yet)
\`\`\`

**Condition 2: Price Stabilized?**
\`\`\`
Check last stabilityDays (3 days) of price history:
if price_change_% <= threshold:
  → RESTORE
else:
  → KEEP WAITING
\`\`\`

**Example Timeline:**

| Day | Amazon Price | Status | Action |
|-----|--------------|--------|--------|
| Day 0 | £15.00 | Normal | — |
| Day 1 | £15.20 | Normal | — |
| Day 2 (spike!) | £35.00 | **Triggered** | ✅ Set OOS |
| Day 3 | £34.50 | Monitoring | Wait (1/3 days) |
| Day 4 | £16.00 | Monitoring | Wait (2/3 days) |
| Day 5 | £15.50 | **Stable** | ✅ Restore qty = 5 |

---

### Step 4: Automatic Restoration

When both conditions are met (time passed + price stable), Coderom restores the listing:

**Backend Actions** (\`services/safe_repricing.py:restore_product\`):

1. **Revise eBay quantity back to original**
   - Uses stored \`safe_repricing_original_qty\` value
   - eBay API: \`ReviseInventoryStatus\` qty = 5

2. **Clear protection flags:**
   \`\`\`sql
   UPDATE products SET
     safe_repricing_triggered = false,
     safe_repricing_triggered_at = NULL,
     safe_repricing_original_qty = NULL,
     safe_repricing_reason = NULL,
     safe_repricing_price_change_pct = NULL
   \`\`\`

3. **Update eBay listing:**
   \`\`\`sql
   UPDATE ebay_listings SET
     quantity = 5,
     oos_since = NULL,
     oos_reason = NULL
   \`\`\`

**Result:** Listing returns to normal stock synchronization, price recalculates based on new stable Amazon price (£15.50).

---

## Safety Tab Settings

Access these settings in **Settings → Safety**:

### 1. Safe Repricing Enabled (safeRepricingEnabled)

**Type:** Boolean (toggle)
**Default:** false

**What it does:**
- **true**: Enable price volatility monitoring for all managed products
- **false**: Disable protection (Coderom will reprice normally, even during spikes)

**Recommended:** Enable if you sell in volatile categories (electronics, trending items)

**Disable if:** You sell stable products (books, established brands) where price spikes are rare

---

### 2. Threshold Percent (safeRepricingThreshold)

**Type:** Number (%)
**Default:** 60%
**Allowed range:** 20-80% (values outside this range are rejected)

**What it does:** Minimum price change % to trigger OOS protection.

**How to choose** (presets available in Settings → Pricing):

| Threshold | Sensitivity | Roughly how many listings stay closed* |
|-----------|-------------|----------------------------------------|
| **30%** Sensitive | High | ~0.25% of catalogue |
| **40%** Balanced | Medium | ~0.15% of catalogue |
| **60%** Recommended (default) | Low | ~0.04% of catalogue |
| **80%** Relaxed | Lowest | ~0.02% of catalogue |

\*Measured on real price history over a stable 3-day window. Your figures will vary
with how volatile your suppliers are.

**Example:**
- Threshold 60%
- Amazon price: £10 → £17 = 70% change
- ✅ Triggered

- Amazon price: £10 → £14 = 40% change
- ❌ NOT triggered (below 60%)

**Note:** Coderom used 20% until August 2026. Measurement showed that at 20% the brake
was closing listings for ordinary supplier price movements (a £6.39 → £7.99 change is
25%), so the default moved to 60%. You can still set any value from 20 to 80.

---

### 3. Monitoring Days (safeRepricingMonitoringDays)

**Type:** Integer (days)
**Default:** 3

**What it does:** Look back period for calculating price volatility.

**How it works:**
\`\`\`
Fetch price_history records where:
  - created_at >= (NOW - monitoring_days)
  - source = "scraper"
  - new_price > 0

Calculate: min/max price over this period
\`\`\`

**Recommendations:**

- **1-2 days**: Very sensitive, catches short-term spikes (may trigger frequently)
- **3 days** (default): Balanced, filters out daily fluctuations
- **5-7 days**: Less sensitive, only triggers on sustained price changes

**Use case:** If you scrape Amazon every 2 hours, 3 days = ~36 price snapshots.

---

### 4. Stability Days (safeRepricingStabilityDays)

**Type:** Integer (days)
**Default:** 1
**Minimum:** 1 day

**What it does:** How long to wait before re-checking whether the price has settled.
Once this period has passed, Coderom looks at the price history again. If the price is
stable **at its new level**, the listing is restored.

**Important — how restore actually works:** Coderom does **not** require the price to
return to its old value. After the stability period, it re-measures volatility **from the
moment the brake fired onwards** — not over a fixed trailing window — and if the price has
not moved since then, the listing goes back In Stock at the new price. A permanent price
change therefore **does** restore (at the new price), it just waits out the stability
period first.

Measuring from the brake rather than over the last few days is what stops a reopened
listing from being closed again by the very movement that closed it in the first place.

**Why it matters:**

Sometimes Amazon prices spike temporarily (flash sales, Prime Day, seller manipulation),
then return to normal. Waiting helps you:

- ✅ **Avoid repricing during temporary volatility**
- ✅ **Reduce eBay API calls** (fewer revisions)
- ✅ **Minimize customer confusion** (listing doesn't flicker in/out of stock)

**Choosing a value** (measured on real price history):

| Stability Days | Share of temporary moves that have already reverted |
|----------------|------------------------------------------------------|
| 1 day (default) | ~44% |
| 2 days | ~55% |
| 3 days | ~60% |
| 5 days | ~78% |
| 7 days | ~84% |

The first day covers the steepest part of the curve; each extra day buys less. Longer
settings keep listings closed longer, so the trade-off is protection versus lost sales.

**Advanced:** Set to 1 day for fast restoration. Set to 5-7 days for an ultra-conservative
approach.

---

## Spike vs. Drop Detection

Safe Repricing triggers on **both spikes and drops** if they exceed the threshold:

### Price Spike (Upward)

**Example:**
- £15 → £35 = +133%
- **Reason:** \`price_spike\`
- **Risk:** Selling at old low price would lose money
- **Action:** Set OOS, wait for price to drop back

### Price Drop (Downward)

**Example:**
- £35 → £10 = -71% (calculated as min £10, max £35 = 250% volatility)
- **Reason:** \`price_drop\`
- **Risk:** Your high eBay price is now uncompetitive
- **Action:** Set OOS, wait for price to stabilize

**Note:** Price volatility is always calculated as \`(max - min) / min\`, so it's always positive. The "reason" field distinguishes spike vs drop by comparing latest vs oldest price.

---

## Real-World Example: Timeline

**Setup:**
- Threshold: 50%
- Monitoring days: 3
- Stability days: 3
- Scraper runs: Every 2 hours

**Timeline:**

| Date/Time | Amazon Price | Volatility | Status | Action |
|-----------|--------------|------------|--------|--------|
| **Day 1, 00:00** | £15.00 | — | Normal | Scraper sync |
| **Day 1, 12:00** | £15.20 | 1.3% | Normal | Price updated |
| **Day 2, 08:00** | £15.10 | 1.3% | Normal | — |
| **Day 2, 20:00** | £35.00 | **133%** | **TRIGGERED** | ✅ **Set OOS** |
| | | | | ebay qty = 0 |
| | | | | oos_reason = "Safe Repricing (133% change)" |
| **Day 3, 06:00** | £34.50 | — | Protected | Stability check: 1/3 days ❌ |
| **Day 4, 06:00** | £34.00 | — | Protected | Stability check: 2/3 days ❌ |
| **Day 5, 06:00** | £16.00 | 6.7% | **Stabilized** | ✅ **Restore qty = 5** |
| | | | | Last 3 days: £34.50 → £34.00 → £16.00 |
| | | | | Volatility: (34.50 - 16) / 16 = 115% ❌ Still high |
| **Day 6, 06:00** | £15.80 | 8% | **Stable** | Last 3 days: £34 → £16 → £15.80 |
| | | | | Volatility: (34 - 15.80) / 15.80 = 115% ❌ |
| **Day 7, 06:00** | £15.50 | 3.2% | **Restored!** | Last 3 days: £16 → £15.80 → £15.50 |
| | | | | Volatility: (16 - 15.50) / 15.50 = **3.2%** ✅ |
| | | | | ✅ **Qty restored to 5** |

**Key takeaway:** It took 5 days from trigger to restoration because the system waited for 3 consecutive stable days with volatility < 50%.

---

## Backend Implementation

Safe Repricing logic is in **backend/services/safe_repricing.py**:

### Main Functions

1. **\`process_safe_repricing_for_store()\`**
   - Called by scheduler every 6 hours
   - Checks all managed products in a store
   - Returns stats: \`{triggered, restored, checked, errors}\`

2. **\`check_price_stability()\`**
   - Fetches price_history for last N days
   - Filters source = "scraper", new_price > 0
   - Returns: \`{min_price, max_price, price_change_percent, prices[]}\`

3. **\`should_trigger_safe_repricing()\`**
   - Checks if not already triggered
   - Checks if record_count >= 2
   - Checks if price_change_percent > threshold
   - Returns: \`(should_trigger, reason)\`

4. **\`trigger_safe_repricing()\`**
   - Revise eBay listing to qty = 0
   - Update product flags
   - Set oos_reason for UI visibility
   - Commit to database

5. **\`should_restore_product()\`**
   - Check days_since_trigger >= stability_days
   - Check current volatility <= threshold
   - Returns: \`(should_restore, reason)\`

6. **\`restore_product()\`**
   - Revise eBay listing to original qty
   - Clear all safe repricing flags
   - Clear oos_since and oos_reason

### Scheduler Integration

File: **backend/services/scheduler.py**

Safe Repricing job runs at **00:00, 06:00, 12:00, 18:00 UTC** (every 6 hours):

\`\`\`python
@scheduler.task('cron', id='safe_repricing_check',
                hour='0,6,12,18', minute=0)
def safe_repricing_check():
    db = SessionLocal()
    try:
        # Process all users with safeRepricingEnabled = true
        for user in active_users:
            stats = process_safe_repricing_for_user(db, user.id)
            logger.info(f"Safe Repricing: {stats}")
    finally:
        db.close()
\`\`\`

---

## Monitoring Protected Products

### Frontend Visibility

**Products Page → OOS Filter:**
- Protected products show 🔴 OOS badge
- **OOS Reason column:** "Safe Repricing (133% change)"
- **OOS Since column:** Timestamp when protection triggered

**Dashboard Stats:**
- "Protected by Safe Repricing: 12 products"
- Click to see list of protected ASINs

### Backend Query

Get all protected products for a store:

\`\`\`sql
SELECT asin, title, safe_repricing_triggered_at,
       safe_repricing_price_change_pct, safe_repricing_reason
FROM products
WHERE user_id = 'user_123'
  AND ebay_account_id = 5
  AND safe_repricing_triggered = true
ORDER BY safe_repricing_triggered_at DESC;
\`\`\`

---

## Best Practices

### When to Enable

✅ **Enable if:**
- You sell electronics, toys, trending items (volatile categories)
- You dropship from multiple Amazon sellers (price competition)
- You have tight profit margins (< 20%)
- You've been burned by price spikes before

❌ **Disable if:**
- You sell books, media, stable brands (low volatility)
- You have high margins (50%+) and can absorb spikes
- You manually review all price changes anyway

### Threshold Recommendations

| Your Margin | Recommended Threshold |
|-------------|----------------------|
| 10-15% (tight) | 20-30% (sensitive) |
| 20-30% (standard) | 50% (default) |
| 40%+ (premium) | 70-100% (tolerant) |

**Rationale:** Higher margins can absorb more volatility before profit turns negative.

### Testing Before Full Deployment

1. **Enable for 1 store only** (test account)
2. **Set threshold to 30%** (lower = more triggers)
3. **Monitor for 1 week**
4. **Check stats:** How many products triggered? False positives?
5. **Adjust threshold** if too sensitive or too loose
6. **Roll out to all stores** once satisfied

---

## Troubleshooting

### Products stuck in OOS for weeks

**Problem:** Safe Repricing triggered 3 weeks ago, listing still OOS

**Diagnosis:**
- Check current Amazon price — is it STILL volatile?
- Check stability window — last 3 days might still show > 50% change

**Solution:**
- **Option 1:** Manually restore from Products page → "Restore Stock"
- **Option 2:** Increase threshold to 70% so it restores despite volatility
- **Option 3:** Disable Safe Repricing for this product

### Too many false positives

**Problem:** 50+ products triggering every day, most are normal price fluctuations

**Solution:**
- **Increase threshold:** 50% → 70%
- **Increase monitoring days:** 3 → 5 (filters daily noise)
- **Check scraper accuracy:** Are prices scraping correctly?

### Product never restored

**Problem:** Price stabilized, but qty still 0

**Check:**
1. **Scheduler running?** \`journalctl -u coderom-backend | grep safe_repricing\`
2. **Settings loaded correctly?** Database → store_settings.data.safeRepricingEnabled
3. **Manual override?** Did someone manually set qty = 0 via eBay?
4. **Token expired?** Coderom needs valid eBay token to revise

---

## Related Articles

- **Smart Repricing** — Configure pricing formulas and margins
- **Stock Synchronization** — Auto-end OOS after N days
- **Amazon Filters** — Prevent volatile suppliers at source

---

**Need help?** Contact support or use the in-app chat widget.
      `
    },
    {
      id: 'stock-sync',
      title: '24/7 Stock Synchronization — Never Oversell Again',
      excerpt: 'Real-time inventory sync between Amazon and eBay — stock buffers, auto-end OOS after N days, smart replenishment with velocity limits, and tiered scheduler for high-volume sellers. Configure all settings in Settings → Stock tab.',
      category: 'stock',
      readTime: '8 min',
      icon: '🔄',
      content: `
# Stock Synchronization System

Coderom monitors Amazon supplier stock 24/7 and automatically updates your eBay quantities to prevent overselling, handle out-of-stock scenarios, and intelligently replenish listings.

---

## Overview

Every managed product in Coderom is continuously synchronized with its Amazon source:

✅ **Real-time monitoring** — Scraper checks Amazon stock multiple times per hour
✅ **Instant eBay updates** — Quantity changes pushed to eBay within 5 minutes
✅ **Safety buffers** — Reserve N units to avoid edge-case overselling
✅ **Auto-end OOS** — Permanently end listings that are out of stock for 30+ days
✅ **Smart replenishment** — Automatically restore listings when stock returns (with velocity limits)
✅ **Order triggers** — Instant stock sync when eBay order is placed
✅ **Tiered scheduling** — Sync frequency adapts to your product count

This guide covers all stock management settings in **Settings → Stock** tab.

---

## How Stock Sync Works

### 1. Scraper Detects Amazon Stock

**Backend:** \`services/scraper_sync.py\`

Every sync cycle, Coderom:

1. **Fetches Amazon product page** for each managed listing
2. **Extracts availability:**
   - In Stock: quantity > 0
   - Out of Stock: quantity = 0
   - Temporarily unavailable: quantity = 0
3. **Parses exact quantity** (if visible: "Only 3 left in stock")
4. **Saves to database:**
   \`\`\`sql
   UPDATE products SET
     amazon_stock_qty = 15,
     last_scraped_at = NOW()
   \`\`\`

---

### 2. Calculate eBay Quantity

**Formula:**
\`\`\`
ebay_quantity = max(0, amazon_stock_qty - stock_buffer)
\`\`\`

**Example:**
- Amazon stock: 15 units
- Stock buffer: 1
- eBay quantity: **14**

**Why buffer?** Amazon stock numbers update with a delay. If you list exact quantity, you risk overselling if multiple buyers purchase simultaneously before Amazon updates.

---

### 3. Push to eBay (if changed)

Coderom only revises eBay if:

✅ **New quantity differs from current eBay qty**
✅ **Not in Safe Repricing protection mode**
✅ **eBay token is valid**

**Backend:** \`services/ebay/listing.py:revise_variation_inventory()\`

eBay API call:
\`\`\`xml
<ReviseInventoryStatus>
  <ItemID>387721248895</ItemID>
  <Quantity>14</Quantity>
</ReviseInventoryStatus>
\`\`\`

Updates are batched: **~50 listings every 5 minutes** to avoid rate limits.

---

## Stock Tab Settings

Access these settings in **Settings → Stock**:

### 1. Stock Buffer (stockBuffer)

**Type:** Integer (units)
**Default:** 1

**What it does:** Reduce eBay quantity by N units as a safety margin.

**Use cases:**

| Buffer | When to Use |
|--------|-------------|
| **0** | Trust Amazon stock 100%, maximize sales |
| **1** (default) | Standard safety — prevents most oversell edge cases |
| **2-3** | High-risk products (expensive items, low stock) |
| **5+** | Very conservative (not recommended — limits sales) |

**Example:**

| Amazon Stock | Buffer | eBay Qty | Notes |
|--------------|--------|----------|-------|
| 20 | 0 | 20 | No buffer, risk of oversell |
| 20 | 1 | 19 | ✅ Recommended default |
| 5 | 1 | 4 | Prevents selling last unit |
| 1 | 1 | 0 | ❌ Never lists (Amazon has 1, you list 0) |

**Recommendation:** Keep at 1 unless you have specific reason to change.

---

### 2. Auto-End OOS Settings

Automatically end eBay listings that remain out of stock for too long.

#### Auto-End Enabled (autoEndOosEnabled)

**Type:** Boolean
**Default:** false

**What it does:**
- **true**: End listings that are OOS for >= autoEndOosDays
- **false**: Keep OOS listings at quantity 0 indefinitely

**Use case:** Clean up "zombie" listings that will never restock (discontinued products, seasonal items).

#### Auto-End Days (autoEndOosDays)

**Type:** Integer (days)
**Default:** 30

**What it does:** How many days to wait before ending an OOS listing.

**How it works:**

1. **Product goes OOS** → \`oos_since\` timestamp saved
2. **Wait N days**
3. **Scheduler checks:** \`(NOW - oos_since).days >= autoEndOosDays\`
4. **If true:** Call eBay \`EndItem\` API, delete from Coderom database

**Example:**

| oos_since | autoEndOosDays | Days Passed | Action |
|-----------|----------------|-------------|--------|
| 2026-03-01 | 30 | 15 | Wait (15/30) |
| 2026-03-01 | 30 | 30 | ✅ End listing |
| 2026-03-01 | 30 | 45 | Already ended |

**Recommendations:**

- **7-14 days**: Aggressive cleanup (electronics, trending items)
- **30 days** (default): Balanced (most categories)
- **60-90 days**: Conservative (seasonal products, niche items)
- **Disable**: Never auto-end (manual control only)

**Note:** Products in Safe Repricing protection are NOT auto-ended (different OOS reason).

---

### 3. Stock Replenishment Settings

Automatically restore listings when Amazon stock returns.

#### Replenish Enabled (stockReplenishEnabled)

**Type:** Boolean
**Default:** false

**What it does:**
- **true**: Auto-restore OOS listings when Amazon stock becomes available
- **false**: Listings remain at qty = 0 until manual intervention

**Use case:** Products that frequently go in/out of stock (popular items, Amazon restocks).

#### Replenish Delay (stockReplenishDelayMinutes)

**Type:** Integer (minutes)
**Default:** 60

**What it does:** Wait N minutes after stock detection before replenishing.

**Why delay?** Prevents flapping if Amazon stock appears/disappears rapidly.

**Example:**

| Time | Amazon Stock | Status | Action |
|------|--------------|--------|--------|
| 10:00 | 0 | OOS | Set eBay qty = 0 |
| 10:05 | 5 | **Stock returned!** | Start timer ⏱️ |
| 10:10 | 0 | Flapping | ❌ Cancel timer (stock gone again) |
| 10:15 | 8 | Stock stable | Start timer ⏱️ |
| 11:15 | 8 | **60 min passed** | ✅ Restore eBay qty = 7 (8 - buffer 1) |

**Recommendations:**

- **30 minutes**: Aggressive replenishment (fast-moving products)
- **60 minutes** (default): Balanced (filters most flapping)
- **120+ minutes**: Conservative (wait for stable stock)

---

#### Replenish Min Margin (stockReplenishMinMargin)

**Type:** Float (decimal %)
**Default:** 0.10 (10%)

**What it does:** Only replenish if current profit margin >= this %.

**Why?** If Amazon price increased significantly during OOS period, you don't want to auto-restore at a loss.

**Formula:**
\`\`\`
margin = (ebay_price - amazon_cost - fees) / ebay_price
if margin >= stockReplenishMinMargin:
  → REPLENISH
else:
  → SKIP (profit too low)
\`\`\`

**Example:**

| Amazon Cost | eBay Price | Margin | Min Margin | Action |
|-------------|------------|--------|------------|--------|
| £10 | £15 | 20% | 10% | ✅ Replenish |
| £13 | £15 | 6% | 10% | ❌ Skip (margin < 10%) |
| £8 | £15 | 30% | 10% | ✅ Replenish |

**Recommendations:**

- **0%**: Always replenish regardless of margin (not recommended)
- **5-10%** (default): Minimum viable margin
- **15-20%**: Only replenish profitable listings

**Note:** If replenishment skipped due to low margin, listing stays at qty = 0. Manual price adjustment needed.

---

#### Replenish Velocity Limit (stockReplenishVelocityLimit)

**Type:** Integer (listings per sync)
**Default:** 3

**What it does:** Maximum number of listings to replenish in a single sync cycle.

**Why limit?** Prevents eBay API spam if 100+ listings restock simultaneously.

**Example:**

| Eligible for Replenish | Velocity Limit | Action |
|------------------------|----------------|--------|
| 50 listings | 3 | Replenish first 3, queue rest for next sync |
| 2 listings | 3 | Replenish both |
| 10 listings | 10 | Replenish all 10 |

**How queuing works:**

1. **Sync #1:** Replenish 3 listings (A, B, C)
2. **Sync #2 (5 min later):** Replenish next 3 (D, E, F)
3. **Sync #3:** Continue until all eligible listings replenished

**Recommendations:**

- **1-3** (default): Conservative, smooth eBay API usage
- **5-10**: Faster replenishment for high-volume sellers
- **50+**: No effective limit (replenish all at once)

**Note:** Each eBay account has a daily API call limit (~5000 calls). Set velocity based on your total listing count.

---

## Smart Scheduler — Tiered Sync Frequency

Coderom adjusts scraper frequency based on your listing count:

| Listings | Tier | Sync Interval | Rationale |
|----------|------|---------------|-----------|
| **< 500** | Tier 1 | **Every 2 hours** | Small catalog, frequent updates |
| **500-2000** | Tier 2 | **Every 4 hours** | Medium catalog, balanced |
| **> 2000** | Tier 3 | **Every 6 hours** | Large catalog, resource-optimized |

**Why tiers?** Scraping 5000 Amazon pages every hour would:
- ❌ Overwhelm Amazon servers (risk of IP ban)
- ❌ Waste Coderom server resources
- ❌ Provide diminishing returns (stock doesn't change THAT often)

**Manual override:** Not available in Settings UI. Contact support if you need custom sync frequency.

---

## Order-Triggered Sync

**When:** eBay order webhook is received
**Action:** Instant scraper sync for ONLY that product

**Flow:**

1. **Buyer purchases on eBay** → eBay sends webhook to Coderom
2. **Coderom receives order** → Creates order record
3. **Trigger scraper** for that specific ASIN
4. **Fetch current Amazon stock**
5. **Update eBay quantity** if changed

**Example:**

- **10:00 AM**: Product has Amazon stock = 10, eBay qty = 9 (buffer 1)
- **10:15 AM**: Buyer purchases on eBay
- **10:15:05 AM**: Coderom scrapes Amazon → stock still 10
- **10:15:10 AM**: Reduce eBay qty to 8 (account for the sale)

**Why instant sync?** Prevents overselling if multiple buyers purchase in quick succession.

**Note:** Order-triggered sync happens IN ADDITION TO scheduled sync, not instead of.

---

## Stock Sync Status Indicators

### Frontend (Products Page)

| Icon | Status | Meaning |
|------|--------|---------|
| 🟢 | **In Stock** | Amazon qty > 0, eBay qty > 0 |
| 🟡 | **Low Stock** | Amazon qty 1-3, eBay qty 1-2 (threshold warning) |
| 🔴 | **Out of Stock** | Amazon qty = 0, eBay qty = 0 |
| 🛡️ | **Safe Repricing** | OOS due to price volatility protection |
| ⏳ | **Replenish Pending** | Stock returned, waiting for delay timer |

### OOS Reason Column

Explains WHY a product is OOS:

| OOS Reason | Cause |
|------------|-------|
| **Amazon OOS** | Supplier has no stock |
| **Safe Repricing (133% change)** | Price volatility protection triggered |
| **Manual** | User manually set qty = 0 |
| **Auto-End** | Listing ended after 30 days OOS |
| **Replenish Skipped (low margin)** | Stock available but margin < threshold |

---

## Real-World Scenarios

### Scenario 1: Product Goes OOS

**Timeline:**

| Time | Event | Amazon Stock | eBay Qty | Action |
|------|-------|--------------|----------|--------|
| Day 1, 10:00 | Normal | 15 | 14 | — |
| Day 1, 14:00 | Scraper sync | 0 | 0 | ✅ Set OOS |
| | | | | \`oos_since = 2026-04-01 14:00\` |
| Day 5 | Still OOS | 0 | 0 | — |
| Day 30 | Auto-end check | 0 | — | ❌ Listing not ended yet (exactly 30 days) |
| Day 31 | Scheduler | 0 | — | ✅ End listing (>= 30 days) |

**Result:** Listing permanently ended, removed from Coderom and eBay.

---

### Scenario 2: Stock Replenishment (Success)

**Settings:**
- stockReplenishEnabled: true
- stockReplenishDelayMinutes: 60
- stockReplenishMinMargin: 0.10
- stockReplenishVelocityLimit: 3

**Timeline:**

| Time | Amazon Stock | Margin | Status | Action |
|------|--------------|--------|--------|--------|
| 10:00 | 0 | — | OOS | \`oos_since = NOW\` |
| 11:00 | 8 | 15% | **Stock detected** | Start delay timer ⏱️ |
| 11:30 | 8 | 15% | Waiting | — |
| 12:00 | 8 | 15% | **60 min passed** | ✅ Replenish |
| | | | | eBay qty = 7 (8 - buffer 1) |
| | | | | Clear \`oos_since\` |

**Result:** Listing restored automatically.

---

### Scenario 3: Replenishment Skipped (Low Margin)

**Timeline:**

| Time | Amazon Stock | Amazon Price | Margin | Action |
|------|--------------|--------------|--------|--------|
| 10:00 | 0 | £10 | — | OOS |
| 11:00 | 5 | **£18** (price spike!) | 2% | ❌ Skip replenish |
| | | | | (margin 2% < 10% min) |

**Result:** Listing stays OOS. User must manually adjust eBay price to restore margin, then manually set qty > 0.

**Alternative:** Safe Repricing would have triggered if price changed > 50% before going OOS.

---

## Troubleshooting

### Stock not syncing to eBay

**Problem:** Amazon shows 15 units, eBay still shows 0

**Check:**

1. **Last scraped time** — Products page → "Last Scraped" column
   - If > 6 hours ago, scraper may be stuck
2. **eBay token valid?** — Settings → eBay Accounts → "Connected"
3. **Safe Repricing triggered?** — Check OOS reason column
4. **needs_ebay_update flag** — Database \`products.needs_ebay_update = true\`?
5. **Scheduler running?** — Backend logs: \`journalctl -u coderom-backend | grep scheduler\`

**Solution:** Usually resolves on next sync cycle (2-6 hours depending on tier).

---

### Products auto-ending too aggressively

**Problem:** Listings ending after 7 days OOS, but Amazon often restocks after 10 days

**Solution:**
- Increase \`autoEndOosDays\` from 30 → 60 or 90
- OR disable \`autoEndOosEnabled\` entirely
- OR enable stock replenishment to auto-restore instead of ending

---

### Too many replenishments at once (eBay API limit)

**Problem:** 200 products restocked, eBay API rejected 150 calls

**Solution:**
- Reduce \`stockReplenishVelocityLimit\` from 10 → 3
- Replenishments will queue and process gradually over next few hours

---

### Buffer causing all listings to be OOS

**Problem:** Amazon stock = 1, buffer = 1, eBay qty = 0 for all products

**Solution:**
- **Option 1:** Reduce buffer to 0 (no safety margin)
- **Option 2:** Filter out low-stock products (don't list items with Amazon qty < 5)
- **Option 3:** Accept that some listings will be OOS (safety > sales)

---

## Best Practices

### Recommended Settings for Most Dropshippers

\`\`\`json
{
  "stockBuffer": 1,
  "autoEndOosEnabled": true,
  "autoEndOosDays": 30,
  "stockReplenishEnabled": true,
  "stockReplenishDelayMinutes": 60,
  "stockReplenishMinMargin": 0.10,
  "stockReplenishVelocityLimit": 3
}
\`\`\`

### High-Volume Sellers (2000+ Listings)

\`\`\`json
{
  "stockBuffer": 1,
  "autoEndOosEnabled": true,
  "autoEndOosDays": 14,  // Faster cleanup
  "stockReplenishEnabled": true,
  "stockReplenishDelayMinutes": 120,  // More conservative
  "stockReplenishMinMargin": 0.15,  // Higher profit requirement
  "stockReplenishVelocityLimit": 10  // Faster replenishment
}
\`\`\`

### Conservative Sellers (Risk-Averse)

\`\`\`json
{
  "stockBuffer": 2,  // Extra safety
  "autoEndOosEnabled": false,  // Manual control
  "stockReplenishEnabled": false,  // Manual restocking
  "stockReplenishMinMargin": 0.20  // High margin requirement
}
\`\`\`

---

## Related Articles

- **Smart Repricing** — Price recalculations trigger stock revisions
- **Safe Repricing Protection** — OOS due to price volatility
- **Amazon Filters** — Filter out low-stock suppliers at import time

---

**Need help?** Contact support or use the in-app chat widget.
      `
    },
    {
      id: 'amazon-filters-guide',
      title: 'Amazon Supplier Filters — Quality Control & Profit Protection',
      excerpt: 'Control which Amazon suppliers Coderom sources from — require Prime/FBA, exclude Amazon Retail, filter by price/delivery/condition, detect price volatility, and select from 8 global marketplaces. Configure all filters in Settings → Amazon Filters tab.',
      category: 'amazon-filters',
      readTime: '8 min',
      icon: '📦',
      content: `
# Amazon Supplier Filters

Control which Amazon offers Coderom uses as suppliers — filter by fulfillment type, seller reputation, price limits, delivery speed, and price stability.

---

## Overview

When you import an ASIN (e.g., B08N5WRWNW), Amazon often has **multiple sellers** offering the same product:

- 🟠 **Amazon.com** (retail) — £15.99, Prime, next-day delivery
- 🔵 **Seller A** (FBA) — £14.50, Prime, 1-2 day delivery
- ⚪ **Seller B** (FBM) — £12.00, Standard, 5-7 day delivery
- ⚪ **Seller C** (FBM) — £18.50, Prime (seller-fulfilled)

**The question:** Which one should Coderom use as your supplier?

**The answer:** Depends on your **Amazon Filter settings**.

This guide covers all filter options in **Settings → Amazon Filters** tab, which control offer selection during:
- ✅ ASIN import (Drafts → Add Product)
- ✅ Scraper sync (automatic price/stock updates)
- ✅ Bulk import (CSV/ASIN list)

---

## How Offer Selection Works

### Step 1: Fetch All Offers

**Backend:** \`services/amazon_scraper.py\`

When scraping an ASIN, Coderom fetches:
\`\`\`json
{
  "offers": [
    {
      "seller_name": "Amazon.com",
      "price": 15.99,
      "shipping": 0,
      "total": 15.99,
      "prime": true,
      "fba": false,
      "condition": "New",
      "delivery_days": 1,
      "dispatch_days": 0,
      "available_qty": 20
    },
    {
      "seller_name": "TechSupplier Ltd",
      "price": 14.50,
      "shipping": 0,
      "total": 14.50,
      "prime": true,
      "fba": true,
      "condition": "New",
      "delivery_days": 2,
      "dispatch_days": 1,
      "available_qty": 8
    },
    // ... more offers
  ]
}
\`\`\`

---

### Step 2: Apply Filters

Coderom filters the offers based on your settings:

**Filter Pipeline:**

1. ✅ **Condition filter** — Keep only "New" (exclude Used/Refurbished)
2. ✅ **Prime/FBA filter** — Require Prime eligibility if enabled
3. ✅ **Amazon-only filter** — Keep only Amazon Retail if enabled
4. ✅ **FBM filter** — Exclude FBM sellers if disabled
5. ✅ **Quantity filter** — Require minimum available stock
6. ✅ **Price filter** — Exclude offers above max price
7. ✅ **Delivery filter** — Exclude slow delivery (> max days)
8. ✅ **Dispatch filter** — Exclude slow processing (> max days)

After filtering, Coderom has a **shortlist of eligible offers**.

---

### Step 3: Select Best Offer

**Selection Logic** (configurable via \`useLowestPrimeOffer\`):

| Setting | Logic |
|---------|-------|
| **useLowestPrimeOffer = true** | Pick LOWEST PRICE among Prime offers |
| **useLowestPrimeOffer = false** | Pick LOWEST PRICE among all eligible offers |

**Example:**

Eligible offers after filters:
- Amazon.com: £15.99 (Prime)
- Seller A: £14.50 (Prime, FBA)
- Seller B: £16.00 (FBM, allowed)

| useLowestPrimeOffer | Selected Offer | Reason |
|---------------------|----------------|--------|
| **true** | Seller A (£14.50) | Lowest Prime offer |
| **false** | Seller A (£14.50) | Lowest overall |

---

## Amazon Filters Tab Settings

Access in **Settings → Amazon Filters**:

### 1. Marketplace Selection (marketplace)

**Type:** Dropdown
**Options:** UK, US, DE, FR, IT, ES, AU, CA
**Default:** UK

**What it does:** Which Amazon marketplace to scrape products from.

**Supported Marketplaces:**

| Code | Marketplace | Domain | Currency |
|------|-------------|--------|----------|
| **UK** | United Kingdom | amazon.co.uk | GBP (£) |
| **US** | United States | amazon.com | USD ($) |
| **DE** | Germany | amazon.de | EUR (€) |
| **FR** | France | amazon.fr | EUR (€) |
| **IT** | Italy | amazon.it | EUR (€) |
| **ES** | Spain | amazon.es | EUR (€) |
| **AU** | Australia | amazon.com.au | AUD ($) |
| **CA** | Canada | amazon.ca | CAD ($) |

**How to choose:**

- ✅ **Match your eBay marketplace** (UK eBay → UK Amazon, US eBay → US Amazon)
- ✅ **Match your target customer location** (faster shipping)
- ✅ **Consider import duties** (EU sellers: DE/FR/IT/ES avoid UK customs post-Brexit)

**Note:** Changing marketplace requires re-importing ASINs (product IDs differ between marketplaces).

---

### 2. Prime/FBA Requirements

#### Require Prime/FBA (requirePrimeFBA)

**Type:** Boolean
**Default:** true

**What it does:**
- **true**: Only select offers with Prime badge (FBA or Amazon-fulfilled)
- **false**: Allow non-Prime offers (FBM sellers)

**Why require Prime?**

✅ **Faster delivery** — Prime = 1-2 days, FBM = 5-7+ days
✅ **Higher conversion** — eBay buyers expect fast shipping
✅ **Reliable stock** — FBA sellers rarely cancel orders
✅ **Amazon customer service** — FBA orders handled by Amazon

**When to disable:**

- ❌ Niche products where Prime offers are rare
- ❌ You're okay with longer delivery times
- ❌ Focusing on price over speed

**Example:**

| Offer | Prime | requirePrimeFBA = true | requirePrimeFBA = false |
|-------|-------|------------------------|-------------------------|
| Amazon Retail | ✅ | ✅ Eligible | ✅ Eligible |
| FBA Seller | ✅ | ✅ Eligible | ✅ Eligible |
| FBM Seller (fast) | ❌ | ❌ Rejected | ✅ Eligible |

---

### 3. Amazon-Only Filter (amazonOnly)

**Type:** Boolean
**Default:** false

**What it does:**
- **true**: ONLY use Amazon Retail as supplier (ignore 3rd-party sellers)
- **false**: Allow any seller (Amazon or 3rd-party)

**Why enable?**

✅ **Maximum reliability** — Amazon never cancels, always ships fast
✅ **Consistent quality** — Amazon-sold products are vetted
✅ **No seller games** — No bait-and-switch, fake reviews, price manipulation

**Why disable?**

- ❌ Amazon Retail is often MORE expensive than FBA sellers
- ❌ Many products ONLY available via 3rd-party (especially niche items)
- ❌ Limits your product catalog significantly

**Recommended:** Keep **false** unless you have strict quality requirements.

---

### 4. Allow FBM (allowFBM)

**Type:** Boolean
**Default:** false

**What it does:**
- **true**: Allow Fulfilled-by-Merchant (seller ships directly)
- **false**: ONLY allow FBA or Amazon-fulfilled

**Difference from requirePrimeFBA:**

| Setting | FBA Allowed | Amazon Retail Allowed | FBM Allowed |
|---------|-------------|----------------------|-------------|
| **requirePrimeFBA = true** | ✅ | ✅ | ❌ |
| **requirePrimeFBA = false** | ✅ | ✅ | ✅ |
| **allowFBM = true** | ✅ | ✅ | ✅ |
| **allowFBM = false** | ✅ | ✅ | ❌ |

**When to enable FBM:**

- ✅ Product category where FBM is dominant (handmade, custom items)
- ✅ You verify FBM seller ratings manually
- ✅ Price is much lower (accept slower delivery trade-off)

**Risks of FBM:**

- ❌ Higher cancellation rates (seller runs out of stock)
- ❌ Slower shipping (3-7 days vs. 1-2 days)
- ❌ Inconsistent packaging quality
- ❌ Harder to handle returns/refunds

**Recommended:** Keep **false** unless necessary.

---

### 5. Use Lowest Prime Offer (useLowestPrimeOffer)

**Type:** Boolean
**Default:** true

**What it does:**
- **true**: Among Prime offers, pick the cheapest
- **false**: Among ALL eligible offers (Prime + non-Prime), pick cheapest

**Example:**

Eligible offers:
- Amazon.com: £20 (Prime)
- FBA Seller: £18 (Prime)
- FBM Seller: £15 (non-Prime, but allowed by filters)

| useLowestPrimeOffer | Selected | Reason |
|---------------------|----------|--------|
| **true** | FBA Seller (£18) | Lowest Prime |
| **false** | FBM Seller (£15) | Lowest overall |

**When to enable:**

- ✅ Prioritize delivery speed over cost
- ✅ You've allowed FBM but prefer Prime when price is close

**When to disable:**

- ✅ Prioritize lowest cost above all else
- ✅ You trust your FBM filter settings

**Recommended:** Keep **true** for balanced quality/price.

---

### 6. Minimum Available Quantity (minAvailableQty)

**Type:** Integer (units)
**Default:** 1

**What it does:** Exclude offers with less than N units in stock.

**Use cases:**

| Setting | When to Use |
|---------|-------------|
| **1** (default) | Accept any in-stock offer |
| **5** | Avoid low-stock sellers (risk of cancellation) |
| **10+** | Only source from high-volume suppliers |

**Example:**

| Offer | Available Qty | minAvailableQty = 5 | Result |
|-------|---------------|---------------------|--------|
| Seller A | 2 | 5 | ❌ Rejected |
| Seller B | 8 | 5 | ✅ Eligible |
| Amazon | 50 | 5 | ✅ Eligible |

**Why filter low stock?**

- ✅ Prevents importing products that will go OOS quickly
- ✅ Reduces "Auto-End OOS" churn
- ✅ Focuses on stable, high-volume suppliers

**Note:** Amazon doesn't always show exact quantity (often just "In Stock"). In those cases, Coderom assumes qty = 999.

---

### 7. Maximum Supplier Price (maxSupplierPrice)

**Type:** Integer (£/$/€)
**Default:** 500

**What it does:** Exclude offers above this price.

**Use cases:**

| Setting | When to Use |
|---------|-------------|
| **£50** | Small items only (accessories, home goods) |
| **£500** (default) | Most electronics, furniture |
| **£5000** | High-value items (TVs, appliances) |

**Why set a limit?**

- ✅ **Cash flow protection** — Don't tie up £1000+ per order
- ✅ **Risk management** — Limit exposure on expensive items
- ✅ **Profitability** — Higher-priced items often have lower % margins

**Example:**

| Product | Amazon Price | maxSupplierPrice | Result |
|---------|--------------|------------------|--------|
| Phone Case | £8 | £500 | ✅ Import |
| Laptop | £650 | £500 | ❌ Rejected (too expensive) |
| Headphones | £120 | £500 | ✅ Import |

**Recommended:** £200-£500 for most dropshippers.

---

### 8. Delivery & Dispatch Filters

#### Max Delivery Days (maxDeliveryDays)

**Type:** Integer (days)
**Default:** 5

**What it does:** Exclude offers with delivery time > N days.

**Example:**

| Offer | Delivery Days | maxDeliveryDays = 5 | Result |
|-------|---------------|---------------------|--------|
| Amazon Prime | 1 | 5 | ✅ Eligible |
| FBA Seller | 3 | 5 | ✅ Eligible |
| FBM Seller | 7 | 5 | ❌ Rejected (too slow) |

**Recommended Values:**

- **1-2 days**: Ultra-fast only (Prime/Amazon)
- **5 days** (default): Balanced (most Prime + fast FBM)
- **10+ days**: Accept slow international shipping

---

---

### 9. Price Volatility Filter

#### Use Volatility Filter (useVolatilityFilter)

**Type:** Boolean
**Default:** false

**What it does:**
- **true**: Track price history, exclude products with high volatility
- **false**: Ignore price stability (import any product)

**How it works:**

1. **Track price for last 30 days** (during scraper sync)
2. **Calculate volatility:**
   \`\`\`
   volatility_% = ((max_price - min_price) / min_price) × 100
   \`\`\`
3. **Compare to maxPriceVolatility threshold**
4. **If volatility > threshold:** Flag product, optionally skip import

**Use case:** Avoid trending/viral products with unstable prices (e.g., scalped game consoles, crypto miners).

---

#### Max Price Volatility (maxPriceVolatility)

**Type:** Integer (%)
**Default:** 50

**What it does:** Maximum allowed price swing over 30 days.

**Example:**

| Product | 30-Day Price Range | Volatility | maxPriceVolatility = 50 | Result |
|---------|-------------------|------------|-------------------------|--------|
| Phone Charger | £8 - £9 | 12.5% | 50% | ✅ Stable |
| GPU (shortage) | £300 - £800 | 167% | 50% | ❌ Too volatile |
| Headphones | £20 - £25 | 25% | 50% | ✅ Stable |

**Recommended Values:**

- **20-30%**: Very strict (stable products only)
- **50%** (default): Balanced (filters extreme volatility)
- **100%+**: Lenient (allow most products)

**Note:** Requires historical price data. New products (< 30 days tracked) bypass this filter.

---

## Filter Combinations & Examples

### Example 1: Conservative Dropshipper

**Goal:** Maximum reliability, accept lower margins for quality

**Settings:**
\`\`\`json
{
  "marketplace": "UK",
  "requirePrimeFBA": true,
  "amazonOnly": true,  // Amazon Retail only
  "allowFBM": false,
  "useLowestPrimeOffer": true,
  "minAvailableQty": 10,
  "maxSupplierPrice": 200,
  "maxDeliveryDays": 2,
  "useVolatilityFilter": true,
  "maxPriceVolatility": 30
}
\`\`\`

**Result:** ONLY Amazon-sold products, fast delivery, stable prices. Smaller catalog, higher cost, maximum reliability.

---

### Example 2: Aggressive Price Hunter

**Goal:** Lowest possible supplier cost, accept some risk

**Settings:**
\`\`\`json
{
  "marketplace": "UK",
  "requirePrimeFBA": false,
  "amazonOnly": false,
  "allowFBM": true,  // Allow Merchant-fulfilled
  "useLowestPrimeOffer": false,  // Lowest overall price
  "minAvailableQty": 1,
  "maxSupplierPrice": 500,
  "maxDeliveryDays": 10,
  "useVolatilityFilter": false
}
\`\`\`

**Result:** Maximum product availability, lowest prices, slower delivery. Larger catalog, higher risk of cancellations.

---

### Example 3: Balanced Dropshipper (Recommended)

**Goal:** Balance between cost, speed, and reliability

**Settings:**
\`\`\`json
{
  "marketplace": "UK",
  "requirePrimeFBA": true,  // Prime only
  "amazonOnly": false,  // Allow 3rd-party FBA
  "allowFBM": false,
  "useLowestPrimeOffer": true,
  "minAvailableQty": 5,
  "maxSupplierPrice": 300,
  "maxDeliveryDays": 5,
  "useVolatilityFilter": false
}
\`\`\`

**Result:** Fast Prime delivery, competitive pricing, stable suppliers. **This is the default configuration.**

---

## Testing Your Filters

### Step 1: Import 10 Test Products

1. Go to **Drafts → Add Product**
2. Enter 10 ASINs from different categories
3. Click "Import"
4. **Check results:**
   - How many found valid suppliers?
   - What's the average supplier cost?
   - Are delivery times acceptable?

### Step 2: Review Supplier Selection

In Drafts table, check:
- **Supplier column** — Is it Amazon, FBA, or FBM?
- **Amazon Price** — Is it competitive?
- **Delivery Days** — Matches your maxDeliveryDays setting?

### Step 3: Adjust Filters

If too many rejections:
- ✅ Increase maxSupplierPrice
- ✅ Increase maxDeliveryDays
- ✅ Set allowFBM = true

If quality concerns:
- ✅ Enable amazonOnly
- ✅ Decrease maxDeliveryDays
- ✅ Increase minAvailableQty

---

## Troubleshooting

### "No valid offers found" error

**Problem:** 90% of ASINs fail to import

**Check:**

1. **Marketplace mismatch** — US ASIN on UK Amazon = not found
2. **Filters too strict** — maxSupplierPrice = £10 but product costs £20
3. **Prime requirement** — No Prime sellers available for that ASIN
4. **Out of stock** — All sellers have qty = 0

**Solution:** Temporarily relax filters (allowFBM = true, maxSupplierPrice = 999) to test.

---

### Importing expensive suppliers when cheap ones exist

**Problem:** Amazon Retail (£20) selected instead of FBA seller (£15)

**Check:**

1. **amazonOnly = true?** — Forcing Amazon Retail
2. **FBA seller filtered out?** — Check delivery days, dispatch days, quantity
3. **Price volatility?** — Cheap seller flagged as volatile

**Solution:** Set amazonOnly = false, review filter logs in backend.

---

### Products flip-flopping between suppliers

**Problem:** Today supplier A (£10), tomorrow supplier B (£12), repeat

**Root cause:** Both suppliers are eligible, prices fluctuate slightly

**Solution:**
- Enable \`useVolatilityFilter\` to prefer stable suppliers
- Increase \`minPriceDeltaAbs\` in Pricing settings (only reprice if change > £0.50)

---

## Backend Implementation

Amazon Filters are applied in **backend/services/amazon_scraper.py**:

### Main Functions

1. **\`get_offers(asin, marketplace)\`**
   - Scrapes Amazon product page
   - Extracts all seller offers
   - Returns array of offer objects

2. **\`filter_offers(offers, settings)\`**
   - Applies all filter rules from settings
   - Returns eligible offers only

3. **\`select_best_offer(offers, useLowestPrimeOffer)\`**
   - Sorts by price (ascending)
   - Picks lowest Prime (or lowest overall)
   - Returns single offer object

4. **\`update_product_supplier(product, offer)\`**
   - Updates product.amazon_price
   - Updates product.supplier_name
   - Triggers repricing calculation

---

## Related Articles

- **Smart Repricing** — Calculated eBay price based on supplier cost
- **Stock Synchronization** — Monitor supplier stock levels
- **Safe Repricing** — Protection from supplier price spikes

---

**Need help?** Contact support or use the in-app chat widget.
      `
    },
    {
      id: 'orders-guide',
      title: 'Order Management — Track, Search & Fulfill Orders',
      excerpt: 'Master Coderom\'s order management system — real-time eBay sync, advanced search (ASIN/buyer/eBay ID), status filters, tracking upload, SSE live updates, and order-triggered scraper sync. Monitor every order from New → Shipped → Delivered.',
      category: 'orders',
      readTime: '7 min',
      icon: '📦',
      content: `
# Order Management System

Coderom automatically syncs all eBay orders and provides powerful tools to search, filter, track, and fulfill them efficiently.

---

## Overview

Every order placed on your eBay store is automatically pulled into Coderom within **60 seconds** via eBay webhooks + polling:

✅ **Automatic eBay sync** — Orders appear instantly in Coderom
✅ **Advanced search** — Find orders by ASIN, buyer name, eBay Order ID, or tracking number
✅ **Status filters** — All / To Ship / Dispatched / Delivered / Cancelled
✅ **Date filters** — Today / Yesterday / Last 7 Days / This Month / This Year
✅ **Tracking management** — Upload tracking manually or via Gmail automation
✅ **Real-time updates** — SSE (Server-Sent Events) push status changes without page refresh
✅ **Order trigger** — Each new order triggers instant scraper sync for that ASIN
✅ **AO (Automatic Ordering)** — Send orders to Chrome Extension for automated Amazon checkout

This guide covers the complete order workflow from **New Order → Shipped → Delivered**.

---

## Order List Page

**Location:** Dashboard → Orders

### Tabs (Status Filters)

The order list has 5 tabs:

| Tab | Filter | Description |
|-----|--------|-------------|
| **All** | No filter | Show all orders regardless of status |
| **To Ship** | status = "To Ship" | Orders awaiting dispatch (NEW orders) |
| **Dispatched** | status = "Dispatched" | Orders with tracking uploaded to eBay |
| **Delivered** | status = "Delivered" | Confirmed delivered orders |
| **Cancelled** | status = "Cancelled" | Buyer/seller cancelled orders |

**Order status flow:**

\`\`\`
New Order
   ↓
To Ship (amazon_order_status = NONE or QUEUED)
   ↓
Dispatched (tracking uploaded → CompleteSale API)
   ↓
Delivered (carrier confirms delivery)
\`\`\`

---

### Date Filters

Filter orders by date range:

| Filter | Range | Use Case |
|--------|-------|----------|
| **All Time** | No date limit | View entire order history |
| **Today** | >= 00:00 today | Check today's sales |
| **Yesterday** | 00:00-23:59 yesterday | Review yesterday's orders |
| **Last 7 Days** | >= 7 days ago | Weekly summary |
| **This Month** | >= 1st of current month | Monthly reports |
| **This Year** | >= Jan 1st of current year | Annual analysis |

**Persistence:** Date filter selection is saved to localStorage and persists across sessions.

---

### Search Functionality

**Minimum 2 characters** to trigger backend search.

**What you can search:**

| Search Term | Matches |
|-------------|---------|
| **ASIN** | B08N5WRWNW, B07XYZ123A |
| **eBay Order ID** | 12-12345-67890 |
| **Buyer name** | "John Smith" |
| **Buyer username** | @johndoe123 |
| **Tracking number** | XX000000000GB, 15501234567890 |
| **Product title** | "Wireless Headphones" |

**How it works:**

1. **Type search term** (e.g., "B08N5WRWNW")
2. **500ms debounce delay**
3. **Backend API call:** \`GET /api/orders?search=B08N5WRWNW&limit=10000\`
4. **Results displayed** — only matching orders shown
5. **Clear search** (< 2 characters) → returns to context cache (all orders)

**Backend search query** (PostgreSQL):

\`\`\`sql
WHERE (
  buyer_name ILIKE '%search%' OR
  buyer_username ILIKE '%search%' OR
  order_id ILIKE '%search%' OR
  line_items::text ILIKE '%search%'  -- JSON search for ASIN/title
)
\`\`\`

---

### Pagination

**Settings:**

- **Page size**: 20, 50, 100, or 200 orders per page (default: 20)
- **Current page**: 1 to N (auto-resets to 1 when filters change)
- **Total pages**: Calculated as \`ceil(filteredOrders.length / pageSize)\`

**Persistence:** Page size saved to localStorage.

**Example:**

- Total orders: 487
- Page size: 20
- Total pages: 25
- Pagination buttons: ‹ 1 2 3 ... 25 ›

---

### Order Columns

| Column | Data | Source |
|--------|------|--------|
| **eBay Order ID** | 12-12345-67890 | \`order.order_id\` |
| **Buyer** | John Smith (@johndoe) | \`buyer_name\` + \`buyer_username\` |
| **Product** | Wireless Headphones | \`line_items[0].title\` (first item) |
| **Items** | 1× or 2× | Count of line_items array |
| **Order Date** | 2026-04-12 14:30 | \`order_date\` (UTC → local) |
| **Amount** | £45.99 | \`total_amount\` (GBP formatted) |
| **Status** | To Ship / Dispatched | \`status\` field |
| **AO Status** | QUEUED / COMPLETED | \`amazon_order_status\` |
| **Actions** | View / Track / Send to Amazon | Action buttons |

---

## Order Detail Page

**Access:** Click **"View"** button on any order

### Sections

#### 1. Action Bar (Top)

**Buttons:**

| Button | Action | Condition |
|--------|--------|-----------|
| **← Back to Orders** | Return to list | Always |
| **Send to Amazon** | Add to AO queue | amazon_order_status = NONE |
| **Add Tracking** | Manual tracking upload | No tracking exists |
| **Edit Tracking** | Update tracking info | Tracking exists |
| **Refresh Status** | Sync from eBay | Always |

---

#### 2. Order Information

**Displayed:**

- **eBay Order ID**: Clickable link → eBay order page
- **Order Date**: Human-readable (e.g., "April 12, 2026 at 2:30 PM")
- **Total Amount**: £45.99 GBP
- **Payment Status**: PAID / PENDING
- **Fulfillment Status**: NOT_STARTED / IN_PROGRESS / FULFILLED

---

#### 3. Buyer Information

- **Name**: John Smith
- **Username**: johndoe123
- **Email**: (from eBay, if provided)

---

#### 4. Shipping Address

\`\`\`
John Smith
123 High Street
Apartment 4B
London, Greater London SW1A 1AA
United Kingdom
Phone: +44 20 1234 5678
\`\`\`

**Used for:** AO (Automatic Ordering) → Amazon "Send to this address" prefill

---

#### 5. Line Items (Products)

For each product in the order:

| Field | Example |
|-------|---------|
| **Title** | Wireless Bluetooth Headphones |
| **ASIN** | B08N5WRWNW |
| **SKU** | WH-BT-001 |
| **Quantity** | 2 |
| **Price** | £22.99 |
| **eBay Item ID** | 387721248895 |

**Multi-item orders:**

If buyer purchases 2+ different products, all are listed here.

---

#### 6. Tracking Information

**If tracking exists:**

\`\`\`
Tracking Number: XX000000000GB
Carrier: Royal Mail
Shipped At: 2026-04-12 15:00 UTC
Tracking URL: https://track.royalmail.com/...
\`\`\`

**If no tracking:**

> No tracking information available. Click "Add Tracking" to upload.

---

### Manual Tracking Upload

**Steps:**

1. **Click "Add Tracking"** button
2. **Enter tracking number** (e.g., XX000000000GB)
3. **Select carrier** (Royal Mail, DPD, Evri, Amazon Logistics, etc.)
4. **Optional: Amazon Order ID** (links order to Amazon tracking)
5. **Click "Submit"**

**What happens:**

1. **Database update:**
   \`\`\`sql
   INSERT INTO parcelflix_trackings (
     user_id, ebay_order_id, tracking_number, carrier, status
   ) VALUES (...)

   UPDATE ebay_orders SET
     parcelflix_tracking_id = <new_id>,
     status = 'Dispatched'
   \`\`\`

2. **eBay CompleteSale API call:**
   \`\`\`xml
   <CompleteSale>
     <OrderID>12-12345-67890</OrderID>
     <Shipment>
       <ShipmentTrackingNumber>XX000000000GB</ShipmentTrackingNumber>
       <ShippingCarrier>RoyalMail</ShippingCarrier>
     </Shipment>
   </CompleteSale>
   \`\`\`

3. **Auto message sent** (if enabled):
   - Template: \`autoMsgShippedBody\`
   - Variables replaced: \`{{tracking_number}}\`, \`{{tracking_carrier}}\`, \`{{buyer_first_name}}\`

4. **SSE broadcast:**
   - \`order_update\` event sent to frontend
   - Order status updates in real-time without page refresh

---

## Order Sync Process

### eBay → Coderom Sync

**Two methods:**

#### 1. Webhook (Real-time)

When buyer places order on eBay:

1. **eBay sends webhook** → \`POST /api/ebay/webhook\`
2. **Coderom receives notification** (order created/updated/cancelled)
3. **Fetch full order details** via eBay Trading API \`GetOrders\`
4. **Save to database** → \`ebay_orders\` table
5. **Trigger scraper** for each ASIN in order (instant stock check)
6. **SSE broadcast** → Order appears in frontend instantly

**Latency:** < 60 seconds from eBay order to Coderom

---

#### 2. Polling (Backup)

**Frequency:** Every 5 minutes

**Query:**

\`\`\`
GET https://api.ebay.com/sell/fulfillment/v1/order
  ?filter=creationdate:[2026-04-12T00:00:00.000Z..NOW]
  &limit=200
\`\`\`

**Purpose:** Catch orders that webhook missed (rare, but happens if webhook endpoint is down)

---

### SSE (Server-Sent Events)

**What is SSE?**

Server-Sent Events allow the backend to push real-time updates to the frontend without polling.

**How it works:**

1. **Frontend connects:** \`EventSource(\`/api/sse/subscribe?user_id=123\`)\`
2. **Backend holds connection open**
3. **Order update occurs** (tracking added, status changed, AO completed)
4. **Backend pushes event:**
   \`\`\`json
   event: order_update
   data: {
     "type": "order_update",
     "orderId": "456",
     "field": "status",
     "value": "Dispatched",
     "timestamp": "2026-04-12T15:00:00Z"
   }
   \`\`\`
5. **Frontend receives event** → Updates order in cache → Re-renders UI

**User experience:** Order status changes appear immediately without page refresh (like Discord/Slack)

---

## Order-Triggered Scraper Sync

**When:** New order webhook received

**Action:** Instant scraper sync for ONLY the ASINs in that order

**Why?**

- Buyer just purchased → Stock changed on Amazon
- Need to update eBay quantity immediately to prevent overselling
- If product has 2 eBay listings, both are updated

**Flow:**

\`\`\`
eBay Order: Buyer purchases ASIN B08N5WRWNW
   ↓
Webhook received → Coderom creates order record
   ↓
Trigger scraper_sync(asin="B08N5WRWNW")
   ↓
Fetch current Amazon stock (e.g., qty = 8)
   ↓
Update eBay listing(s): qty = 8 - buffer(1) = 7
   ↓
ReviseInventoryStatus API call → eBay updated
\`\`\`

**Timing:** Scraper runs within 30 seconds of order placement

---

## AO (Automatic Ordering) Integration

**Overview:** Send eBay orders to Chrome Extension for automated Amazon checkout.

**Status Badges in Order List:**

| Badge | amazon_order_status | Meaning |
|-------|---------------------|---------|
| — | NONE | Not sent to AO queue |
| 🕐 QUEUED | QUEUED | Waiting for Chrome Extension |
| ⏳ PROCESSING | IN_PROGRESS | Extension processing checkout |
| ✅ COMPLETED | PLACED | Amazon order placed successfully |
| ❌ FAILED | FAILED | Checkout failed (see ao_error field) |

**Send to Amazon button:**

- Visible when \`amazon_order_status = NONE\`
- Click → Sets status to QUEUED, adds \`ao_queued_at\` timestamp
- Chrome Extension polls \`/api/orders/ao-queue\` and processes order

**Bulk send:**

- Select multiple orders (checkboxes)
- "Send All to Amazon" button
- Sequential processing (1 order per minute to avoid Amazon bot detection)

**See [Automatic Ordering guide](#ao-guide) for full details.**

---

## Troubleshooting

### Orders not syncing from eBay

**Problem:** Order placed 30 minutes ago, not appearing in Coderom

**Check:**

1. **eBay webhook status** — Settings → eBay Accounts → "Webhook: Active"
2. **Token valid?** — If token expired, webhooks fail
3. **Backend logs:** \`journalctl -u coderom-backend | grep webhook\`
4. **Manual sync:** Click "Refresh Orders" button in Orders page

**Solution:** Usually resolves on next polling cycle (5 min). If not, reconnect eBay account.

---

### Search not finding order

**Problem:** Searching for "B08N5WRWNW" returns no results, but order exists

**Cause:** Search requires minimum 2 characters AND order must contain search term in:
- Buyer name
- eBay Order ID
- line_items JSON (ASIN/title)

**Solution:**

1. Clear search filter (search < 2 characters)
2. Use "All" tab + date filter to browse manually
3. Check if ASIN is in line_items (may be missing if product deleted)

---

### Tracking upload failed

**Problem:** "Add Tracking" button submitted, but eBay still shows "Not shipped"

**Check:**

1. **eBay API error** — Backend logs show CompleteSale response
2. **Common errors:**
   - Invalid tracking number format
   - Order already marked as shipped
   - Order cancelled
   - eBay token expired

**Solution:** Check backend logs (\`grep CompleteSale /var/log/coderom.log\`), fix error, retry

---

### SSE not working (no real-time updates)

**Problem:** Orders don't update automatically, need to refresh page

**Check:**

1. **Browser console:** \`EventSource\` connection errors?
2. **Network tab:** SSE connection established? (should show as pending/streaming)
3. **Ad blockers:** Some block SSE connections
4. **Firewall:** Corporate networks may block SSE

**Solution:**

- Disable ad blocker for Coderom domain
- Use Chrome/Firefox (better SSE support than Safari)
- Fallback: Manual refresh (Orders page auto-refreshes context cache every 60s)

---

## Best Practices

### Daily Order Workflow

**Morning:**

1. Check **"To Ship"** tab
2. Filter by **"Today"** date
3. Send all to Amazon (if using AO)
4. OR manually order from Amazon
5. Upload tracking (manual or wait for Gmail automation)

**Afternoon:**

1. Check **"Dispatched"** tab
2. Verify all tracking uploaded to eBay
3. Monitor AO queue for failures

**Evening:**

1. Check **"Delivered"** tab
2. Review any **"Cancelled"** orders
3. Plan next day's inventory

---

### Using Search Effectively

**Find specific order:**

- Search by eBay Order ID (exact match)

**Find all orders for a product:**

- Search by ASIN (e.g., "B08N5WRWNW")
- Shows all orders containing that product

**Find buyer's order history:**

- Search by buyer name
- Shows all orders from that customer

**Find untracked orders:**

- Cannot search directly
- Use: "Dispatched" tab → Sort by "Shipped At" → Check for nulls

---

## Related Articles

- **Automatic Ordering (AO)** — Automated Amazon checkout via Chrome Extension
- **Auto Messages** — Send automated messages to buyers on order/shipment events
- **Gmail Integration** — Auto-capture tracking from Amazon dispatch emails

---

**Need help?** Contact support or use the in-app chat widget.
      `
    },
    {
      id: 'safety-guide',
      title: 'Safety & Protection — VeRO Protection & Brand Blocking',
      excerpt: 'Protect your eBay store from VeRO claims using blocked brands, keywords, and whitelist rules.',
      category: 'safety-protection',
      readTime: '8 min',
      icon: '🛡️',
      content: `
# Automatic Ordering (AO) System

Coderom's Chrome Extension automates Amazon checkout — from adding items to cart through placing the order — eliminating manual order processing.

---

## Overview

**The Problem:**

You receive 50 eBay orders per day. Manually ordering each one from Amazon takes:
- 3-5 minutes per order
- 150-250 minutes total (2.5-4 hours of repetitive work)
- High risk of human error (wrong address, wrong quantity)

**The Solution: AO Chrome Extension**

Coderom's Chrome Extension connects to your backend and processes Amazon orders automatically:

✅ **Automatic cart management** — Add items to cart via ASIN
✅ **Address automation** — Send eBay buyer address to Amazon's address book
✅ **Checkout automation** — Navigate through cart → address → payment → place order
✅ **Price validation** — Compare scraper price vs. checkout price (spike detection)
✅ **Subscribe & Save guard** — Prevents accidental subscription
✅ **Voucher auto-claim** — Apply available vouchers/coupons automatically
✅ **Sequential processing** — Process orders one-by-one to avoid bot detection
✅ **Multi-item support** — Handle orders with 2+ different ASINs
✅ **Status tracking** — Real-time updates (QUEUED → PROCESSING → COMPLETED)

---

## How AO Works

### Step 1: Install Chrome Extension

**Installation:**

1. **Download extension** — Dashboard → Settings → Auto Order tab → "Download Extension"
2. **Unzip file** (e.g., \`coderom-ao-extension.zip\`)
3. **Open Chrome:** \`chrome://extensions\`
4. **Enable "Developer mode"** (top-right toggle)
5. **Click "Load unpacked"**
6. **Select unzipped folder**
7. **Extension installed** ✅ (icon appears in toolbar)

**Connect to account:**

1. **Click extension icon** in toolbar
2. **Enter API key** (from Settings → Auto Order → "Generate API Key")
3. **Click "Connect"**
4. **Status:** "Connected to [your-email@example.com]"

---

### Step 2: Send Order to AO Queue

**From Orders page:**

1. **Find order** (To Ship tab)
2. **Click "Send to Amazon"** button
3. **Order added to queue:**
   \`\`\`sql
   UPDATE ebay_orders SET
     amazon_order_status = 'QUEUED',
     ao_queued_at = NOW(),
     ao_error = NULL,
     ao_retry_count = 0
   \`\`\`

**Bulk send:**

1. **Select multiple orders** (checkboxes)
2. **Click "Send All to Amazon"** (action bar)
3. **All orders queued** in sequential order

---

### Step 3: Chrome Extension Polls Queue

**Polling logic:**

\`\`\`javascript
// Every 10 seconds
GET /api/orders/ao-queue

Response:
{
  "success": true,
  "queue": [
    {
      "orderId": 123,
      "ebayOrderId": "12-12345-67890",
      "amazonOrderStatus": "QUEUED",
      "lineItems": [
        {
          "asin": "B08N5WRWNW",
          "title": "Wireless Headphones",
          "quantity": 1,
          "amazonPrice": 25.99,
          "scraperUpdatedAt": "2026-04-12T14:00:00Z"
        }
      ],
      "shippingAddress": { ... },
      "queuedAt": "2026-04-12T15:00:00Z"
    }
  ]
}
\`\`\`

**Queue order:** Orders sorted by \`ao_queued_at\` (oldest first)

---

### Step 4: Checkout Flow Automation

Extension processes first order in queue:

#### 4.1. Add to Cart

**Action:** Navigate to \`https://www.amazon.co.uk/dp/{ASIN}?th=1&psc=1\`

**Steps:**

1. **Check product page loaded**
2. **Check availability:** "In Stock" or "Only X left in stock"
   - If OOS → FAILED status, skip order
3. **Set quantity** in dropdown (if qty > 1)
4. **Click "Add to Cart"** button
5. **Wait for cart confirmation**

**Multi-item orders:**

If order has 2+ ASINs:
- Repeat above for each ASIN
- All items must add successfully before proceeding

**Error handling:**

- ASIN not found → FAILED
- Out of stock → FAILED
- Add to cart failed (Amazon bot detection) → Retry 3×, then FAILED

---

#### 4.2. Subscribe & Save Detection

**Problem:** Some Amazon products auto-enable "Subscribe & Save" when added to cart.

**Detection:**

\`\`\`javascript
// After "Add to Cart"
if (document.querySelector('[data-subscription-id]')) {
  // Subscribe & Save detected!
  await clickElement('#sc-unsubscribe-all-option');
  // Deselect subscription checkbox
}
\`\`\`

**Why?** Subscriptions charge monthly, not one-time. This would destroy your profit margins.

---

#### 4.3. Price Validation

**Before proceeding to checkout:**

\`\`\`javascript
const cartPrice = parseFloat(
  document.querySelector('.sc-product-price').textContent
);
const scraperPrice = lineItem.amazonPrice; // From queue data
const priceDelta = Math.abs(cartPrice - scraperPrice);
const priceDeltaPct = (priceDelta / scraperPrice) * 100;

if (priceDeltaPct > 10) {
  // Price changed by > 10% since scrape
  ao_error = \`Price spike: £\${scraperPrice} → £\${cartPrice}\`;
  amazon_order_status = 'FAILED';
  return; // Abort checkout
}
\`\`\`

**Threshold:** 10% (configurable in Settings → Auto Order)

**Example:**

- Scraper price: £20.00
- Cart price: £23.50 (+17.5%)
- ❌ **FAILED** (price spike detected)

---

#### 4.4. Proceed to Checkout

**Action:** Click "Proceed to checkout" button in cart

**Amazon redirects to:** \`https://www.amazon.co.uk/gp/buy/spc/handlers/display.html\`

---

#### 4.5. Send to This Address

**Purpose:** Add eBay buyer's address to Amazon address book.

**Steps:**

1. **Click "Add a new address"**
2. **Fill form:**
   \`\`\`javascript
   document.querySelector('#address-ui-widgets-enterAddressFullName').value = order.shippingAddress.name;
   document.querySelector('#address-ui-widgets-enterAddressPhoneNumber').value = order.shippingAddress.phone;
   document.querySelector('#address-ui-widgets-enterAddressLine1').value = order.shippingAddress.street1;
   document.querySelector('#address-ui-widgets-enterAddressLine2').value = order.shippingAddress.street2;
   document.querySelector('#address-ui-widgets-enterAddressCity').value = order.shippingAddress.city;
   document.querySelector('#address-ui-widgets-enterAddressPostalCode').value = order.shippingAddress.postalCode;
   \`\`\`
3. **Click "Use this address"**
4. **Amazon saves address** to account

**Retry logic (3 attempts):**

- **Attempt 1:** Fill form, submit
- **If failed:** Clear form, refill, submit
- **If failed again:** Wait 2s, retry
- **If 3× failures:** \`ao_error = "Failed to save address"\`, status = FAILED

**Why 3 retries?** Amazon sometimes shows "Address already exists" error even for new addresses. Retry usually works.

---

#### 4.6. Payment Method

**Action:** Select saved payment method (default card on Amazon account)

**Steps:**

1. **Check if payment method already selected** (Amazon usually auto-selects)
2. **If not:** Click first available payment method radio button
3. **Verify selection:** Green checkmark appears

**Note:** Extension does NOT enter card details. You must have a payment method saved on your Amazon account.

---

#### 4.7. Voucher Auto-Claim

**Detection:**

\`\`\`javascript
const voucherCheckbox = document.querySelector('#sc-buy-box-gift-certificate-checkbox');
if (voucherCheckbox && !voucherCheckbox.checked) {
  voucherCheckbox.click(); // Apply voucher
}
\`\`\`

**Benefit:** Saves £0.50-£5.00 per order if voucher available.

---

#### 4.8. Place Order

**Final step:**

1. **Verify "Place your order" button visible**
2. **Click "Place your order"**
3. **Wait for confirmation page** (\`/gp/buy/thankyou\` or \`/gp/buy/payselect/handlers/display.html\`)
4. **Extract Amazon Order ID** from confirmation page:
   \`\`\`javascript
   const amazonOrderId = document.querySelector('.od-confirmation-order-id').textContent.trim();
   // e.g., "113-1234567-8901234"
   \`\`\`

---

### Step 5: Update Order Status

**Extension reports back to Coderom:**

\`\`\`javascript
POST /api/orders/{orderId}/ao-complete
{
  "success": true,
  "amazonOrderId": "113-1234567-8901234",
  "totalCost": 25.99,
  "trackingInfo": null // May be available later
}
\`\`\`

**Backend updates:**

\`\`\`sql
UPDATE ebay_orders SET
  amazon_order_status = 'PLACED',
  amazon_order_id = '113-1234567-8901234',
  ao_completed_at = NOW()
\`\`\`

**SSE broadcast:**

\`\`\`json
{
  "type": "ao_update",
  "orderId": 123,
  "status": "PLACED",
  "amazonOrderId": "113-1234567-8901234"
}
\`\`\`

**Frontend:** Order badge changes from ⏳ PROCESSING → ✅ COMPLETED

---

## AO Status Flow

| Status | amazon_order_status | Meaning | Next Action |
|--------|---------------------|---------|-------------|
| — | NONE | Not sent to AO | User clicks "Send to Amazon" |
| 🕐 QUEUED | QUEUED | Waiting in queue | Extension polls, starts processing |
| 📍 ADDRESS_FILLED | ADDRESS_FILLED | Address added to Amazon | Continue to cart |
| 🛒 CART_ADDED | CART_ADDED | Items added to cart | Validate price, proceed to checkout |
| 💳 CHECKOUT_READY | CHECKOUT_READY | Payment method selected | Place order |
| ⏳ PROCESSING | IN_PROGRESS | Checkout in progress | Wait for completion |
| ⏸️ AWAITING_CONFIRMATION | AWAITING_CONFIRMATION | Waiting for Amazon confirmation page | Extract order ID |
| ✅ COMPLETED | PLACED | Amazon order placed | Done! |
| 📦 SHIPPED | SHIPPED | Amazon dispatched | Gmail polling will capture tracking |
| 🎉 DELIVERED | DELIVERED | Confirmed delivered | Close order |
| ❌ FAILED | FAILED | Checkout failed | Check ao_error field |
| 🚫 CANCELLED | CANCELLED | Order cancelled | Refund buyer on eBay |

---

## Settings → Auto Order Tab

**Access:** Settings → Auto Order

### 1. Enable AO (aoEnabled)

**Type:** Boolean
**Default:** false

**What it does:**

- **true**: AO system active, queue processing enabled
- **false**: AO system disabled (orders stay in QUEUED status)

**Use case:** Disable during holidays or when testing

---

### 2. Price Validation Threshold (aoPriceThreshold)

**Type:** Integer (%)
**Default:** 10

**What it does:** Max allowed price difference between scraper and checkout.

**Formula:**

\`\`\`
delta_% = ((checkout_price - scraper_price) / scraper_price) × 100

if delta_% > aoPriceThreshold:
  ABORT checkout, set status = FAILED
\`\`\`

**Example:**

| Scraper | Checkout | Delta | Threshold | Result |
|---------|----------|-------|-----------|--------|
| £20 | £21 | 5% | 10% | ✅ PASS |
| £20 | £24 | 20% | 10% | ❌ FAIL |

---

### 3. Max Retry Count (aoMaxRetries)

**Type:** Integer
**Default:** 3

**What it does:** How many times to retry failed checkout steps.

**Retry scenarios:**

- Address save failed
- Add to cart failed (bot detection)
- Payment method selection failed

**After N retries:** Set status = FAILED, ao_error = "Max retries exceeded"

---

### 4. Sequential Processing Delay (aoProcessingDelay)

**Type:** Integer (seconds)
**Default:** 60

**What it does:** Wait N seconds between orders to avoid Amazon bot detection.

**Flow:**

\`\`\`
Process Order 1 → Wait 60s → Process Order 2 → Wait 60s → Process Order 3
\`\`\`

**Why?** Amazon flags accounts that place 10+ orders in quick succession.

**Recommended:** 60-120 seconds

---

### 5. Subscribe & Save Detection (aoDetectSubscribe)

**Type:** Boolean
**Default:** true

**What it does:**

- **true**: Auto-detect and disable Subscribe & Save checkboxes
- **false**: Ignore subscriptions (DANGEROUS — may create recurring charges)

**Recommended:** Keep enabled

---

### 6. Voucher Auto-Claim (aoAutoVoucher)

**Type:** Boolean
**Default:** true

**What it does:**

- **true**: Automatically apply vouchers/gift certificates if available
- **false**: Skip voucher application

**Benefit:** Saves £0.50-£5 per order

---

## Troubleshooting

### Extension not processing queue

**Problem:** Orders stuck in QUEUED status for 30+ minutes

**Check:**

1. **Extension installed?** \`chrome://extensions\`
2. **Extension connected?** Click icon → "Connected to..."
3. **Chrome tab active?** Background tabs throttle JavaScript (keep Amazon tab open)
4. **Amazon logged in?** Extension requires active Amazon session
5. **API key valid?** Generate new key in Settings if expired

**Solution:** Keep Amazon tab in foreground while processing queue

---

### "Price spike" failures

**Problem:** 50% of orders fail with "Price changed by X%"

**Cause:**

- Scraper data stale (last scraped 6 hours ago)
- Amazon price volatile (flash sales, seller competition)

**Solution:**

1. **Increase threshold:** 10% → 20% (Settings → Auto Order)
2. **OR** manually trigger scraper before sending to AO
3. **OR** check Amazon price manually, override if acceptable

---

### "Failed to save address" errors

**Problem:** All orders fail at address step

**Cause:**

- Amazon address book full (max 50 addresses)
- Address format invalid (missing postcode, invalid phone)
- Amazon requires CAPTCHA (bot detection)

**Solution:**

1. **Clear Amazon address book** manually
2. **Check eBay buyer address format** (phone number valid?)
3. **Solve CAPTCHA** in Amazon tab, retry order

---

### Subscribe & Save not detected

**Problem:** Order placed with subscription, monthly charge appears

**Check:**

1. **aoDetectSubscribe enabled?** Settings → Auto Order
2. **Amazon changed UI?** Subscribe & Save checkbox selector may have changed
3. **Extension version outdated?** Download latest version

**Solution:**

1. **Cancel subscription** on Amazon
2. **Refund buyer** (unexpected cost)
3. **Update extension** to latest version
4. **Report to support** (selector may need updating)

---

### Orders processing too slowly

**Problem:** 100 orders in queue, processing 1 per minute = 100 minutes

**Cause:** \`aoProcessingDelay = 60s\`

**Solution:**

1. **Reduce delay:** 60s → 30s (Settings → Auto Order)
2. **Risk:** Amazon may flag account for bot activity
3. **Balance:** 30-45s is safe for most accounts

**Note:** Never set delay < 15s (almost guaranteed ban)

---

## Best Practices

### Queue Management

**Morning routine:**

1. **Check "To Ship" tab**
2. **Send all to Amazon** (bulk send)
3. **Open Amazon tab** (keep in foreground)
4. **Monitor queue** (check every 30 min)

**If queue stuck:**

- Check extension status
- Refresh Amazon tab
- Re-login to Amazon if needed

---

### Price Validation Strategy

**Conservative (low risk):**

- Threshold: 5-10%
- More FAILED orders, but zero losses

**Balanced (recommended):**

- Threshold: 10-15%
- Occasional price spike failures, manageable

**Aggressive (high volume):**

- Threshold: 20-30%
- Fewer failures, but risk of loss on some orders

---

### Subscription Detection

**Always keep enabled.** Even 1 accidental subscription can cost £100s over time.

**If subscription created:**

1. **Cancel immediately** on Amazon
2. **Check eBay order:** Did buyer pay for 1-time or subscription?
3. **Refund difference** if necessary

---

## Related Articles

- **Order Management** — View AO status badges in order list
- **Stock Synchronization** — Order trigger scraper sync
- **Gmail Integration** — Auto-capture Amazon tracking after AO completes

---

**Need help?** Contact support or use the in-app chat widget.
      `
    },
    {
      id: 'auto-messages-guide',
      title: 'Auto Messages — Automated Buyer Communication',
      excerpt: 'Send professional automated messages to eBay buyers at key order stages — New Order confirmation, Shipped notification with tracking, and Delivered follow-up. Customize templates with variables (buyer name, tracking, etc.) and configure in Settings → Messages tab.',
      category: 'auto-messages',
      readTime: '5 min',
      icon: '💬',
      content: `
# Auto Messages System

Automatically send professional messages to eBay buyers when orders are placed, shipped, or delivered — improving customer satisfaction and feedback ratings.

---

## Overview

**The Problem:**

Manually messaging each buyer is time-consuming:
- 50 orders/day = 50 messages to write
- Inconsistent messaging (typos, forgot to include tracking)
- Buyers left wondering about order status

**The Solution: Auto Messages**

Coderom automatically sends eBay messages at three key stages:

✅ **New Order** — Buyer receives confirmation within minutes of purchase
✅ **Shipped** — Tracking info sent automatically when you upload tracking
✅ **Delivered** — Follow-up message requesting positive feedback

**Benefits:**

- ⭐ **Higher feedback scores** — Professional communication = happy buyers
- ⏰ **Time savings** — Zero manual messaging required
- 📊 **Consistency** — Every buyer gets the same quality experience
- 🎯 **Customizable** — Personalize templates with buyer name, tracking, etc.

---

## Message Types

### 1. New Order Message

**Trigger:** eBay order webhook received (buyer places order)

**When sent:** Within 60 seconds of order placement

**Purpose:**

- Confirm order received
- Set expectations (dispatch time)
- Build buyer confidence

**Template variables:**

| Variable | Example Value |
|----------|---------------|
| \`{{buyer_first_name}}\` | John |
| \`{{name}}\` | Your Store Name |
| \`{{item_title}}\` | Wireless Headphones |

**Example message:**

\`\`\`
Subject: Thanks for buying from our store

Dear John,

Thank you for choosing our store! 🛍️

Your order is being prepared and will be on its way soon. 🚚
We hope it arrives even sooner than expected!

If you have any questions or concerns, don't hesitate to reach
out—we're here to help.

Wishing you a wonderful day! 😊

Warm regards,
My Store UK
\`\`\`

---

### 2. Shipped Message

**Trigger:** Tracking number uploaded via:

- Manual tracking upload (Orders → Add Tracking)
- Gmail automation (Amazon dispatch email parsed)
- CompleteSale API call

**When sent:** Immediately after \`CompleteSale\` API succeeds

**Purpose:**

- Confirm shipment
- Provide tracking details
- Reduce "Where is my item?" messages

**Template variables:**

| Variable | Example Value |
|----------|---------------|
| \`{{buyer_first_name}}\` | John |
| \`{{name}}\` | Your Store Name |
| \`{{tracking_number}}\` | XX000000000GB |
| \`{{tracking_carrier}}\` | Royal Mail |
| \`{{tracking_url}}\` | https://track.royalmail.com/... |
| \`{{item_title}}\` | Wireless Headphones |

**Example message:**

\`\`\`
Subject: Your item has shipped and the tracking number updates

Dear John,

🚀 We are excited to let you know that your order has been
dispatched and is now on its way!

📦 Tracking Details:
• Carrier: Royal Mail
• Tracking Number: XX000000000GB
• Track your parcel: https://track.royalmail.com/...

Under normal circumstances, you can expect it to arrive as soon
as possible. We're confident that you'll love your purchase!

If you have any questions, feel free to reach out. We're always
here to help! 🤗

Wishing you a wonderful day ahead. 🌼

Warm regards,
My Store UK
\`\`\`

---

### 3. Delivered Message

**Trigger:** Carrier confirms delivery (via Gmail polling or manual update)

**When sent:** When \`status = "Delivered"\` in database

**Purpose:**

- Confirm delivery
- Request positive feedback (⭐⭐⭐⭐⭐)
- Encourage repeat purchases

**Template variables:**

| Variable | Example Value |
|----------|---------------|
| \`{{buyer_first_name}}\` | John |
| \`{{name}}\` | Your Store Name |
| \`{{item_title}}\` | Wireless Headphones |

**Example message:**

\`\`\`
Subject: Order delivered

Dear John,

Thank you for your recent purchase! 📦✨

If you are happy with our fast & free expedited delivery and our
performance as a seller, please take a moment of your time to
leave positive feedback for us. ⭐⭐⭐⭐⭐

Your support means the world to us and helps other buyers
discover our store!

Thank you and hope to deal with you again soon! 🙏

Warm regards,
My Store UK
\`\`\`

---

## How It Works

### Backend Implementation

**File:** \`backend/services/ebay/messaging.py\`

**Flow:**

1. **Trigger event occurs** (order placed / tracking uploaded / delivered)
2. **Check settings:** \`autoMsgNewOrderEnabled = true\`?
3. **Load template:** \`autoMsgNewOrderTitle\` + \`autoMsgNewOrderBody\`
4. **Render variables:**
   \`\`\`python
   body = template.replace("{{buyer_first_name}}", buyer_name.split()[0])
   body = body.replace("{{tracking_number}}", tracking_number)
   body = body.replace("{{name}}", store_settings.companyName)
   \`\`\`
5. **eBay Trading API call:**
   \`\`\`xml
   <AddMemberMessageAAQToPartner>
     <ItemID>387721248895</ItemID>
     <MemberMessage>
       <Subject>Your item has shipped</Subject>
       <Body>Dear John, your order...</Body>
       <RecipientID>johndoe123</RecipientID>
       <QuestionType>CustomizedSubject</QuestionType>
     </MemberMessage>
   </AddMemberMessageAAQToPartner>
   \`\`\`
6. **eBay sends message** to buyer's eBay inbox
7. **Buyer receives email** (eBay forwards inbox messages to buyer's email)

---

### Rate Limiting

**eBay limit:** 75 messages per 60 seconds

**Coderom protection:**

- Max 60 messages per minute (conservative)
- If limit exceeded: Queue messages for next minute
- Retry failed messages 3× before giving up

---

## Settings → Messages Tab

**Access:** Settings → Messages

### 1. New Order Message

#### autoMsgNewOrderEnabled

**Type:** Boolean
**Default:** true

**What it does:**

- **true**: Send message when order is placed
- **false**: Skip new order messages

**Recommended:** Keep enabled (buyers appreciate order confirmation)

---

#### autoMsgNewOrderTitle

**Type:** String
**Default:** "Thanks for buying from our store"

**What it does:** eBay message subject line

**Max length:** 80 characters

**Tips:**

- Keep short and professional
- Avoid ALL CAPS (looks spammy)
- Avoid excessive emojis

---

#### autoMsgNewOrderBody

**Type:** Text (multiline)
**Default:** (See New Order example above)

**What it does:** Message body sent to buyer

**Max length:** 2000 characters

**Available variables:**

- \`{{buyer_first_name}}\` — Buyer's first name
- \`{{name}}\` — Your store/company name
- \`{{item_title}}\` — Product title

**Tips:**

- Start with buyer's name for personalization
- Set clear expectations (dispatch time)
- Include contact info for questions
- Use emojis sparingly (1-2 per message)

---

### 2. Shipped Message

#### autoMsgShippedEnabled

**Type:** Boolean
**Default:** true

**What it does:**

- **true**: Send message when tracking uploaded
- **false**: Skip shipped messages

**Recommended:** Keep enabled (tracking info is critical)

---

#### autoMsgShippedTitle

**Type:** String
**Default:** "Your item has shipped and the tracking number updates"

**What it does:** Subject line for shipping confirmation

---

#### autoMsgShippedBody

**Type:** Text (multiline)
**Default:** (See Shipped example above)

**Available variables:**

- \`{{buyer_first_name}}\` — Buyer's first name
- \`{{name}}\` — Your store name
- \`{{tracking_number}}\` — XX000000000GB
- \`{{tracking_carrier}}\` — Royal Mail
- \`{{tracking_url}}\` — https://track.royalmail.com/...
- \`{{item_title}}\` — Product title

**CRITICAL:** Always include all tracking variables (\`tracking_number\`, \`carrier\`, \`url\`)

---

### 3. Delivered Message

#### autoMsgDeliveredEnabled

**Type:** Boolean
**Default:** true

**What it does:**

- **true**: Send message when delivery confirmed
- **false**: Skip delivered messages

**Recommended:** Enable if you want to request feedback

**Note:** Some sellers disable this to avoid appearing pushy

---

#### autoMsgDeliveredTitle

**Type:** String
**Default:** "Order delivered"

---

#### autoMsgDeliveredBody

**Type:** Text (multiline)
**Default:** (See Delivered example above)

**Available variables:**

- \`{{buyer_first_name}}\` — Buyer's first name
- \`{{name}}\` — Your store name
- \`{{item_title}}\` — Product title

**Feedback Request Strategy:**

**Good:**

> "If you're happy with your purchase, please consider leaving positive feedback. ⭐"

**Bad (pushy):**

> "LEAVE 5 STARS NOW OR WE WILL BE SAD!!!"

**Best practice:** Polite ask, focus on experience, not just stars

---

## Template Customization

### Best Practices

**1. Personalization**

✅ **Good:**

> Dear {{buyer_first_name}},

❌ **Bad:**

> Dear Customer,

**Why?** Personalized messages feel human, not robotic.

---

**2. Professional Tone**

✅ **Good:**

> Your order is being prepared and will be dispatched soon.

❌ **Bad:**

> ur order gonna ship tmrw lol

**Why?** Professional language = trust

---

**3. Clear Information**

✅ **Good:**

> Tracking Number: {{tracking_number}}
> Carrier: {{tracking_carrier}}
> Track: {{tracking_url}}

❌ **Bad:**

> Check tracking somewhere idk

**Why?** Specific info reduces support tickets

---

**4. Emoji Usage**

✅ **Good:**

> Thank you! 🙏 (1-2 emojis)

❌ **Bad:**

> THANK YOU!!!!! 🎉🎊🎈🎁💝🌟✨ (7 emojis)

**Why?** Excessive emojis look unprofessional

---

### Testing Templates

**Dry-Run Mode:**

Not available in UI, but backend supports it:

\`\`\`python
send_buyer_message(
  ...,
  dry_run=True  # Logs message instead of sending
)
\`\`\`

**Manual test:**

1. Place a test order on your eBay account
2. Check eBay inbox for message
3. Verify all variables rendered correctly
4. Check tracking URL works
5. Adjust template if needed

---

## Troubleshooting

### Messages not sending

**Problem:** autoMsgShippedEnabled = true, but no message sent

**Check:**

1. **Backend logs:** \`grep "AUTO MSG" /var/log/coderom.log\`
2. **eBay API error?** Common errors:
   - Invalid item_id
   - Buyer blocked seller messages
   - eBay token expired
3. **Variables missing?** If \`tracking_number = null\`, message may fail

**Solution:** Check backend logs for specific error, fix and retry

---

### Variables not rendering

**Problem:** Message shows "{{buyer_first_name}}" instead of "John"

**Cause:**

- Typo in variable name (\`buyer_firstname\` vs. \`buyer_first_name\`)
- Variable not defined in backend code

**Solution:**

- Use EXACT variable names from docs
- Check backend \`render_template()\` function

---

### Buyer complains about too many messages

**Problem:** Buyer receives 3 messages in 2 hours (order → shipped → delivered)

**Solution:**

1. **Disable delivered message** (autoMsgDeliveredEnabled = false)
2. **OR** increase delay between messages (not configurable, contact support)
3. **OR** combine shipped + delivered into one message

---

### eBay rate limit exceeded

**Problem:** "Too many messages sent, try again later"

**Cause:** Sent > 75 messages in 60 seconds

**Solution:**

- Coderom auto-queues excess messages
- Wait 60 seconds, messages will send
- If persistent: Contact support (backend may have bug)

---

## Best Practices for Feedback

### 5-Star Messaging Strategy

**Delivered message should:**

1. ✅ **Confirm delivery** ("Hope you received your order safely")
2. ✅ **Ask politely** ("If satisfied, please leave feedback")
3. ✅ **Emphasize experience** ("fast delivery", "excellent service")
4. ❌ **NOT beg** ("PLEASE GIVE 5 STARS")
5. ❌ **NOT pressure** ("If you don't leave feedback, we will be sad")

---

### Timing Matters

**New Order:** Send immediately (builds trust)

**Shipped:** Send immediately after tracking upload (buyer wants to know ASAP)

**Delivered:** Wait 24 hours after delivery (give buyer time to open package)

**Note:** Delivered timing not configurable in current version

---

## Related Articles

- **Order Management** — Messages sent during order status changes
- **Gmail Integration** — Automatic tracking triggers shipped message
- **eBay Policies** — Configure store name used in messages

---

**Need help?** Contact support or use the in-app chat widget.
      `
    },
    {
      id: 'gmail-tracking-guide',
      title: 'Gmail Integration — Automatic Tracking Capture',
      excerpt: 'Connect Gmail via an App Password (IMAP) to automatically capture tracking numbers from Amazon dispatch emails and carrier notifications (Royal Mail, DPD, Evri). Coderom polls every 2 hours, parses emails, matches orders, and uploads tracking to eBay automatically.',
      category: 'gmail-tracking',
      readTime: '6 min',
      icon: '📧',
      content: `
# Gmail Integration & Tracking Automation

Connect your Gmail account to automatically capture Amazon dispatch notifications and carrier tracking emails — eliminating manual tracking upload.

---

## Overview

**The Problem:**

After ordering from Amazon (manually or via AO), you need tracking:

- Amazon sends dispatch email to your Gmail
- Royal Mail / DPD / Evri sends tracking email
- You manually copy tracking number
- Paste into Coderom → upload to eBay

**50 orders/day = 50× manual copy/paste = Repetitive and error-prone**

**The Solution: Gmail Integration**

Coderom connects to your Gmail via an IMAP App Password and:

✅ **Polls Gmail every 2 hours** — Checks for new Amazon/carrier emails
✅ **Parses dispatch emails** — Extracts Amazon Order ID from "Your order has shipped"
✅ **Parses carrier emails** — Extracts tracking number (Royal Mail: XX000000000GB, DPD: 15501234567890)
✅ **Matches orders** — Links email to eBay order via Amazon Order ID or recipient name
✅ **Uploads to eBay** — Calls CompleteSale API with tracking number
✅ **Sends auto message** — Buyer receives shipped notification

**Time savings:** 50 orders × 2 min each = 100 min/day → **0 min/day** (fully automated)

---

## How It Works

### Step 1: Email App Password Connection

**Location:** Settings → Email tab

**Process:**

1. Enable **2-Step Verification** on your Google account (required for App Passwords)
2. Go to **myaccount.google.com/apppasswords**, enter an app name (e.g. "Coderom"), click **Create**
3. Copy the **16-character App Password** Google shows you
4. In Coderom: **Settings → Email tab** — enter your Gmail address and paste the App Password
5. Click **Connect** ✅ (read-only — Coderom cannot send or delete emails)

**Backend saves:**

\`\`\`sql
INSERT INTO email_integrations (
  user_id, email, app_password_encrypted, imap_host, since_cutover
) VALUES (...)
\`\`\`

**App Password lifespan:** Permanent — it does **not** expire (unlike OAuth tokens). It only breaks if 2-Step Verification is disabled or the App Password is deleted.

---

### Step 2: Polling Gmail

**Frequency:** Every 2 hours (scheduler job)

**Backend:** \`services/gmail_polling.py:poll_all_gmail_accounts()\`

**Query:**

\`\`\`python
# Search for Amazon emails (last 2 days)
from:auto-confirm@amazon.co.uk
subject:"dispatched" OR subject:"shipped" OR subject:"out for delivery"
after:2026/04/10
\`\`\`

**AND**

\`\`\`python
# Search for carrier emails (last 2 days)
from:noreply@royalmail.com OR
from:notifications@dpd.co.uk OR
from:track@evri.com
after:2026/04/10
\`\`\`

**API call:**

\`\`\`
GET https://gmail.googleapis.com/gmail/v1/users/me/messages
  ?q=from:auto-confirm@amazon.co.uk subject:dispatched after:2026/04/10
  &maxResults=100
\`\`\`

**Response:**

\`\`\`json
{
  "messages": [
    {"id": "18f2a3b4c5d6e7f8", "threadId": "..."},
    {"id": "28a9b0c1d2e3f4g5", "threadId": "..."}
  ]
}
\`\`\`

---

### Step 3: Email Parsing

For each email found:

#### 3.1. Amazon Dispatch Email

**Fetch email content:**

\`\`\`
GET https://gmail.googleapis.com/gmail/v1/users/me/messages/18f2a3b4c5d6e7f8
\`\`\`

**Response:**

\`\`\`json
{
  "payload": {
    "headers": [
      {"name": "Subject", "value": "Your Amazon.co.uk order has been dispatched"},
      {"name": "From", "value": "auto-confirm@amazon.co.uk"}
    ],
    "body": {
      "data": "SGVsbG8gSm9obiwKWW91ciBvcmRlciAxMTMtMTIzNDU2Ny04OTAxMjM0IGhhcyBiZWVuIGRpc3BhdGNoZWQh..."
    }
  }
}
\`\`\`

**Decode base64 body:**

\`\`\`
Hello John,

Your order 113-1234567-8901234 has been dispatched!

Tracking: XX000000000GB
Carrier: Royal Mail
...
\`\`\`

**Parse:**

\`\`\`python
def parse_amazon_dispatch_email(content):
    # Extract Amazon Order ID
    match = re.search(r'\\b(\\d{3}-\\d{7}-\\d{7})\\b', content['body'])
    amazon_order_id = match.group(1) if match else None

    # Extract tracking (may not always be present)
    tracking_match = re.search(r'\\b([A-Z]{2}\\d{9}GB)\\b', content['body'])
    tracking_number = tracking_match.group(1) if tracking_match else None

    return {
        "amazon_order_id": amazon_order_id,
        "tracking_number": tracking_number,
        "carrier": "Royal Mail" if tracking_number else None
    }
\`\`\`

**Result:**

\`\`\`python
{
  "amazon_order_id": "113-1234567-8901234",
  "tracking_number": "XX000000000GB",
  "carrier": "Royal Mail"
}
\`\`\`

---

#### 3.2. Royal Mail Tracking Email

**Subject:** "Your parcel is on its way"

**Body:**

\`\`\`
Hi John,

Your parcel is on its way!

Tracking Number: XX000000000GB
Expected delivery: Tuesday, 15 April

Track: https://track.royalmail.com/...
\`\`\`

**Parse:**

\`\`\`python
def parse_carrier_email(content):
    # Royal Mail format: XX000000000GB
    tracking_match = re.search(r'\\b([A-Z]{2}\\d{9}GB)\\b', content['body'])

    # DPD format: 15501234567890 (14 digits)
    if not tracking_match:
        tracking_match = re.search(r'\\b(155\\d{11})\\b', content['body'])

    # Extract recipient name (for order matching)
    name_match = re.search(r'Hi ([A-Za-z]+)', content['body'])

    return {
        "tracking_number": tracking_match.group(1) if tracking_match else None,
        "carrier": "Royal Mail" if tracking_match else "DPD",
        "recipient_name": name_match.group(1) if name_match else None,
        "status": "dispatched"
    }
\`\`\`

**Result:**

\`\`\`python
{
  "tracking_number": "XX000000000GB",
  "carrier": "Royal Mail",
  "recipient_name": "John",
  "status": "dispatched"
}
\`\`\`

---

### Step 4: Order Matching

**Two matching strategies:**

#### Strategy 1: Amazon Order ID (Primary)

If email contains Amazon Order ID:

\`\`\`sql
SELECT * FROM ebay_orders
WHERE user_id = 'user_123'
  AND amazon_order_id = '113-1234567-8901234'
\`\`\`

**Match rate:** ~95% (most Amazon emails include order ID)

---

#### Strategy 2: Recipient Name (Fallback)

If no Amazon Order ID, match by recipient name:

\`\`\`sql
SELECT * FROM ebay_orders
WHERE user_id = 'user_123'
  AND shipping_address->>'name' ILIKE '%John%'
  AND status = 'To Ship'
  AND order_date >= NOW() - INTERVAL '7 days'
ORDER BY order_date DESC
LIMIT 1
\`\`\`

**Match rate:** ~70% (common names like "John" may match wrong order)

**Improvement:** Match last name too (\`ILIKE '%John Smith%'\`)

---

### Step 5: Update Database

**If match found:**

\`\`\`sql
-- Create ParcelflixTracking record
INSERT INTO parcelflix_trackings (
  user_id,
  ebay_order_id,
  tracking_number,
  carrier,
  status,
  gmail_message_id
) VALUES (
  'user_123',
  12,
  'XX000000000GB',
  'Royal Mail',
  'in_transit',
  '18f2a3b4c5d6e7f8'
)

-- Update EbayOrder
UPDATE ebay_orders SET
  parcelflix_tracking_id = <new_id>,
  status = 'Dispatched'
WHERE id = 12
\`\`\`

---

### Step 6: Upload to eBay

**CompleteSale API call:**

\`\`\`xml
<CompleteSale>
  <OrderID>12-12345-67890</OrderID>
  <Shipment>
    <ShipmentTrackingNumber>XX000000000GB</ShipmentTrackingNumber>
    <ShippingCarrier>RoyalMail</ShippingCarrier>
  </Shipment>
</CompleteSale>
\`\`\`

**eBay updates:**

- Order status → "Shipped"
- Tracking visible to buyer
- Buyer receives eBay email notification

---

### Step 7: Auto Message

**If \`autoMsgShippedEnabled = true\`:**

Coderom sends shipped message to buyer with tracking details (see Auto Messages guide).

---

## Supported Carriers

| Carrier | Tracking Format | Example |
|---------|-----------------|---------|
| **Royal Mail** | XX000000000GB (2 letters + 9 digits + GB) | XX123456789GB |
| **DPD UK** | 155XXXXXXXXXXX (14 digits, starts with 155) | 15501234567890 |
| **Evri (Hermes)** | EVXXXXXXXXXX (12 chars) | EV123456789A |
| **Amazon Logistics** | TBA123456789000 (15 chars) | TBA123456789000 |

**Auto-detection:**

Coderom uses regex patterns to detect carrier from tracking format.

**If unknown format:**

- Carrier = "Other"
- Still uploads to eBay (eBay accepts custom tracking)

---

## Settings → Email Tab

**Access:** Settings → Email

### 1. Email Connected

**Display:** Shows connected Gmail address or "Not connected"

**Actions:**

- **Connect** — Enter your Gmail address + App Password
- **Test Connection** — Verify the saved App Password still works
- **Disconnect** — Deactivate and clear the App Password from the database

---

### 2. Polling Status

**Display:**

- "Last checked: 2 hours ago"
- "Next check: in 15 minutes"

**Note:** Polling interval not configurable (always 2 hours)

---

### 3. Email Statistics

**Display:**

- Amazon emails processed: 487
- Carrier emails processed: 512
- Orders matched: 463
- No match: 36

**Why no match?**

- Amazon Order ID not in database (manual Amazon orders)
- Name mismatch (buyer name different from recipient)
- Email too old (> 7 days)

---

## Troubleshooting

### Connection failed

**Problem:** "Connection failed: invalid email or App Password" error

**Causes:**

1. **Wrong App Password** — Mistyped, or revoked in the Google account
2. **2-Step Verification off** — App Passwords require 2FA enabled
3. **Network error** — Timeout reaching imap.gmail.com

**Solution:**

1. **Generate a fresh App Password** — myaccount.google.com/apppasswords
2. **Re-enter** your email + the new 16-character App Password, then click "Connect"
3. **Use "Test Connection"** to confirm, or check backend logs for specifics

---

### Tracking not captured

**Problem:** Amazon dispatch email received 6 hours ago, still no tracking in Coderom

**Check:**

1. **Email connected?** Settings → Email → "Connected"
2. **Email subject correct?** Must contain "dispatched" or "shipped"
3. **Email from Amazon?** \`from:auto-confirm@amazon.co.uk\`
4. **Amazon Order ID in email?** Check email body for \`113-1234567-8901234\` format
5. **Order in database?** If you ordered manually (not via AO), Coderom may not have order record

**Solution:**

- If order exists: Wait for next polling cycle (up to 2 hours)
- If order missing: Add tracking manually

---

### Wrong order matched

**Problem:** Tracking from Order A assigned to Order B

**Cause:** Recipient name matching (fallback strategy) matched wrong order

**Example:**

- Order A: Shipping to "John Smith"
- Order B: Shipping to "John Doe"
- Email says "Hi John" → Matched first "John" in database (wrong one!)

**Solution:**

1. **Manual correction** — Orders → Edit tracking → Reassign to correct order
2. **Prevention:** Always include Amazon Order ID in AO orders (ensures primary matching)

---

### App Password revoked

**Problem:** "Authentication failed" after it was previously working

**Cause:** The App Password was revoked — usually because 2-Step Verification was turned off, or the App Password was deleted in the Google account. After 3 consecutive auth failures Coderom auto-deactivates the integration.

**Solution:**

1. **Disconnect** — Settings → Email → "Disconnect"
2. **Create a new App Password** — myaccount.google.com/apppasswords
3. **Reconnect** — Enter email + the new App Password

**Note:** App Passwords do not expire on a timer (unlike OAuth tokens) — they only break on 2FA disable/delete.

---

## Best Practices

### Which Gmail to Use?

**Option 1: Dedicated Gmail for business**

✅ **Best practice:** Create \`yourbusiness.orders@gmail.com\`

**Pros:**

- Clean inbox (only Amazon/carrier emails)
- Easy to audit
- No personal emails mixed in

**Cons:**

- Need to forward Amazon emails from personal Gmail

---

**Option 2: Personal Gmail (where Amazon sends emails)**

✅ **Easier setup** — No forwarding needed

**Pros:**

- Amazon already sends to this email
- Immediate tracking capture

**Cons:**

- Coderom can see all email subjects (NOT content, only metadata)
- Privacy concern for some users

---

### Email Forwarding

If using dedicated Gmail:

1. **Go to personal Gmail** → Settings → Forwarding
2. **Add forwarding address:** \`yourbusiness.orders@gmail.com\`
3. **Create filter:**
   - From: \`auto-confirm@amazon.co.uk\`
   - Subject contains: "dispatched"
   - Action: Forward to \`yourbusiness.orders@gmail.com\`

---

### Monitoring

**Weekly check:**

1. **Settings → Email** → Check statistics
2. **"No match" count high?** (> 10%)
   - Investigate: Are Amazon Order IDs missing?
   - Fix: Ensure AO orders include Amazon Order ID

**Monthly audit:**

1. **Orders → "Dispatched" tab**
2. **Check all have tracking** (should be 100%)
3. **If missing:** Gmail may have missed email, upload manually

---

## Related Articles

- **Order Management** — View tracking in order detail page
- **Auto Messages** — Shipped message sent after tracking upload
- **Automatic Ordering** — AO orders include Amazon Order ID for matching

---

**Need help?** Contact support or use the in-app chat widget.
      `
    },
    {
      id: 'ebay-multistore-guide',
      title: 'eBay Policies & Multi-Store Management',
      excerpt: 'Configure eBay Business Policies (payment, shipping, return), set item location and handling time, manage multiple eBay accounts (up to 10 stores), switch between marketplaces, and maintain separate settings per store. All in Settings → eBay Policies tab.',
      category: 'ebay-multistore',
      readTime: '6 min',
      icon: '🏪',
      content: `
# eBay Policies & Multi-Store Management

Configure eBay Business Policies, item location, handling time, and manage multiple eBay accounts across different marketplaces — all from Coderom Settings.

---

## Overview

**eBay Business Policies** control how your listings handle:

- **Payment** — How buyers pay (eBay Managed Payments)
- **Shipping** — Shipping services, costs, and delivery times
- **Returns** — Return window, refund method, and who pays return shipping

**Multi-Store Support** allows you to:

- Connect up to 10 eBay accounts (plan dependent)
- Manage different marketplaces (UK, US, DE, FR, IT, ES, AU, CA)
- Maintain separate settings per store
- Switch between stores with one click

This guide covers **Settings → eBay Policies** tab configuration.

---

## eBay Business Policies

### What Are Business Policies?

**Created on eBay Seller Hub:**

1. **Go to:** [https://www.ebay.co.uk/sh/ovw](https://www.ebay.co.uk/sh/ovw)
2. **Navigate to:** Account → Business Policies
3. **Create policies** for payment, shipping, returns
4. **Copy Policy IDs** (e.g., \`6074698000\`)

**Why use policies?**

- ✅ **Consistency** — Same shipping terms across all listings
- ✅ **Easy updates** — Change policy once, all listings update
- ✅ **eBay requirement** — Managed Payments requires payment policy

---

### Policy Types

#### 1. Payment Policy

**Controls:**

- Payment methods accepted (eBay Managed Payments, PayPal)
- Payment terms (immediate payment required?)

**Example:**

- Name: "UK Standard Payment"
- Policy ID: \`6074698000\`
- Payment method: eBay Managed Payments
- Immediate payment: Required

**In Coderom:**

\`\`\`
Settings → eBay Policies → Payment Profile
Select: "UK Standard Payment (6074698000)"
\`\`\`

**Applied to listings:**

\`\`\`xml
<AddFixedPriceItem>
  <Item>
    <PaymentPolicyID>6074698000</PaymentPolicyID>
    ...
  </Item>
</AddFixedPriceItem>
\`\`\`

---

#### 2. Shipping Policy

**Controls:**

- Shipping services (Royal Mail 1st Class, Tracked 24, etc.)
- Shipping costs (free, flat rate, calculated)
- Handling time (1 day, 2 days, 3 days)
- Domestic vs. international shipping

**Example:**

- Name: "UK Free Tracked Shipping"
- Policy ID: \`6074699000\`
- Service: Royal Mail Tracked 24
- Cost: Free
- Handling time: 2 business days

**In Coderom:**

\`\`\`
Settings → eBay Policies → Shipping Profile
Select: "UK Free Tracked Shipping (6074699000)"
\`\`\`

---

#### 3. Return Policy

**Controls:**

- Returns accepted? (Yes / No)
- Return window (30 days, 60 days)
- Who pays return shipping? (Buyer / Seller)
- Refund method (Money back, Replacement)

**Example:**

- Name: "30-Day Returns"
- Policy ID: \`6074700000\`
- Returns: Accepted
- Window: 30 days
- Return shipping: Buyer pays
- Refund: Money back

**In Coderom:**

\`\`\`
Settings → eBay Policies → Return Profile
Select: "30-Day Returns (6074700000)"
\`\`\`

---

## Settings → eBay Policies Tab

**Access:** Settings → eBay Policies

### 1. Payment Policy ID (paymentPolicyId)

**Type:** Dropdown (loaded from eBay API)
**Default:** "" (empty)

**What it does:** Sets payment policy for all new listings

**How to set:**

1. **Click "Sync from eBay"** button (loads policies from your eBay account)
2. **Select policy** from dropdown
3. **Click "Save Settings"**

**Note:** Changing this does NOT update existing listings. Only affects new listings published after save.

---

### 2. Shipping Policy ID (shippingPolicyId)

**Type:** Dropdown
**Default:** ""

**What it does:** Sets shipping policy for all new listings

**Critical:** Shipping policy includes handling time AND shipping service. Coderom's \`handlingTimeDays\` setting is IGNORED if shipping policy has different handling time.

**Priority:**

1. eBay shipping policy handling time (if policy set)
2. Coderom handlingTimeDays setting (if no policy)

---

### 3. Return Policy ID (returnPolicyId)

**Type:** Dropdown
**Default:** ""

**What it does:** Sets return policy for all new listings

**Recommended:** Always set a return policy. eBay search ranking favors listings with returns accepted.

---

### 4. Item Location

#### Item Country (itemCountry)

**Type:** Dropdown
**Options:** GB, US, DE, FR, IT, ES, AU, CA, etc.
**Default:** GB

**What it does:** Item location country shown to buyers

**Match your marketplace:**

| eBay Marketplace | itemCountry |
|------------------|-------------|
| eBay UK | GB |
| eBay US | US |
| eBay Germany | DE |
| eBay France | FR |
| eBay Italy | IT |
| eBay Spain | ES |
| eBay Australia | AU |
| eBay Canada | CA |

---

#### Item Location (itemLocation)

**Type:** String
**Default:** "London"

**What it does:** City/town name shown to buyers

**Examples:**

- UK: "London", "Manchester", "Birmingham"
- US: "New York", "Los Angeles", "Chicago"
- DE: "Berlin", "Munich", "Hamburg"

**Tip:** Use major city near you for better buyer trust

---

#### Item Postcode (itemPostcode)

**Type:** String
**Default:** "SW1A 1AA"

**What it does:** Postcode used for:

1. Shipping cost calculation (if using calculated shipping)
2. eBay search ranking (local pickup priority)
3. Buyer trust (shows your general location)

**Example postcodes:**

- UK: "SW1A 1AA", "M1 1AA", "B1 1AA"
- US: "10001", "90001", "60601"
- DE: "10115", "80331", "20095"

**Privacy:** eBay shows only PARTIAL postcode to buyers (e.g., "SW1A ***")

---

### 5. Handling Time (handlingTimeDays)

**Type:** Integer (days)
**Default:** 2

**What it does:** How many business days before you dispatch the order

**Options:**

| Days | Meaning |
|------|---------|
| 0 | Same-day dispatch |
| 1 | Next business day |
| 2 | Within 2 business days (recommended) |
| 3 | Within 3 business days |
| 5+ | Slow dispatch (NOT recommended) |

**Impact on eBay search ranking:**

- ✅ **0-1 days** — Boost in search results ("Fast dispatch" badge)
- ⚠️ **2-3 days** — Neutral
- ❌ **5+ days** — Penalty in search ranking

**Note:** If shipping policy has handling time, that takes priority over this setting.

---

## Multi-Store Management

### Connecting Multiple eBay Accounts

**Plan limits:**

| Plan | Max eBay Accounts |
|------|-------------------|
| Free Trial | 1 |
| Starter | 1 |
| Professional | 3 |
| Business | 10 |

**How to add store:**

1. **Dashboard → Top-right dropdown** → "Add eBay Account"
2. **Select marketplace** (UK, US, DE, etc.)
3. **eBay OAuth flow** (login, grant permissions)
4. **Store added** ✅

**Each store has:**

- Separate eBay API token
- Separate settings (all Settings tabs)
- Separate product catalog
- Separate order list

---

### Switching Between Stores

**Location:** Dashboard → Top-right dropdown

**Display:**

\`\`\`
🇬🇧 eBay UK (your-store-uk) ✓ ACTIVE
🇺🇸 eBay US (your-store-us)
🇩🇪 eBay DE (your-store-de)
\`\`\`

**Click dropdown → Select store → Page reloads with that store's data**

**What changes:**

- Products page → Shows products from selected store
- Orders page → Shows orders from selected store
- Settings → Loads settings for selected store

**What stays same:**

- User account (billing, email, etc.)
- Gmail connection (shared across stores)

---

### Per-Store Settings

**Each store has independent:**

- eBay Business Policies (payment, shipping, return)
- Pricing settings (margin%, flat fee, rounding)
- Stock settings (buffer, auto-end OOS)
- Amazon Filters (marketplace, Prime requirements)
- Auto Messages (templates can differ per store)

**Example use case:**

- **eBay UK store:** Margin 20%, stockBuffer 1, marketplace UK
- **eBay US store:** Margin 25%, stockBuffer 2, marketplace US

**How to set:**

1. **Switch to eBay UK** (dropdown)
2. **Settings → Pricing** → Set margin 20%
3. **Save Settings**
4. **Switch to eBay US** (dropdown)
5. **Settings → Pricing** → Set margin 25%
6. **Save Settings**

---

## Multi-Marketplace Strategies

### Strategy 1: UK + US (Most Popular)

**Why?**

- Largest eBay marketplaces
- Both English-language (no translation needed)
- Different buyer pools (2× potential customers)

**Setup:**

- **eBay UK:** Amazon UK as supplier, Royal Mail shipping
- **eBay US:** Amazon US as supplier, USPS shipping

**Challenge:** Price conversion (GBP vs USD)

**Solution:** Coderom auto-converts based on current exchange rate

---

### Strategy 2: UK + EU (Post-Brexit)

**Why?**

- Avoid UK customs for EU buyers
- Larger combined EU market than UK alone

**Setup:**

- **eBay DE:** Amazon DE supplier, DHL shipping
- **eBay FR:** Amazon FR supplier, Colissimo shipping

**Challenge:** VAT compliance (different rules per country)

**Solution:** Use eBay's VAT collection service (available in policy settings)

---

### Strategy 3: Single Marketplace (Beginners)

**Why?**

- Simplicity (one marketplace = less complexity)
- Focus on mastering one market first

**Recommended:** Start with eBay UK or eBay US

**Expand later:** Once profitable, add 2nd marketplace

---

## Troubleshooting

### Policy not loading in Coderom

**Problem:** Click "Sync from eBay" but dropdown shows "No policies found"

**Causes:**

1. **No policies created on eBay** — Must create in Seller Hub first
2. **eBay token expired** — Reconnect eBay account
3. **API error** — Check backend logs

**Solution:**

1. **Go to eBay Seller Hub** → Create payment/shipping/return policies
2. **Return to Coderom** → Click "Sync from eBay"
3. **Policies appear in dropdown**

---

### Wrong policy applied to listing

**Problem:** Published listing has old payment policy, not new one

**Cause:** Existing draft had old policy cached

**Solution:**

1. **Delete draft**
2. **Re-import ASIN**
3. **Publish** (will use current policy setting)

---

### Handling time not updating

**Problem:** Changed handlingTimeDays to 1, but listings still show 2 days

**Causes:**

1. **Shipping policy overrides** — Check if shipping policy has handling time set
2. **eBay cache** — Takes up to 24 hours for eBay to update listings

**Solution:**

1. **Check shipping policy** on eBay Seller Hub
2. **If policy has handling time:** Edit policy to match Coderom setting
3. **If no policy:** Wait 24 hours for eBay to refresh

---

### Store switch not working

**Problem:** Switched to eBay US, but still seeing eBay UK products

**Cause:** Browser cache or frontend state issue

**Solution:**

1. **Hard refresh:** Ctrl+Shift+R (Windows) or Cmd+Shift+R (Mac)
2. **Clear cache:** Browser settings → Clear cache
3. **Logout/login:** Dashboard → Logout → Login again

---

## Best Practices

### Policy Naming

**Good:**

- "UK Free Shipping - 2 Day Handling"
- "US Standard Payment - Managed"
- "30-Day Returns - Buyer Pays"

**Bad:**

- "Policy 1"
- "asdfjkl"
- "test"

**Why?** Clear names make it easy to select correct policy in Coderom dropdown

---

### Handling Time Strategy

**For most dropshippers:**

- **Set 2 days** (realistic for Amazon → eBay workflow)
- **Over-deliver:** Actually dispatch in 1 day when possible

**For AO users:**

- **Set 1 day** (AO orders Amazon same day, Amazon dispatches next day)
- **Risk:** If AO fails, you may miss 1-day target

**For manual ordering:**

- **Set 3 days** (gives buffer for manual process)

---

### Multi-Store Organization

**Store naming:**

- eBay UK: "YourBrand UK"
- eBay US: "YourBrand USA"
- eBay DE: "YourBrand Deutschland"

**Separate branding:**

Each store can have different:

- Logo (\`companyLogo\` in Settings)
- Store name (\`companyName\`)
- Return address (in shipping policy)

---

## Related Articles

- **Order Management** — Orders segregated per eBay store
- **Stock Synchronization** — Stock synced per-store independently
- **Smart Repricing** — Pricing settings customizable per store

---

**Need help?** Contact support or use the in-app chat widget.
      `
    },
    {
      id: 'reports-guide',
      title: 'Daily Reports & Notifications — Stay Informed 24/7',
      excerpt: 'Receive daily email reports with sales summaries, profit analysis, stock alerts, and OOS listings. Configure report sections, timezone-aware delivery (09:00 local), and real-time SSE notifications for order updates. Plus Telegram alerts for system health.',
      category: 'reports',
      readTime: '5 min',
      icon: '📊',
      content: `
# Daily Reports & Notifications

Stay informed about your business with automated daily email reports and real-time notifications for critical events.

---

## Overview

Coderom provides three notification systems:

✅ **Daily Email Reports** — Comprehensive business summary delivered every morning
✅ **SSE Real-Time Notifications** — Instant order/stock updates in browser
✅ **Telegram Admin Alerts** — System health checks and critical errors

This guide covers **Settings → Reports** configuration and notification management.

---

## Daily Email Report

### What's Included

Every morning at **09:00 local time**, Coderom sends a comprehensive email report:

**Subject:** "Coderom Daily Report — [Date]"

**Sections:**

1. **Listing Overview**
   - Total active listings
   - New listings (published yesterday)
   - Delisted (ended/OOS)
   - Needs update (price/stock changes pending)

2. **Sales Summary**
   - Orders received (yesterday)
   - Total revenue (£)
   - Average order value
   - Top-selling products (top 5)

3. **Scraper Status** (optional, default OFF)
   - Last scraper run time
   - Products scraped
   - Scraper errors (if any)
   - Next scheduled run

4. **Alerts**
   - Out-of-stock products (new OOS since yesterday)
   - Safe Repricing triggers (price spikes detected)
   - Failed AO orders
   - eBay API errors

5. **Price Changes** (optional, default OFF)
   - Products with price increases
   - Products with price decreases
   - Largest price swings

6. **Stock Changes** (optional, default OFF)
   - Products restocked
   - Products low stock (< 3 units)
   - Stock replenishment queue

---

### Example Report

\`\`\`
From: reports@coderom.com
To: your-email@example.com
Subject: Coderom Daily Report — April 12, 2026

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📊 CODEROM DAILY REPORT
April 12, 2026 — Your Store Name
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

📦 LISTING OVERVIEW
─────────────────────────────────────────
• Active Listings: 1,247 ✅
• New Yesterday: 23 🆕
• Delisted: 5 🚫
• Needs Update: 87 ⚠️

💰 SALES SUMMARY (Last 24h)
─────────────────────────────────────────
• Orders: 45 orders
• Revenue: £2,134.50
• Avg Order Value: £47.43
• Top Products:
  1. Wireless Headphones (12 sold)
  2. Phone Charger (8 sold)
  3. USB Cable (7 sold)

🚨 ALERTS
─────────────────────────────────────────
• New OOS: 12 products
  - ASIN B08N5WRWNW: Out of stock on Amazon
  - ASIN B07XYZ123A: Auto-ended (30 days OOS)

• Safe Repricing Triggered: 3 products
  - ASIN B08ABC123D: Price spike 133% (£15→£35)

• AO Failures: 2 orders
  - Order #12345: Price validation failed
  - Order #12346: Subscribe & Save detected

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

View full dashboard: https://app.coderom.com

Unsubscribe: https://app.coderom.com/settings/reports
\`\`\`

---

## Report Settings

**Access:** Settings → Reports (or General tab)

### 1. Daily Report Enabled (dailyReportEnabled)

**Type:** Boolean
**Default:** true

**What it does:**

- **true**: Send daily report every morning
- **false**: Disable all daily reports

**Use case:** Disable during holidays or if inbox gets cluttered

---

### 2. Report Sections (dailyReportSections)

**Type:** Object (checkboxes for each section)

**Available sections:**

| Section | Field | Default | Description |
|---------|-------|---------|-------------|
| **Listing Overview** | listing_overview | ✅ true | Active/new/delisted counts |
| **Sales Summary** | sales_summary | ✅ true | Revenue, orders, top products |
| **Scraper Status** | scraper_status | ❌ false | Last scrape time, errors |
| **Alerts** | alerts | ✅ true | OOS, Safe Repricing, AO failures |
| **Price Changes** | price_changes | ❌ false | Products with price updates |
| **Stock Changes** | stock_changes | ❌ false | Restocked/low stock products |

**Recommended:**

- **Enable:** listing_overview, sales_summary, alerts (core business metrics)
- **Disable:** scraper_status, price_changes, stock_changes (too detailed, can check manually)

**Why some are OFF by default?**

- **Scraper Status:** Technical info, only useful if debugging issues
- **Price/Stock Changes:** Can generate 100+ line items if you have 1000+ products

---

### 3. Report Delivery Time

**Setting:** Not configurable (fixed at 09:00 local time)

**Timezone detection:**

- Uses \`timezone\` field from Settings → General
- Options: UTC+0, UTC+1, UTC-5, etc.
- Default: UTC+0 (London)

**Examples:**

| Your Timezone | Delivery Time |
|---------------|---------------|
| UTC+0 (UK) | 09:00 GMT |
| UTC+1 (Germany) | 09:00 CET |
| UTC-5 (US East) | 09:00 EST |
| UTC+10 (Australia) | 09:00 AEST |

**How it works:**

Backend scheduler runs every hour:

\`\`\`python
@scheduler.task('cron', hour='*', minute=0)
def send_daily_reports():
    for user in users:
        user_hour = get_local_time(user.timezone).hour
        if user_hour == 9:
            send_report(user)
\`\`\`

---

## Email Template Structure

**Backend:** \`services/email_service.py\` or \`routers/reports.py\`

**Template engine:** Jinja2 or plain Python f-strings

**HTML template:**

\`\`\`html
<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; }
    .section { margin-bottom: 20px; }
    .metric { font-size: 24px; font-weight: bold; }
    .alert { background: #fee; padding: 10px; }
  </style>
</head>
<body>
  <h1>📊 Coderom Daily Report</h1>
  <p>{{ date }} — {{ store_name }}</p>

  {% if sections.listing_overview %}
  <div class="section">
    <h2>📦 Listing Overview</h2>
    <p>Active: <span class="metric">{{ active_count }}</span></p>
    <p>New: {{ new_count }}</p>
  </div>
  {% endif %}

  {% if sections.sales_summary %}
  <div class="section">
    <h2>💰 Sales Summary</h2>
    <p>Orders: {{ order_count }}</p>
    <p>Revenue: £{{ revenue }}</p>
  </div>
  {% endif %}

  {% if sections.alerts %}
  <div class="section">
    <h2>🚨 Alerts</h2>
    {% for alert in alerts %}
    <div class="alert">{{ alert.message }}</div>
    {% endfor %}
  </div>
  {% endif %}
</body>
</html>
\`\`\`

---

### Unsubscribe Mechanism

**List-Unsubscribe Header:**

\`\`\`
List-Unsubscribe: <https://app.coderom.com/settings/reports/unsubscribe?user=123&token=xyz>
List-Unsubscribe-Post: List-Unsubscribe=One-Click
\`\`\`

**How it works:**

1. **User clicks "Unsubscribe"** in email client (Gmail, Outlook)
2. **GET request** to \`/settings/reports/unsubscribe?user=123&token=xyz\`
3. **Backend updates:**
   \`\`\`sql
   UPDATE users SET daily_report_enabled = false WHERE id = 123
   \`\`\`
4. **Confirmation page:** "You have been unsubscribed from daily reports."

**Re-subscribe:**

- Settings → Reports → dailyReportEnabled = true

---

## SSE Real-Time Notifications

**What is SSE?** Server-Sent Events — server pushes updates to browser without polling.

**Use cases:**

- Order status changes (New → To Ship → Dispatched)
- AO status updates (QUEUED → COMPLETED)
- Stock updates (product back in stock)
- Tracking updates (carrier confirms delivery)

**How it works:**

\`\`\`javascript
// Frontend (Orders page)
const eventSource = new EventSource('/api/sse/subscribe?user_id=123');

eventSource.addEventListener('order_update', (event) => {
  const data = JSON.parse(event.data);
  // data = { orderId: 456, field: "status", value: "Dispatched" }

  // Update order in cache
  updateOrder(data.orderId, { [data.field]: data.value });

  // Show toast notification
  toast.success(\`Order #\${data.orderId} status: \${data.value}\`);
});
\`\`\`

**Backend (Redis Pub/Sub):**

\`\`\`python
# When order status changes
from services.pubsub import publish_order_update_sync

publish_order_update_sync(
    user_id="123",
    data={
        "type": "order_update",
        "orderId": 456,
        "field": "status",
        "value": "Dispatched"
    }
)
\`\`\`

**Redis Pub/Sub:**

\`\`\`
PUBLISH user:123:events '{"type":"order_update","orderId":456,...}'
\`\`\`

**SSE server:**

\`\`\`python
@app.get("/api/sse/subscribe")
async def subscribe(user_id: str):
    async def event_generator():
        # Subscribe to Redis channel
        pubsub = redis.pubsub()
        pubsub.subscribe(f"user:{user_id}:events")

        for message in pubsub.listen():
            if message['type'] == 'message':
                yield f"data: {message['data']}\\n\\n"

    return StreamingResponse(event_generator(), media_type="text/event-stream")
\`\`\`

**User experience:**

- Order placed → Status badge updates from 🕐 QUEUED → ⏳ PROCESSING instantly
- No page refresh needed
- Feels like real-time app (Discord/Slack)

---

## Telegram Admin Alerts

**For system administrators only** (not end users)

**Use cases:**

- Server health checks (CPU > 90%, disk full)
- Database errors (connection lost, query timeout)
- eBay API rate limits exceeded
- Scraper failures (Amazon blocking IP)

**Setup:**

1. **Create Telegram bot** (@BotFather)
2. **Get bot token** (e.g., \`123456:ABC-DEF...\`)
3. **Add to .env:** \`TELEGRAM_BOT_TOKEN=...\`
4. **Get chat ID** (send /start to bot, check logs)
5. **Add to .env:** \`TELEGRAM_ADMIN_CHAT_ID=...\`

**Backend:**

\`\`\`python
import requests

def send_telegram_alert(message: str, level: str = "ERROR"):
    url = f"https://api.telegram.org/bot{TELEGRAM_BOT_TOKEN}/sendMessage"
    emoji = "🚨" if level == "ERROR" else "⚠️" if level == "WARNING" else "ℹ️"

    requests.post(url, json={
        "chat_id": TELEGRAM_ADMIN_CHAT_ID,
        "text": f"{emoji} {level}\\n\\n{message}",
        "parse_mode": "Markdown"
    })

# Usage
send_telegram_alert(
    "eBay API rate limit exceeded: 5000/5000 calls used",
    level="ERROR"
)
\`\`\`

**Example alerts:**

\`\`\`
🚨 ERROR

eBay API rate limit exceeded
User: user_123
Store: eBay UK
Calls: 5000/5000
Action: Paused scraper for 1 hour

─────────────────────
Time: 2026-04-12 15:30 UTC
Server: coderom-prod-01
\`\`\`

---

## Troubleshooting

### Daily report not arriving

**Problem:** dailyReportEnabled = true, but no email received

**Check:**

1. **Spam folder** — Check junk/spam (Gmail often filters automated reports)
2. **Email address correct?** — Settings → General → Email
3. **Timezone setting** — If timezone = UTC+5, delivery is 09:00 EST (may be overnight for you)
4. **Backend logs:** \`grep "daily_report" /var/log/coderom.log\`

**Solution:**

- **Add to Safe Senders:** \`reports@coderom.com\`
- **Check timezone:** Settings → General → Timezone
- **Manual test:** Contact support to trigger test report

---

### Report landing in spam

**Problem:** Reports always go to Gmail spam folder

**Causes:**

1. **No SPF/DKIM records** — Email authentication missing
2. **High spam score** — Content triggers spam filters
3. **Low sender reputation** — \`reports@coderom.com\` domain new

**Solution:**

1. **Mark as "Not Spam"** in Gmail
2. **Add filter:** From \`reports@coderom.com\` → Never send to spam
3. **Contact support:** If persistent, Coderom may need to improve email authentication

---

### Wrong timezone

**Problem:** Report arrives at 02:00 instead of 09:00

**Cause:** Timezone setting incorrect

**Solution:**

1. **Settings → General → Timezone**
2. **Select correct timezone** (e.g., UTC+0 for UK, UTC-5 for US East)
3. **Save Settings**
4. **Wait for next day** — Report will arrive at 09:00 local time

**Note:** Timezone change affects future reports, not today's report

---

### SSE not working

**Problem:** Order status updates require page refresh

**Check:**

1. **Browser console:** EventSource errors?
2. **Network tab:** SSE connection established? (status: pending/streaming)
3. **Ad blocker:** Some block SSE connections
4. **Firewall:** Corporate networks may block SSE
5. **Browser compatibility:** Chrome/Firefox recommended (Safari has issues)

**Solution:**

- **Disable ad blocker** for Coderom domain
- **Use Chrome/Firefox**
- **Fallback:** Orders page auto-polls every 60s (less real-time, but works)

---

### Too many email reports

**Problem:** Receiving 3 reports per day (one per eBay store)

**Cause:** Multi-store setup sends separate report for each store

**Solution:**

**Option 1:** Disable reports for some stores

- Switch to Store 1 → Settings → dailyReportEnabled = false
- Switch to Store 2 → Settings → dailyReportEnabled = true

**Option 2:** Request consolidated report (contact support)

- Future feature: Single report with all stores combined

---

## Best Practices

### Report Customization

**For small sellers (< 100 products):**

- Enable: ALL sections (listing_overview, sales_summary, scraper_status, alerts, price_changes, stock_changes)
- Benefit: Full visibility, email remains short

**For large sellers (1000+ products):**

- Enable: listing_overview, sales_summary, alerts
- Disable: scraper_status, price_changes, stock_changes
- Benefit: Focused on key metrics, avoid email overload

---

### Timezone Accuracy

**Always set correct timezone:**

- UK sellers: UTC+0 (GMT) or UTC+1 (BST during summer)
- US East: UTC-5 (EST) or UTC-4 (EDT)
- US West: UTC-8 (PST) or UTC-7 (PDT)
- Germany: UTC+1 (CET) or UTC+2 (CEST)

**Tip:** Google "What is my timezone offset" to confirm

---

### Email Filters

**Create Gmail filter:**

1. **From:** \`reports@coderom.com\`
2. **Apply label:** "Coderom Reports"
3. **Never send to spam:** ✅
4. **Mark as important:** ✅

**Benefit:** Reports always visible, easy to search

---

### SSE vs Polling

**SSE (recommended):**

- ✅ Real-time updates (< 1s latency)
- ✅ Lower server load (no polling)
- ❌ Requires browser tab open

**Polling fallback:**

- ✅ Works with ad blockers
- ✅ Works in background tabs
- ❌ Slower (60s refresh interval)
- ❌ Higher server load

**Coderom uses hybrid:** SSE primary, polling fallback

---

## Related Articles

- **Order Management** — SSE updates order status in real-time
- **Stock Synchronization** — Stock changes appear in daily report
- **Safe Repricing** — Price spike alerts in daily report

---

**Need help?** Contact support or use the in-app chat widget.
      `
    },
    {
      id: 'migration-guide',
      title: 'Switch to Coderom — Migration from AutoDS, Easync, SaleFreaks',
      excerpt: 'Complete migration guide from AutoDS, Easync, SaleFreaks, DSM Tool, Yaballe, and HustleGotReal. Export your ASIN list, import to Coderom, configure settings, and switch platforms in 8 steps. 14-day free trial, no credit card required.',
      category: 'migration',
      readTime: '8 min',
      icon: '🔄',
      content: `
# Switch to Coderom — Migration Guide

Complete step-by-step guide to migrate from other dropshipping platforms to Coderom — preserve your eBay listings, import ASINs, and configure settings.

---

## Why Switch to Coderom?

### Platform Comparison

| Feature | **Coderom** | AutoDS | Easync | SaleFreaks |
|---------|-------------|--------|--------|------------|
| **AI-Powered Listing** | ✅ GPT-4o | Basic AI | ❌ | ❌ |
| **Safe Repricing Protection** | ✅ | ❌ | ❌ | ❌ |
| **120+ Settings** | ✅ | ~40 | ~30 | ~25 |
| **Chrome Extension AO** | ✅ | ✅ FBA | ✅ | ✅ |
| **Gmail Tracking Integration** | ✅ | ❌ | ❌ | ❌ |
| **Daily Email Reports** | ✅ | ❌ | ❌ | ❌ |
| **Multi-Store (10 eBay accounts)** | ✅ | ✅ | ✅ (5) | ✅ (3) |
| **14-Day Free Trial** | ✅ No CC | ❌ CC required | ❌ CC required | ❌ |
| **Price (100 products)** | **£15/mo** | $24.99/mo | £19.99/mo | $29.99/mo |
| **Price (500 products)** | **£35/mo** | $99.99/mo | £49.99/mo | $79.99/mo |
| **VeRO Protection** | ✅ Built-in | ✅ | ✅ | ✅ |
| **Real-Time Stock Sync** | ✅ 2-6h | 4-12h | 6h | 6-12h |
| **API Access** | ✅ Coming | ❌ | ❌ | ❌ |
| **Customer Support** | 24/7 Chat | Email | Email | Email |

**Key advantages:**

1. **✅ Smarter pricing:** Safe Repricing prevents profit loss from Amazon price spikes
2. **✅ Better AI:** GPT-4o generates higher-quality listings than AutoDS basic AI
3. **✅ More settings:** 120+ configuration options vs. ~40 on competitors
4. **✅ Cheaper:** £15/mo for 100 products vs. $24.99 (AutoDS) or £19.99 (Easync)
5. **✅ Gmail automation:** Automatic tracking capture from Amazon dispatch emails
6. **✅ Better trial:** 14 days, no credit card required (AutoDS requires CC)

---

## Migration Overview

**The good news:** Your eBay listings stay on eBay! You don't need to relist everything.

**What happens:**

1. **eBay listings remain active** — Buyers can still purchase
2. **Export ASIN list** from old platform
3. **Import ASINs** to Coderom
4. **Coderom syncs** with your existing eBay listings (matches by ASIN/SKU)
5. **Repricing/stock sync** takes over from old platform

**Timeline:** 2-4 hours for most sellers

---

## Migration from AutoDS

### Step 1: Export Products from AutoDS

**Method 1: CSV Export (Recommended)**

1. **Go to:** AutoDS → Products → All Products
2. **Select all products** (checkbox at top)
3. **Click "Export"** → Download CSV
4. **CSV contains:** ASIN, eBay Item ID, Title, Price, Stock

**Method 2: Manual ASIN List**

If CSV export unavailable:

1. **Copy ASINs** from AutoDS product list
2. **Paste into text file** (one ASIN per line)
3. **Save as:** \`autods-asins.txt\`

---

### Step 2: Create Coderom Account

1. **Visit:** [https://app.coderom.com/register](https://app.coderom.com/register)
2. **Sign up** with email or Google OAuth
3. **14-day free trial** — No credit card required ✅
4. **Connect eBay account** (OAuth flow)

---

### Step 3: Import ASINs to Coderom

**Bulk Import:**

1. **Dashboard → Drafts** → Click "Add Product" → "Bulk Import"
2. **Paste ASIN list** (one per line) OR **upload CSV**
3. **Select marketplace:** UK, US, DE, etc. (match AutoDS setting)
4. **Click "Import"**
5. **Wait 2-5 minutes** for scraper to fetch Amazon data

**What Coderom does:**

- Scrapes Amazon for each ASIN (price, stock, images, title)
- Checks eBay for existing listings (matches by ASIN in SKU field)
- If found: Links Coderom product to existing eBay listing
- If not found: Creates draft for new listing

---

### Step 4: AI Optimize Listings (Optional)

**If you want better titles:**

1. **Select all drafts** (checkbox)
2. **Click "Bulk Optimize with AI"**
3. **GPT-4o generates:**
   - SEO-optimized 80-char eBay titles
   - Professional descriptions
   - Accurate category matching
4. **Review and publish**

**Note:** AI optimization is optional. If you keep AutoDS titles, skip this step.

---

### Step 5: Configure Pricing Settings

**AutoDS → Coderom conversion:**

| AutoDS Setting | Coderom Equivalent |
|----------------|-------------------|
| Profit Margin % | Settings → Pricing → marginPercent |
| Fixed Profit $ | Settings → Pricing → flatFee |
| Min Price | Settings → Pricing → minPrice |
| Max Price | Settings → Pricing → maxPrice |
| Round to .99 | Settings → Pricing → rounding = ".99" |

**Example:**

- **AutoDS:** 20% margin, $0.50 fixed profit, round to .99
- **Coderom:** marginPercent = 20, flatFee = 0.50, rounding = ".99"

**Steps:**

1. **Settings → Pricing**
2. **Set marginPercent:** (e.g., 20)
3. **Set flatFee:** (e.g., 0.50)
4. **Set rounding:** .99
5. **Save Settings**

---

### Step 6: Configure Stock Settings

**AutoDS → Coderom:**

| AutoDS Setting | Coderom Equivalent |
|----------------|-------------------|
| Stock Buffer | Settings → Stock → stockBuffer |
| Auto-End OOS | Settings → Stock → autoEndOosEnabled |
| OOS Days | Settings → Stock → autoEndOosDays |

**Recommended:**

- stockBuffer = 1 (safety margin)
- autoEndOosEnabled = true
- autoEndOosDays = 30 (end listings OOS for 30+ days)

---

### Step 7: Setup AO (Automatic Ordering)

**AutoDS "Fulfilled by AutoDS" vs. Coderom Chrome Extension:**

| AutoDS FBA | Coderom AO |
|------------|------------|
| Managed warehouse | Your own Amazon account |
| AutoDS orders on your behalf | You order via Chrome Extension |
| Higher fees (AutoDS commission) | Lower fees (direct Amazon purchase) |
| No control over supplier | Full control (choose FBA/FBM) |

**Coderom AO setup:**

1. **Download Chrome Extension** — Settings → Auto Order → "Download Extension"
2. **Install in Chrome** (Developer mode, load unpacked)
3. **Connect to Coderom** (enter API key)
4. **Send orders to queue** — Orders → "Send to Amazon"
5. **Extension processes automatically**

**See [AO Guide](#ao-guide) for full details.**

---

### Step 8: Cancel AutoDS Subscription

**After confirming Coderom works:**

1. **AutoDS → Settings → Billing**
2. **Click "Cancel Subscription"**
3. **Confirm cancellation**
4. **Export final report** (for records)

**Important:** Don't cancel until you've verified Coderom repricing/stock sync is working correctly (give it 24-48 hours).

---

## Migration from Easync

### Key Differences

**Easync repricing formula:**

\`\`\`
eBay Price = (Amazon Price × Multiplier) + Fixed Fee
\`\`\`

**Coderom repricing formula:**

\`\`\`
eBay Price = (Amazon Price × (1 + Margin%)) + Flat Fee + eBay Fees (reversed)
\`\`\`

**Conversion:**

- **Easync Multiplier 1.25** → Coderom marginPercent = 25%
- **Easync Fixed Fee £2** → Coderom flatFee = 2.00

---

### Migration Steps (Same as AutoDS)

1. **Export ASIN list** from Easync (CSV or manual)
2. **Create Coderom account** (14-day trial)
3. **Import ASINs** (Bulk Import)
4. **Convert pricing formula:**
   - Easync multiplier 1.25 → marginPercent = 25
   - Easync fixed £2 → flatFee = 2.00
5. **Configure stock settings** (buffer, auto-end OOS)
6. **Setup Chrome Extension AO**
7. **Cancel Easync** after 24-48h verification

---

## Migration from SaleFreaks / DSM Tool / Yaballe

### Same Principles Apply

All platforms follow similar structure:

1. **ASIN-based dropshipping** (Amazon → eBay)
2. **eBay listings already exist** (no need to relist)
3. **Export ASIN list** (CSV or manual)
4. **Import to Coderom**
5. **Configure settings** (pricing, stock, filters)

**Platform-specific notes:**

#### SaleFreaks

- **Export:** SaleFreaks → Products → Export CSV
- **Pricing:** SaleFreaks uses "Markup %" → Coderom marginPercent

#### DSM Tool

- **Export:** DSM → Export Product List
- **Pricing:** DSM "Profit Margin" → Coderom marginPercent + flatFee

#### Yaballe

- **Export:** Yaballe → Products → Download CSV
- **Pricing:** Yaballe "Price Formula" → Coderom Settings → Pricing

#### HustleGotReal

- **Export:** Manual ASIN list (no CSV export)
- **Pricing:** HGR "Margin" → Coderom marginPercent

---

## 8-Step Migration Checklist

### ✅ Step 1: Create Coderom Account

- [ ] Sign up at [app.coderom.com/register](https://app.coderom.com/register)
- [ ] 14-day free trial starts (no credit card)
- [ ] Verify email (check spam folder)

---

### ✅ Step 2: Connect eBay Account

- [ ] Dashboard → Settings → eBay Accounts
- [ ] Click "Connect eBay Account"
- [ ] Select marketplace (UK, US, DE, etc.)
- [ ] Complete OAuth (login to eBay, grant permissions)
- [ ] Verify connection (green "Connected" badge)

---

### ✅ Step 3: Export ASIN List from Old Platform

**AutoDS:**

- [ ] Products → Export CSV

**Easync:**

- [ ] Products → Download CSV

**SaleFreaks:**

- [ ] Products → Export

**Manual (any platform):**

- [ ] Copy all ASINs to text file (one per line)

---

### ✅ Step 4: Import ASINs to Coderom

- [ ] Drafts → Add Product → Bulk Import
- [ ] Paste ASIN list OR upload CSV
- [ ] Select Amazon marketplace (UK, US, etc.)
- [ ] Click "Import"
- [ ] Wait for scraper to complete (2-5 min)
- [ ] Verify products appear in Drafts table

---

### ✅ Step 5: (Optional) AI Optimize Listings

- [ ] Select all drafts (checkbox)
- [ ] Click "Bulk Optimize with AI"
- [ ] Wait for GPT-4o processing (1-2 min per 10 products)
- [ ] Review optimized titles/descriptions
- [ ] Publish to eBay OR keep old titles

---

### ✅ Step 6: Configure Pricing Settings

- [ ] Settings → Pricing
- [ ] Set marginPercent (e.g., 20%)
- [ ] Set flatFee (e.g., £0.50)
- [ ] Set rounding (.99, .90, etc.)
- [ ] Set eBay fees (13% final value fee)
- [ ] Set minProfitGBP (e.g., £1.00)
- [ ] Click "Save Settings"

---

### ✅ Step 7: Configure Stock & Filter Settings

**Stock:**

- [ ] Settings → Stock
- [ ] Set stockBuffer (default: 1)
- [ ] Enable autoEndOosEnabled
- [ ] Set autoEndOosDays (default: 30)
- [ ] Click "Save Settings"

**Amazon Filters:**

- [ ] Settings → Amazon Filters
- [ ] Set marketplace (UK, US, etc.)
- [ ] Enable requirePrimeFBA (recommended)
- [ ] Set maxSupplierPrice (e.g., £500)
- [ ] Click "Save Settings"

---

### ✅ Step 8: Setup AO & Cancel Old Platform

**AO:**

- [ ] Settings → Auto Order → Download Chrome Extension
- [ ] Install extension (chrome://extensions → Load unpacked)
- [ ] Connect extension (enter API key)
- [ ] Test with 1-2 orders

**Cancel old platform:**

- [ ] Wait 24-48 hours (verify Coderom repricing works)
- [ ] Old platform → Settings → Billing → Cancel Subscription
- [ ] Export final report (for records)
- [ ] Delete payment method (optional)

---

## Coderom Advantages Summary

### 1. Safe Repricing Protection

**Problem with other platforms:**

Amazon price spikes from £20 → £50. Your platform auto-reprices eBay to £65. Buyer purchases. You lose £15 per sale.

**Coderom solution:**

Safe Repricing detects 150% price change, sets listing to OOS automatically. Monitors for 3 days. If price stabilizes, restores listing.

**Result:** Zero losses from price spikes.

---

### 2. GPT-4o AI Listing

**AutoDS AI:**

- Basic title optimization
- Generic descriptions
- Limited category matching

**Coderom GPT-4o:**

- Advanced SEO optimization (analyzes 20,000+ eBay categories)
- Professional descriptions with product features
- 98% accurate category matching
- VeRO brand detection (prevents listing violations)

**Example:**

**AutoDS title:**

> Wireless Bluetooth Headphones Over Ear Noise Cancelling Stereo Bass

**Coderom GPT-4o title:**

> Wireless Bluetooth 5.0 Headphones Over-Ear Noise Cancelling Foldable Stereo HD

**Difference:** Coderom includes Bluetooth version (5.0) and "Foldable" (important search term).

---

### 3. 120+ Settings (vs. 40 on AutoDS)

**AutoDS settings:**

- Basic pricing (margin, min/max)
- Basic stock (buffer, OOS handling)
- Basic filters (Prime only)

**Coderom settings:**

- **Pricing:** 15+ settings (margin, flat fee, 4 rounding options, min profit guarantee, auto-revise thresholds)
- **Stock:** 10+ settings (buffer, auto-end OOS, replenishment, velocity limits)
- **Filters:** 12+ settings (marketplace, Prime/FBA, FBM, delivery days, volatility filter)
- **Safety:** Safe Repricing (4 settings)
- **Messages:** Auto buyer messages (6 templates)
- **Reports:** Daily email reports (6 sections)

**Result:** Granular control over every aspect of your business.

---

### 4. Gmail Tracking Integration

**AutoDS:** Manual tracking upload only

**Coderom:**

- Connect Gmail via App Password
- Auto-parse Amazon dispatch emails
- Auto-parse carrier emails (Royal Mail, DPD, Evri)
- Auto-upload tracking to eBay
- Auto-send shipped message to buyer

**Time saved:** 50 orders × 2 min each = 100 min/day → **0 min/day**

---

### 5. Cheaper Pricing

| Products | **Coderom** | AutoDS | Easync | Savings |
|----------|-------------|--------|--------|---------|
| **100** | **£15/mo** | $24.99/mo | £19.99/mo | 40-50% |
| **250** | **£25/mo** | $49.99/mo | £29.99/mo | 30-50% |
| **500** | **£35/mo** | $99.99/mo | £49.99/mo | 50-65% |
| **1000** | **£55/mo** | $199.99/mo | £79.99/mo | 60-70% |

**14-day free trial, no credit card** — Try before you pay.

---

## Troubleshooting

### Products not matching existing eBay listings

**Problem:** Imported 500 ASINs, but Coderom created 500 drafts instead of linking to existing listings

**Cause:** eBay listing SKU doesn't contain ASIN

**Solution:**

1. **Check eBay listing SKU** (eBay → My eBay → Selling → Click listing → SKU field)
2. **If SKU = empty or custom:** Coderom can't auto-match
3. **Manual link:** Drafts → Click product → "Link to eBay listing" → Enter eBay Item ID
4. **Bulk link:** Export eBay Item IDs, import to Coderom with ASIN mapping (contact support)

---

### Pricing doesn't match old platform

**Problem:** AutoDS price £25.00, Coderom price £27.50

**Cause:** Different fee calculations

**Solution:**

1. **Check eBay fees:** AutoDS may use 10% final value fee, Coderom uses 13% (current UK rate)
2. **Adjust marginPercent:** If AutoDS 20% → Try Coderom 18% to match
3. **Use minProfitGBP:** Set to £2.00 to guarantee minimum profit

---

### AO not working

**Problem:** Chrome Extension not processing queue

**Cause:** Extension not connected or Amazon not logged in

**Solution:**

1. **Check extension status:** Click icon → Should show "Connected to [email]"
2. **Check Amazon login:** Open Amazon tab → Must be logged in
3. **Keep tab active:** Background tabs throttle JavaScript
4. **Check logs:** Extension → Right-click icon → Inspect → Console

---

## Related Articles

- **Getting Started** — Complete Coderom setup guide
- **Automatic Ordering** — Chrome Extension AO detailed guide
- **Smart Repricing** — Pricing formula and settings
- **Safe Repricing** — Protection from price spikes

---

**Need help with migration?** Contact support or use the in-app chat widget. Our team can assist with bulk imports and custom data migration.
      `
    },
    {
      id: 'getting-started',
      title: 'Complete Setup Guide — From Registration to First Sale',
      excerpt: 'Everything you need to set up your Coderom account, connect eBay, and create your first listing.',
      category: 'getting-started',
      readTime: '8 min',
      icon: '🚀',
      content: `
## Welcome to Coderom

Coderom is an AI-powered Amazon to eBay dropshipping automation platform. We handle product listing, smart repricing, automatic ordering, tracking, and buyer messaging — everything you need to run and scale your dropshipping business.

This guide will walk you through the complete setup process, from creating your account to making your first sale.

---

## Step 1: Create Your Account

Coderom offers two registration methods:

### Google OAuth (Recommended)
- Click "Sign up with Google"
- Select your Google account
- Grant Coderom permission to access your basic profile
- Instant account creation — no password needed

### Email Registration
- Enter your email address, password, first name, and last name
- Verify your email address (check spam folder if needed)
- Complete the registration form

### 14-Day Free Trial

All new accounts start with a **14-day free trial** — no credit card required.

**Trial Limits:**
- ✅ Up to 100 managed products
- ✅ Up to 50 orders per month
- ✅ 1 eBay store connection
- ✅ Access to all core features (AI listing, repricing, stock sync)
- ✅ 3 HTML listing templates

After your trial ends, you'll be prompted to select a paid plan. Your products and settings will be preserved when you upgrade.

<div style="background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 12px; padding: 40px; text-align: center; margin: 20px 0;">
  <p style="color: #64748b; font-size: 14px; margin: 0;">📸 Screenshot: Registration page</p>
</div>

---

## Step 2: Connect Your eBay Account

After registration, you'll be redirected to the eBay connection page.

### eBay OAuth Flow

1. Click **"Connect eBay Account"**
2. Select your eBay marketplace (UK, US, DE, FR, IT, ES, AU, CA)
3. You'll be redirected to eBay's secure login page
4. Sign in to your eBay seller account
5. Review the permissions Coderom is requesting
6. Click **"Agree"** to authorize Coderom

### Required Permissions

Coderom requests the following eBay API permissions:

- ✅ **Inventory & Listings**: Create, edit, and manage your product listings
- ✅ **Order Management**: View and manage orders placed by buyers
- ✅ **Account Data**: Access your eBay seller account information
- ✅ **Fulfillment Policies**: Read and apply your shipping/payment policies

**Important**: Coderom uses OAuth tokens — we never store your eBay password.

### Multi-Marketplace Support

Coderom supports **8 eBay marketplaces**:
- 🇬🇧 **eBay UK** (ebay.co.uk)
- 🇺🇸 **eBay US** (ebay.com)
- 🇩🇪 **eBay Germany** (ebay.de)
- 🇫🇷 **eBay France** (ebay.fr)
- 🇮🇹 **eBay Italy** (ebay.it)
- 🇪🇸 **eBay Spain** (ebay.es)
- 🇦🇺 **eBay Australia** (ebay.com.au)
- 🇨🇦 **eBay Canada** (ebay.ca)

**Which marketplace should I choose?**
- Choose the marketplace where you already have an eBay seller account
- Most dropshippers start with eBay UK or eBay US
- You can connect multiple eBay accounts later (plan dependent)

<div style="background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 12px; padding: 40px; text-align: center; margin: 20px 0;">
  <p style="color: #64748b; font-size: 14px; margin: 0;">📸 Screenshot: eBay connection page</p>
</div>

---

## Step 3: Configure Basic Settings

After connecting eBay, go to **Settings → General** to configure your account.

### General Settings Tab

**Store Name**
- This is your internal label for the eBay account
- Example: "My UK Store" or "eBay US Main"
- Useful when managing multiple stores

**Currency**
- Select your preferred currency for pricing calculations
- Supported: GBP, USD, EUR, AUD, CAD
- This currency will be used throughout the dashboard
- eBay listings will use the marketplace's native currency

**Timezone**
- Set your local timezone (e.g., "UTC+0" for London, "UTC-5" for New York)
- **Important for Daily Email Reports** — your reports will arrive at 9 AM your local time
- Affects scheduling and reporting timestamps

**Sync Interval**
- How often Coderom checks Amazon for price and stock changes
- **Plan-based**: Starter (2h), Advanced (1h), Professional (30min), Business (15min), Enterprise+ (5min)
- Higher tier plans get more frequent sync intervals

<div style="background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 12px; padding: 40px; text-align: center; margin: 20px 0;">
  <p style="color: #64748b; font-size: 14px; margin: 0;">📸 Screenshot: General Settings tab</p>
</div>

---

## Step 4: Add Your First Product

Now you're ready to create your first eBay listing!

### Finding Products on Amazon

1. Go to **Products → Add Product**
2. Find a product on Amazon UK, US, DE, FR, IT, ES, AU, or CA
3. Copy the **ASIN** (10-character product ID)
   - Example: B07ZPKBL9V
   - Found in the product URL or Product Information section

### Creating the Listing with AI

1. Paste the ASIN into Coderom
2. Click **"Fetch Product"**
3. Coderom's **GPT-4o AI** will automatically:
   - ✅ Detect the best eBay category (98% accuracy)
   - ✅ Generate an SEO-optimized title (eBay's 80-character limit)
   - ✅ Create a professional product description
   - ✅ Extract key features and item specifics
   - ✅ Download and optimize product images

### Choosing a Listing Template

4. Select one of **9 professional HTML templates**:
   - Modern, Minimal, Classic, Premium, Bold, Elegant, Tech, Fashion, Vintage
   - All templates are mobile-responsive and conversion-optimized

### Review and Publish

5. Review your listing in the draft editor:
   - Check title, description, category
   - Adjust pricing (margin rules apply)
   - Set quantity (synced from Amazon stock)
6. Click **"Publish to eBay"**
7. Your listing goes live on eBay in under 10 seconds!

<div style="background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 12px; padding: 40px; text-align: center; margin: 20px 0;">
  <p style="color: #64748b; font-size: 14px; margin: 0;">📸 Screenshot: Draft page — ASIN entry</p>
</div>

---

## Step 5: Understand Your Dashboard

After publishing your first product, head to the **Dashboard** to monitor your business.

### Key Dashboard Metrics

**Sales Overview**
- **Revenue**: Total sales in the selected period
- **Profit**: Calculated after Amazon costs, eBay fees, and shipping
- **Orders**: Number of eBay orders received
- **Active Listings**: Currently live on eBay

**Sales Chart**
- Daily revenue and order volume
- Filter by 7 days, 30 days, or custom date range
- Visual trend analysis

**Stock Status**
- **In Stock**: Products available on Amazon
- **Low Stock**: Below safety buffer threshold
- **Out of Stock**: Not available on Amazon (listing paused)

**Quick Actions**
- ➕ Add Product (ASIN entry)
- 📊 View Orders
- ⚙️ Settings
- 📧 Daily Reports

**Real-Time Updates**
- Dashboard uses **Server-Sent Events (SSE)** technology
- No page refresh needed — metrics update automatically
- See order notifications in real-time

<div style="background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 12px; padding: 40px; text-align: center; margin: 20px 0;">
  <p style="color: #64748b; font-size: 14px; margin: 0;">📸 Screenshot: Dashboard</p>
</div>

---

## What's Next?

You've successfully set up your Coderom account and created your first listing! Here's what to explore next:

### Essential Guides

- **[Smart Repricing & Pricing Rules](#pricing)** — Configure margin-based pricing
- **[Safe Repricing Protection](#safe-repricing)** — Protect against Amazon price spikes
- **[Stock Synchronization](#stock)** — Set up 24/7 stock monitoring
- **[Safety & Protection](#safety-protection)** — Protect your store from VeRO claims with blocked brands and keywords
- **[Auto Buyer Messages](#auto-messages)** — Send automatic messages to customers
- **[Gmail Integration](#gmail-tracking)** — Capture tracking numbers automatically
- **[Professional Listing Templates](#templates)** — Customize your listing design

### Support

Need help? We're here 24/7:
- 💬 **Live Chat**: Click the chat widget in the bottom-right corner
- 📧 **Email**: support@coderom.com
- 📚 **Help Center**: Browse all guides and tutorials

**Welcome to Coderom — let's grow your dropshipping business!** 🚀
      `
    },
    {
      id: 'account-billing',
      title: 'Account, Subscription & Billing Guide',
      excerpt: 'Manage your plan, payments, invoices, and account settings.',
      category: 'account-billing',
      readTime: '6 min',
      icon: '💳',
      content: `
## Plans & Pricing

Coderom offers **10 subscription plans** designed to scale with your business — from individual sellers to large agencies.

### Plan Comparison Table

| Plan | SKUs | Monthly Price | Yearly Price | Sync Interval | eBay Accounts | Templates |
|------|------|---------------|--------------|---------------|---------------|-----------|
| **Starter** | 200 | $14.90 | $143 | 2 hours | 1 | 3 |
| **Advanced** | 500 | $29.90 | $287 | 1 hour | 1 | 5 |
| **Professional** | 1,000 | $39.90 | $383 | 30 minutes | 2 | 7 |
| **Business** | 2,000 | $69.90 | $671 | 15 minutes | 3 | All |
| **Enterprise** | 5,000 | $99.90 | $959 | 10 minutes | 3 | All |
| **Enterprise Plus** | 10,000 | $149.90 | $1,439 | 5 minutes | 5 | All |
| **Agency** | 25,000 | $249.90 | $2,399 | 5 minutes | 5 | All |
| **Ultimate** | 50,000 | $399.90 | $3,839 | Real-time | 10 | All |
| **Ultimate Plus** | 75,000 | $549.90 | $5,279 | Real-time | 10 | All |
| **Titan** | 100,000 | $699.90 | $6,719 | Real-time | 10 | All |

**Annual Billing Discount**: Save **~20%** by paying yearly instead of monthly.

### Which Plan Is Right for You?

**Starter** — Perfect for beginners testing dropshipping
- Just starting out with fewer than 200 products
- 2-hour stock sync is sufficient for low-competition niches
- 1 eBay account

**Professional** (Most Popular) — Best for growing sellers
- Managing 500-1,000 products
- 30-minute sync keeps you competitive
- 2 eBay accounts for multi-marketplace selling
- AI title & description generation

**Business** — For established dropshippers
- 2,000 products across multiple categories
- 15-minute sync for high-competition items
- 3 eBay accounts
- Bulk import/export tools

**Enterprise & Above** — For power sellers and agencies
- 5,000+ products
- 5-10 minute sync (or real-time for Ultimate+)
- Dedicated account manager
- Priority support

<div style="background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 12px; padding: 40px; text-align: center; margin: 20px 0;">
  <p style="color: #64748b; font-size: 14px; margin: 0;">📸 Screenshot: Subscription page</p>
</div>

---

## Trial Period

### Free Trial Details

All new accounts start with a **14-day free trial** — no credit card required.

**Trial Limits:**
- ✅ Up to 100 managed products
- ✅ Up to 50 orders per month
- ✅ 1 eBay store connection
- ✅ Access to all core features
- ✅ 3 HTML listing templates

**What Happens After Trial?**

When your trial ends:
1. You'll receive an email reminder 3 days before expiration
2. You'll be redirected to the **Subscription** page to select a plan
3. Your products and settings are preserved — no data loss
4. If you don't upgrade, your account will be paused (read-only access)

**Can I Extend My Trial?**
Trial extensions are not available, but you can contact support@coderom.com for special circumstances.

---

## Upgrading & Downgrading

### How to Change Your Plan

1. Go to **Account → Subscription**
2. Click **"Change Plan"** or **"Upgrade"**
3. Select your new plan
4. Confirm payment method
5. Changes take effect immediately

### Pro-Rata Billing

When upgrading mid-cycle:
- You'll be charged the difference between your old and new plan
- The remaining days in your billing cycle are credited
- Example: Upgrading from Starter ($14.90) to Professional ($39.90) after 10 days = ~$17 pro-rata charge

When downgrading:
- Your new plan takes effect at the **next billing cycle**
- You keep your current plan until renewal date
- No immediate refund for unused days

### SKU Limits

If you exceed your plan's SKU limit:
- You'll receive a warning notification
- New product creation will be blocked
- Upgrade to a higher plan to continue adding products

---

## Payment Methods

Coderom supports two payment methods:

### 1. Credit/Debit Card (Stripe)
- Visa, Mastercard, American Express
- Secure processing via Stripe
- Automatic recurring billing
- **Recommended** for most users

### 2. PayPal
- Link your PayPal account
- Automatic recurring payments
- Available for monthly and yearly plans

### Monthly vs Yearly Billing

**Monthly Billing**
- ✅ Pay month-to-month
- ✅ Cancel anytime, no commitment
- ❌ No discount

**Yearly Billing** (Recommended)
- ✅ Save ~20% compared to monthly
- ✅ Pay once, valid for 12 months
- ✅ One invoice, easier accounting
- ❌ Upfront payment required

**Example Savings:**
- Professional Monthly: $39.90 × 12 = $478.80/year
- Professional Yearly: $383/year
- **You save $95.80** (20% discount)

<div style="background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 12px; padding: 40px; text-align: center; margin: 20px 0;">
  <p style="color: #64748b; font-size: 14px; margin: 0;">📸 Screenshot: Plan cards</p>
</div>

---

## Invoices & VAT

### Automatic Invoice Generation

Every payment generates an invoice automatically:
- Sent to your registered email address
- Downloadable from **Account → Billing History**
- PDF format with full payment details

### Invoice Details Include:
- Invoice number and date
- Plan name and billing period
- Subtotal, VAT (if applicable), and total
- Payment method and transaction ID
- Company details (if provided in account settings)

### VAT/Tax Information

**EU Customers:**
- VAT is automatically added based on your country
- Provide your VAT number in **Account Settings** for reverse charge
- VAT-registered businesses can claim input VAT

**Non-EU Customers:**
- No VAT charged
- Local sales tax may apply (consult your tax advisor)

### Viewing Invoice History

1. Go to **Account → Billing History**
2. View all past invoices
3. Download individual invoices as PDF
4. Filter by date range or payment status

<div style="background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 12px; padding: 40px; text-align: center; margin: 20px 0;">
  <p style="color: #64748b; font-size: 14px; margin: 0;">📸 Screenshot: Invoice example</p>
</div>

---

## Discount Codes

### How to Apply a Promo Code

1. Go to **Account → Subscription**
2. Select a plan (or click "Change Plan")
3. On the checkout page, click **"Have a discount code?"**
4. Enter your promo code (e.g., CODEROM20)
5. Click **"Apply"**
6. The discount will be shown in the order summary

**Common Discount Types:**
- **Percentage Off**: 20% off first month/year
- **Fixed Amount**: $10 off
- **Free Trial Extension**: Extra 7 days trial
- **Partner Codes**: Special offers from affiliate partners

**Where to Find Promo Codes:**
- Email campaigns (newsletter subscribers)
- Social media announcements (Twitter, Facebook)
- Affiliate partners (YouTube creators, bloggers)
- Seasonal sales (Black Friday, New Year)

---

## Cancellation & Renewal

### How to Cancel Your Subscription

1. Go to **Account → Subscription**
2. Click **"Cancel Subscription"**
3. Confirm cancellation
4. Your plan remains active until the end of your billing cycle
5. After expiration, your account becomes read-only

**What Happens After Cancellation:**
- ✅ Your data is preserved for 90 days
- ✅ You can export your product data
- ✅ Read-only access to dashboard and orders
- ❌ No new products or eBay listings
- ❌ Stock sync and repricing stopped

### Reactivating After Cancellation

If you cancel and want to return:
1. Go to **Account → Subscription**
2. Click **"Renew Plan"** or **"Reactivate"**
3. Select a plan and payment method
4. Your account will be restored immediately
5. All your products and settings will be restored

**Data Retention:**
- Cancelled accounts: **90 days**
- After 90 days, data is permanently deleted

### Account Deletion

To permanently delete your Coderom account:
1. Go to **Account → Settings**
2. Scroll to **Danger Zone**
3. Click **"Delete Account"**
4. Confirm deletion (this cannot be undone)
5. All data will be permanently erased within 7 days

**Before deleting:**
- ✅ Export your product data (CSV)
- ✅ Download all invoices
- ✅ Save any important information
- ❌ This action is irreversible

---

## Need Help?

Questions about billing, plans, or invoices?

- 💬 **Live Chat**: Click the chat widget (bottom-right corner)
- 📧 **Email**: support@coderom.com or billing@coderom.com
- 📚 **Help Center**: Browse all guides

**Our support team is available 24/7 to assist you!**
      `
    },
    {
      id: 'bulk-import',
      title: 'Bulk Product Import',
      excerpt: 'Learn how to add hundreds of products at once using bulk ASIN import, AI optimization in batches, and automated publishing to scale your eBay store quickly.',
      category: 'bulk-import',
      readTime: '5 min',
      icon: '📦',
      content: `
# Bulk Product Import

Learn how to add hundreds of products to your eBay store in minutes using Coderom's bulk import tools.

---

## Overview

Coderom's **Bulk Import** feature allows you to:
- 📦 Add **hundreds of products** at once using ASIN lists
- 🤖 Optimize **all listings with AI** in one batch
- 🚀 Publish **multiple products** to eBay simultaneously
- ⚡ Manage bulk operations on existing products

**Perfect for:**
- Starting a new eBay store with a large product catalog
- Seasonal inventory updates (e.g., Christmas, Back to School)
- Expanding into new product categories
- Migrating from other dropshipping tools

**Time savings:**
- Manual: ~5 minutes per product = 500 minutes for 100 products (8+ hours)
- Bulk Import: ~15 minutes total for 100 products (97% faster)

---

## ASIN List Import

The fastest way to add products is through the **Bulk ASIN Import** feature.

### Step 1: Prepare Your ASIN List

Create a text list of Amazon ASINs (one per line):

\`\`\`
B07ZPKBL9V
B08N5WRWNW
B09G9HDQPD
B07XYZ1234
B08ABC5678
\`\`\`

**How to find ASINs:**
- **Amazon product URL**: amazon.com/dp/B07ZPKBL9V (the part after /dp/)
- **Product information section** on Amazon (under "Product details")
- **Amazon Best Sellers** lists (high-converting products)
- **Competitor research tools** (e.g., Jungle Scout, Helium 10)

**Tips for building your ASIN list:**
- ✅ One ASIN per line (no commas, no spaces)
- ✅ Remove any extra characters or formatting
- ✅ Maximum: **100 ASINs per import** (plan limits apply)
- ✅ Mix ASINs from different Amazon marketplaces (UK, US, DE, FR, IT, ES, AU, CA)
- ✅ Include the marketplace code if mixing (e.g., B07ZPKBL9V-UK)

**Quality over quantity:**
- Focus on products with 4+ star Amazon reviews
- Check stock availability (avoid "Currently Unavailable")
- Verify price points allow for profitable margins
- Avoid VeRO-risky brands (Nike, Disney, Lego, etc.)

### Step 2: Import ASINs

1. Go to **Products → Add Product**
2. Click **"Bulk Import"** tab at the top
3. Paste your ASIN list into the large textarea
4. Select default source marketplace (UK, US, etc.)
5. **Optional settings**:
   - ☑️ **Fetch Variations** — Import all size/color options (slower but comprehensive)
   - ☑️ **Exclude Out of Stock** — Skip products with no Amazon stock
   - ☑️ **Auto-optimize with AI** — Optimize titles immediately (uses credits)
6. Click **"Import ASINs"**

### Step 3: Scraper Process

Coderom will automatically process each ASIN:

**For each product, the scraper:**
1. ✅ Detects Amazon marketplace automatically (UK, US, DE, etc.)
2. ✅ Scrapes product data:
   - Title (full Amazon title)
   - Brand, model, ASIN
   - All images (up to 12 per product)
   - Price and shipping cost
   - Stock availability
   - Product features ("About this item")
   - Specifications (dimensions, weight, color, size)
3. ✅ Downloads and optimizes images
   - Compresses for faster loading
   - Removes Amazon watermarks (if present)
   - Stores locally for eBay upload
4. ✅ Checks for VeRO brands
   - Flags risky products (Nike, Apple, Disney, etc.)
   - Shows warning badge in drafts
5. ✅ Extracts variation data (if "Fetch Variations" enabled)
   - Sizes, colors, styles
   - Individual pricing and stock per variant
   - Variant-specific images

**Processing time:**
- ~5-10 seconds per product
- 10 products = ~1-2 minutes
- 100 products = ~10-15 minutes total

**Progress tracking:**
You'll see a **real-time progress bar** showing:
- ✅ Products successfully scraped (green)
- ❌ Products failed (red) — invalid ASIN, unavailable, region-locked
- ⏳ Current product being processed
- ⏱️ Estimated time remaining

**Common failure reasons:**
- Invalid ASIN (typo, wrong format)
- Product deleted/unavailable on Amazon
- Region-locked (e.g., US-only ASIN on UK Amazon)
- Amazon bot protection (rare — retry later)

---

## Bulk AI Optimization

After importing, optimize all listings with AI in one click.

### What Gets Optimized?

**1. Category Matching**
- GPT-4o AI scans **20,000+ eBay categories**
- Detects the best category with **98% accuracy**
- Falls back to human review for uncertain matches (<70% confidence)

**2. Title Generation**
- SEO-optimized titles with primary keywords first
- eBay's **80-character limit** compliance
- Removes filler words ("Brand New", "High Quality")
- Includes brand, model, key features
- VeRO-safe (removes risky trademark brands)

**3. Description Creation**
- Professional HTML descriptions
- Highlights product benefits and features
- Mobile-responsive formatting
- eBay TOS compliant (no external links)

**4. Item Specifics**
- Extracts brand, color, size, material, model
- Auto-fills eBay's required fields
- Category-specific attributes (voltage for tools, size for clothing, etc.)

### Running Bulk AI Optimization

1. Go to **Products → Drafts**
2. **Filter** to find unoptimized products:
   - Click **"Status"** dropdown
   - Select **"Not Optimized"**
3. Select products you want to optimize:
   - Click checkboxes next to each product
   - OR click **"Select All"** at the top (selects all visible on page)
   - OR use **"Select All Matching"** to select all 500+ products matching your filter
4. Click **"Bulk Optimize with AI"** button (top-right)
5. Confirm the action
6. Wait for optimization to complete

**Progress tracking:**
- Real-time progress bar (e.g., "Optimizing 47 / 200 products...")
- Skips already optimized products automatically
- Shows failures with error messages (e.g., "No category found for ASIN B123...")

**Processing speed:**
- ~2-3 seconds per product
- 10 products = ~30 seconds
- 100 products = ~4-5 minutes
- 500 products = ~20-25 minutes

**AI optimization uses credits** (check your plan):
- **Starter**: 100 optimizations/month
- **Advanced**: 500 optimizations/month
- **Professional**: 1,000 optimizations/month
- **Business**: 2,000 optimizations/month
- **Enterprise+**: Unlimited optimizations

Check remaining credits: **Settings → Subscription → Usage**

---

## Bulk Publish to eBay

Publish hundreds of optimized listings to eBay in one batch.

### Pre-Publish Checklist

Before bulk publishing, ensure:
- ✅ All products are **optimized with AI** (green ✓ checkmark)
- ✅ **Categories** are assigned (no "Category Not Set" labels)
- ✅ **Prices** are configured (margin rules applied in Settings)
- ✅ **Templates** are selected (or use default template from Settings)
- ✅ **Item specifics** are filled (especially required fields like Brand)

**Common issues that block publishing:**
- ❌ Missing category → AI will auto-detect, but double-check
- ❌ Missing brand → eBay validation fails (add manually)
- ❌ Zero quantity → Product won't list (Amazon is out of stock)
- ❌ Negative margin → Price too low (adjust margin rules)

### Publishing Process

1. Go to **Products → Drafts**
2. **Filter** by **"Optimized"** status to see ready products
3. Select products to publish:
   - Check individual products
   - OR **"Select All"** for batch selection (50 products per page)
   - OR **"Select All Matching"** for unlimited selection
4. Click **"Bulk Publish to eBay"** (top-right)
5. Review summary:
   - Number of products to publish
   - Estimated time
   - eBay marketplace (UK, US, etc.)
6. Click **"Confirm & Publish"**

**Publishing speed:**
- eBay API allows **~50 products every 5 minutes**
- 100 products = ~10 minutes
- 500 products = ~50 minutes
- Background worker processes the queue server-side

**You can close the browser** — publishing continues in the background. You'll receive:
- Real-time browser notifications (if enabled)
- Email summary when batch is complete
- Detailed report of successes and failures

**Success rate:**
- **95%+ publish success rate** on average
- Failed listings show detailed error messages
- Common failures:
  - Item specifics validation errors (missing required fields)
  - VeRO brand detected by eBay
  - Duplicate listing (ASIN already listed)
  - Image upload failures (retry usually fixes)

### Monitoring Published Products

After publishing:
1. Go to **Products → All Products**
2. Filter by **"Published"** status
3. View eBay item IDs and listing URLs
4. Monitor sync status (prices/stock syncing from Amazon)

---

## Bulk Operations on Existing Products

Manage your existing eBay listings in bulk.

### Available Bulk Actions

**1. Bulk Update Prices**
- Update margin rules across multiple products
- Apply percentage-based pricing (e.g., +20% margin)
- Apply fixed-amount pricing (e.g., +£5 markup)
- Changes sync to eBay automatically within 5-15 minutes

**How to:**
1. Select products in **All Products** page
2. **Bulk Actions** → **Update Prices**
3. Set new margin rule
4. Confirm — prices update on eBay

**2. Bulk Update Quantities**
- Override Amazon stock values
- Set fixed quantities for selected products (e.g., limit to 5 max)
- Useful for inventory limits or safety stock
- Changes push to eBay immediately

**3. Bulk Re-optimize**
- Re-run AI optimization on already-published listings
- Update titles/descriptions with improved AI
- Useful after AI model updates
- Changes require manual review before pushing to eBay

**4. Bulk Delete**
- End eBay listings and remove from database
- **Useful for:**
  - Discontinued products
  - Low-profit items
  - VeRO-flagged products
- **Warning:** Cannot be undone — listings are ended on eBay

**5. Bulk Template Change**
- Apply a different listing template to multiple products
- Changes take effect after re-publishing to eBay
- Preview before applying

**6. Bulk Category Change**
- Move products to a different eBay category
- Useful if AI chose wrong category for a batch
- Requires re-publishing to eBay

### How to Use Bulk Operations

1. Go to **Products → All Products**
2. **Filter** products to target specific groups:
   - **By status**: Active, OOS (Out of Stock), Needs Update
   - **By category**: Select eBay category
   - **By profit margin**: < £5, £5-£10, > £10
   - **By risk**: VeRO-flagged, Safe
3. Select products using checkboxes
4. Click **"Bulk Actions"** dropdown (top-right)
5. Choose action:
   - Update Prices
   - Update Quantities
   - Re-optimize
   - Delete
   - Change Template
   - Change Category
6. Configure action settings
7. Click **"Apply to X Products"**
8. Confirm

**Best practices:**
- **Test on 5-10 products first** before bulk-applying to hundreds
- Use filters to **target specific products** (e.g., only "Active" products)
- **Review changes** in the confirmation modal before applying
- **Monitor eBay** for errors after bulk updates (check sync status)

---

## Best Practices

### 1. Start Small, Scale Gradually
- Import **10-20 products** first to test your workflow
- Review AI optimization quality (categories, titles, descriptions)
- Test publishing process (check eBay listings)
- Monitor for errors (sync issues, VeRO warnings)
- **Then** scale to 100+ products

### 2. Use High-Quality ASINs
Choose products with:
- ✅ **Good Amazon reviews** (4+ stars, 50+ reviews)
- ✅ **Multiple high-quality images** (at least 5 images)
- ✅ **Detailed product descriptions** (AI needs context)
- ✅ **Consistent stock availability** (avoid "Currently Unavailable")
- ✅ **Profitable margins** (check Amazon price vs eBay sold listings)

Avoid:
- ❌ **VeRO brands** (Nike, Disney, Lego, Apple, etc.)
- ❌ **Restricted categories** (weapons, medicine, alcohol)
- ❌ **Low-margin products** (< £5 profit after fees)
- ❌ **Oversized/heavy items** (high shipping costs)

### 3. Leverage Filters
Use **Drafts page filters** to organize bulk imports:
- **"Not Optimized"** → needs AI processing
- **"Optimized"** → ready to publish
- **"Failed"** → needs manual review
- **"VeRO Warning"** → risky brands detected

**Saved filters:**
Create custom filter combinations and save them for quick access.

### 4. Monitor Sync Status
After publishing, check **Dashboard → Sync Status**:
- Ensure products are syncing prices/stock from Amazon
- Fix any sync errors before they affect sales
- Common errors:
  - Amazon product deleted
  - Amazon out of stock
  - Region mismatch (US ASIN on UK Amazon)

### 5. Schedule Bulk Operations
Import during **off-peak hours**:
- eBay API is faster outside business hours (midnight-6am)
- Reduces risk of rate limit errors
- Less impact on your active listings (if re-publishing)

### 6. Keep Track of Credits
**AI optimizations** use plan credits:
- Starter: 100/month
- Advanced: 500/month
- Professional: 1,000/month
- Business: 2,000/month (67/day)
- Enterprise+: Unlimited

Check remaining credits: **Settings → Subscription → Usage**

If you run out:
- Wait for next month's reset
- Upgrade plan for more credits
- Manually optimize without AI (slower)

### 7. Review Before Publishing
Spot-check **5-10 listings** before bulk publishing:
- Verify categories make sense
- Check titles are compelling (not just keyword-stuffed)
- Ensure descriptions are accurate
- Confirm prices are profitable
- Look for missing item specifics

### 8. Use Variation Fetching Wisely
**When to enable "Fetch Variations":**
- ✅ Clothing (sizes, colors)
- ✅ Shoes (sizes)
- ✅ Phone cases (models, colors)
- ✅ Multi-packs (quantity options)

**When to disable:**
- ❌ Single-variant products (wastes time)
- ❌ Large catalogs (100+ ASINs) — too slow

**Impact:**
- Fetching variations **3-5x slower** per product
- But creates **one multi-variant listing** instead of separate listings

---

## Troubleshooting

**Q: Import is stuck at "Processing..." for 10+ minutes. What's wrong?**
A: Likely an Amazon rate limit or network timeout. Refresh the page and retry. Failed ASINs will be skipped.

**Q: Half my ASINs failed with "Product not found". Why?**
A: Check if:
- ASINs are valid (10 characters, alphanumeric)
- Products are available on the selected marketplace (UK, US, etc.)
- Products aren't region-locked or deleted from Amazon

**Q: AI optimization failed for some products. Can I retry?**
A: Yes! Select the failed products in Drafts and click "Bulk Optimize with AI" again.

**Q: Bulk publish says "0 products published" but I selected 50. What happened?**
A: Check if:
- Products are already published (filter shows "Published" status)
- Required item specifics are missing (check for warning icons)
- eBay account is connected and authorized

**Q: Can I import from other sources (not Amazon)?**
A: Currently, only Amazon ASINs are supported. AliExpress, Walmart, and other sources are planned for future releases.

**Q: What's the maximum number of products I can import at once?**
A: 100 ASINs per import. If you have more, split into multiple batches or contact support for Enterprise plans (500+ ASINs).

---

**Questions?** Check the **AI Listing** or **Publishing** help articles, or contact support!
      `
    },
    {
      id: 'templates-guide',
      title: 'Professional Listing Templates',
      excerpt: 'Discover Coderom\'s library of 20+ mobile-responsive HTML templates designed to boost conversions and make your eBay listings stand out.',
      category: 'templates',
      readTime: '8 min',
      icon: '🎨',
      content: `
# Professional Listing Templates

Discover Coderom's library of 20+ mobile-responsive HTML templates designed to boost conversions and make your eBay listings stand out.

---

## Overview

Coderom offers **20+ professionally designed HTML templates** that transform your product listings into high-converting sales pages.

**Why templates matter:**
- 📱 **Mobile-optimized**: 70%+ of eBay buyers shop on mobile devices
- 🎨 **Professional design**: Stand out from competitors with plain text listings
- ⚡ **Fast loading**: Optimized HTML/CSS with no heavy images or scripts
- 🔄 **Auto-populated**: Product data fills in automatically from your drafts
- 🌍 **Multi-marketplace**: Works on all eBay sites (UK, US, DE, FR, IT, ES, AU, CA)

**Template features:**
- ✅ Responsive layout (adapts to desktop, tablet, mobile)
- ✅ Product image gallery with zoom functionality
- ✅ Feature highlights section with icons
- ✅ Shipping & returns policy display
- ✅ Brand logo area (if you upload your logo)
- ✅ Clear call-to-action buttons
- ✅ eBay TOS compliant (no external links, JavaScript, or prohibited content)

**Conversion impact:**
- Plain text listings: ~1-2% conversion rate
- Template listings: ~3-5% conversion rate
- **2-3x improvement** on average

---

## Available Templates

Coderom includes **20+ pre-built templates** across multiple design styles. Here's the full collection:

### 🎨 Modern & Clean

**1. coderomModern**
- Clean, minimal design with white background
- Blue accent color scheme
- Perfect for: Electronics, tech products, gadgets
- Features: Large product images, tabbed content sections, modern typography

**2. coderomElegant**
- Sophisticated gray/black color scheme
- Premium feel with elegant serif typography
- Best for: Fashion, jewelry, luxury items, designer products
- Features: Centered layout, subtle hover animations, high-end aesthetic

**3. lightBlue**
- Fresh, clean design with light blue accents
- Excellent readability and simplicity
- Best for: Home goods, kitchen items, general products
- Features: Grid layout, icon-based feature list, airy spacing

**4. grayBlue**
- Professional corporate design
- Gray background with blue highlights
- Best for: Business products, office supplies, professional tools
- Features: Multi-column layout, structured information hierarchy

### 🌈 Colorful & Bold

**5. Pink**
- Vibrant pink and white design
- Eye-catching and playful aesthetic
- Best for: Beauty products, cosmetics, kids items, toys
- Features: Gradient headers, rounded corners, fun typography

**6. Sapphire**
- Deep blue gemstone-inspired design
- Conveys luxury and trust
- Best for: Jewelry, watches, premium accessories, gifts
- Features: Dark background with gold accents, elegant layout

**7. Wheat**
- Warm, earthy tones (beige, brown, cream)
- Natural and organic feel
- Best for: Food products, health items, eco-friendly goods, natural materials
- Features: Textured backgrounds, nature-inspired imagery

**8. blackYellow**
- High-contrast black and yellow
- Bold and attention-grabbing
- Best for: Tools, automotive parts, sports equipment, industrial products
- Features: Strong typography, clear CTAs, high visibility

**9. grayOrange**
- Modern gray with energetic orange highlights
- Professional yet energetic
- Best for: Fitness products, outdoor gear, adventure equipment
- Features: Dynamic layout, action-oriented design

**10. darkBlue**
- Navy blue professional design
- Trust and reliability focused
- Best for: Electronics, business products, professional tools, software
- Features: Clean sections, minimal distractions, trust-building layout

### 🎄 Seasonal & Themed

**11. coderomChristmas**
- Festive red and green design
- Holiday-themed graphics and icons
- Best for: Christmas products, gifts, decorations, seasonal items
- Features: Seasonal icons, festive color palette, gift imagery

**12. coderomAutumn**
- Fall-inspired orange, brown, yellow tones
- Warm and cozy aesthetic
- Best for: Autumn products, home decor, seasonal items, harvest themes
- Features: Leaf patterns, harvest colors, warm imagery

**13. coderomSunshine**
- Bright yellow and orange design
- Happy and energetic vibe
- Best for: Summer products, outdoor items, kids toys, beach gear
- Features: Sun graphics, cheerful layout, vibrant colors

**14. coderomSweet**
- Soft pastel colors (pink, purple, mint)
- Gentle and inviting design
- Best for: Baby products, sweets, gifts, handmade items
- Features: Curved shapes, soft shadows, delicate aesthetic

**15. coderomTropical**
- Vibrant greens and blues
- Beach and vacation theme
- Best for: Summer clothing, travel accessories, outdoor products, beachwear
- Features: Palm leaf graphics, ocean colors, vacation vibes

### 🏪 Business & E-commerce

**16. coderomStorefront**
- Full store layout with navigation header
- Professional e-commerce design
- Best for: Multi-product sellers, brand stores, established businesses
- Features: Header menu, footer with policies, brand showcase area

**17. coderomPremium**
- Luxury black and gold design
- High-end, exclusive feel
- Best for: Premium products, designer items, luxury goods
- Features: Large hero images, elegant spacing, gold accents

**18. coderomCoastal**
- Beach-inspired blues and whites
- Fresh and clean aesthetic
- Best for: Beach products, summer items, nautical themes, coastal decor
- Features: Wave patterns, coastal color palette, fresh design

**19. coderomNature**
- Green and brown earth tones
- Organic and eco-friendly theme
- Best for: Natural products, gardening items, outdoor gear, eco products
- Features: Leaf graphics, natural textures, earthy palette

**20. coderomClassic**
- Timeless, traditional design
- Universal appeal across categories
- Best for: Any product category, general use
- Features: Balanced layout, neutral colors, classic typography

---

## Choosing the Right Template

### Match Template to Product Category

Choose a template that aligns with your product's style and target audience:

**Electronics & Tech** → coderomModern, darkBlue, grayBlue
**Fashion & Clothing** → coderomElegant, Pink, coderomSweet
**Home & Garden** → lightBlue, coderomNature, Wheat, coderomCoastal
**Beauty & Cosmetics** → Pink, Sapphire, coderomSweet
**Automotive & Tools** → blackYellow, grayOrange, darkBlue
**Seasonal Products** → coderomChristmas, coderomAutumn, coderomSunshine
**Luxury Items** → coderomPremium, Sapphire, coderomElegant
**Kids Products** → Pink, coderomSweet, coderomSunshine
**Sports & Fitness** → grayOrange, blackYellow, coderomTropical

### Preview Before Choosing

1. Go to **Products → Add Product** or open any draft
2. Scroll to **"Choose Template"** section in the editor
3. Click on any template thumbnail to see a **live preview**
4. Preview shows your actual product data (images, title, description)
5. Switch between templates instantly to compare
6. Check **mobile preview** by clicking the phone icon (important!)

**Preview tips:**
- Test 3-4 templates to see which one suits your product best
- Check mobile view (70%+ buyers use mobile)
- Consider your brand colors and overall style
- Look at competitor listings for inspiration
- Check how images are displayed (full-width vs contained)

---

## Setting Your Default Template

### Global Template Settings

1. Go to **Settings → Templates**
2. Browse all 20+ templates in a grid view
3. Hover over each template to see a preview
4. Click **"Set as Default"** on your preferred template
5. All new products will automatically use this template

**Benefits of setting a default:**
- ⚡ Saves time — no need to choose template for every product
- 🎨 Consistent branding across your store
- 🔄 Easy to change later (updates future products only)

### Per-Product Template Override

You can override the default template for individual products:

1. Go to **Products → Drafts** or **Edit Draft** page
2. Scroll to **"Template"** dropdown
3. Select a different template
4. Preview changes instantly
5. Save and publish

**Use case:** Different templates for different product categories:
- Electronics → coderomModern
- Fashion → coderomElegant
- Christmas gifts → coderomChristmas
- Summer products → coderomTropical

---

## Customizing Templates

While Coderom templates are ready-to-use out of the box, you can customize certain elements.

### What You Can Customize

**1. Product Description Text**
- Templates auto-fill with AI-generated descriptions
- Edit description in **Draft Editor** using the **CKEditor** WYSIWYG editor
- Add custom sections, bullet points, formatting
- Insert additional images or tables

**2. Brand Logo (Pro Feature)**
- Upload your logo in **Settings → Branding**
- Logo appears in template header area
- Builds brand recognition across all listings
- Supported formats: PNG, JPG, SVG (max 500KB)

**3. Shipping Policy Text**
- Edit shipping/return text in **Settings → Policies**
- Auto-populates in all templates
- Update once, applies everywhere
- Include delivery estimates, return window, conditions

**4. Color Scheme (Advanced)**
- Some templates allow color customization
- Go to **Settings → Templates → Advanced**
- Adjust accent colors, button colors, backgrounds
- Preview changes before applying

**5. Template HTML (Custom)**
- Custom template creation available on **Agency+ plans**
- Contact support with your branding requirements
- Full custom design matching your website/brand guidelines
- Developer access to template HTML/CSS

### Editing Template Content (CKEditor)

When editing a draft, use the **CKEditor WYSIWYG editor** to customize your description:

**CKEditor features:**
1. **Text Formatting** — Bold, italic, underline, colors, font sizes
2. **Headers & Paragraphs** — H2, H3, H4 headers, paragraph spacing
3. **Lists** — Bullet points, numbered lists
4. **Insert Images** — Add extra product images or infographics
5. **Tables** — Create specification tables, sizing charts
6. **Links** — Add YouTube video links (must be eBay-compliant)
7. **HTML Source** — Advanced users can edit raw HTML

**Important restrictions (eBay TOS):**
- ❌ No external links (except YouTube videos)
- ❌ No contact details (email, phone, website)
- ❌ No JavaScript or active content
- ❌ No "Buy now from [external site]" links

**Best practices:**
- ✅ Keep mobile-friendliness in mind (avoid wide tables)
- ✅ Use headers to break up long descriptions
- ✅ Include bullet points for easy scanning
- ✅ Add specification tables for tech products
- ✅ Test preview on mobile before publishing

---

## Template Performance Tips

### 1. Keep Descriptions Concise
- Templates look best with **300-500 words** of description text
- Too much text (1000+ words) overwhelms mobile users
- Use bullet points instead of long paragraphs
- Break content into scannable sections with headers

### 2. Use High-Quality Images
- Templates showcase your images prominently
- Minimum resolution: **1000x1000px** (eBay requirement)
- White background preferred for main image
- Include lifestyle shots (product in use) for secondary images
- Avoid Amazon logos or watermarks

### 3. Highlight Key Features
- 4-6 feature bullet points work best in templates
- Use icons/emojis to draw attention (✅ ⚡ 🎯 📦)
- Focus on benefits, not just technical specs
- Answer "What's in it for the buyer?"

### 4. Mobile-First Thinking
- **70% of eBay buyers use mobile** devices
- Always preview on mobile before publishing
- Avoid tiny text or complex multi-column tables
- Ensure buttons/CTAs are easily tappable (min 44px height)
- Test loading speed (templates are optimized, but images matter)

### 5. Test Different Templates (A/B Testing)
- Run A/B tests with different templates
- Track conversion rates in **Analytics**
- Compare: views, watchers, sold quantities
- Stick with what works for your niche
- Seasonal variations (Christmas template in Dec, Summer in June)

### 6. Update Templates Regularly
- Refresh tired listings with new templates every 6-12 months
- Use seasonal templates for holidays (coderomChristmas, coderomAutumn)
- Keep designs modern and fresh
- Align with current design trends

---

## Templates in Action

Here's how a template transforms a listing:

### Before (Plain Text)

\`\`\`
Product Title: Wireless Mouse 2.4GHz Optical

Description:
This is a wireless mouse. It works on 2.4GHz.
Battery included. Good product.
\`\`\`

**Result:** Boring, unprofessional, low conversions

### After (coderomModern Template)

**Visual Layout:**
- ✅ Hero image banner with main product photo (1200x800px)
- ✅ Formatted title with brand and model in header
- ✅ Feature highlights with icons:
  - 🖱️ 2.4GHz Wireless Technology — No lag or interference
  - 🔋 Long Battery Life — Up to 12 months on a single AA battery
  - ⚡ Plug & Play — No drivers needed, works instantly
  - 📱 Universal Compatibility — Windows, Mac, Linux, ChromeOS
- ✅ Professional description with formatted paragraphs
- ✅ Specifications table:
  - DPI: 1600
  - Dimensions: 11cm x 6cm x 4cm
  - Weight: 85g
  - Connectivity: 2.4GHz USB receiver
- ✅ Shipping & returns policy box (30-day returns, free UK shipping)
- ✅ Call-to-action: "Add to Cart" button (orange, prominent)
- ✅ Trust badges: Fast UK dispatch, buyer protection, secure payment

**Result:** Professional, trustworthy, **3-5% conversion rate** (vs 1-2% plain text)

---

## FAQs

**Q: Can I create my own custom template?**
A: Yes! Custom templates are available on **Agency and Ultimate plans**. Contact support with your branding requirements, and we'll create a bespoke template matching your brand guidelines.

**Q: Do templates work on all eBay marketplaces?**
A: Absolutely! All Coderom templates are fully compatible with eBay UK, US, DE, FR, IT, ES, AU, and CA. HTML renders correctly across all eBay platforms.

**Q: Will templates slow down my listing load time?**
A: No. Coderom templates are highly optimized HTML/CSS with minimal code. They load instantly on all devices. No heavy images or external resources are loaded.

**Q: Can I use templates for variation listings (size/color options)?**
A: Yes! Templates work perfectly with variation listings. Each variation displays correctly with its own image and specifications.

**Q: What if I don't like any template?**
A: Contact support — we can:
1. Recommend a template based on your products
2. Customize an existing template (color scheme, layout tweaks)
3. Create a fully custom template (Agency+ plans)

**Q: Do templates comply with eBay's Terms of Service?**
A: Yes. All Coderom templates are eBay TOS compliant:
- ✅ No external links (except YouTube videos)
- ✅ No JavaScript or active content
- ✅ No contact information
- ✅ No prohibited content
- ✅ Mobile-responsive (eBay requirement)

**Q: Can I switch templates after publishing to eBay?**
A: Yes, but you'll need to **revise the listing** on eBay:
1. Change template in Coderom
2. Click **"Update eBay Listing"**
3. Changes push to eBay within 5-10 minutes

**Q: Are templates included in all plans?**
A: Yes! All 20+ templates are available on **all plans** (Starter to Titan). Custom templates require Agency+ plans.

---

**Ready to transform your listings?** Head to **Products → Add Product** and choose your template, or set your default in **Settings → Templates**!
      `
    },
  ];

  // Check URL hash to open article directly
  useEffect(() => {
    if (location.hash) {
      const articleId = location.hash.replace('#', '');
      const article = popularArticles.find(a => a.id === articleId);
      if (article) {
        setSelectedArticle(article);
        setIsArticleModalOpen(true);
      }
    }
  }, [location.hash]);

  // NOTE: openArticle() removed — both card grids now navigate via real <a href>
  // to the dedicated /help/<slug> pages. The hash-based modal is still opened by
  // the useEffect above (deep links like /help#article-id keep working).

  const closeArticle = () => {
    setSelectedArticle(null);
    setIsArticleModalOpen(false);
    // Remove hash from URL
    window.history.pushState(null, '', location.pathname);
  };

  const videoTutorials = [
    { id: '1', title: 'Complete CodeRom Setup Guide', duration: '12:45', thumbnail: '/images/tutorials/setup.jpg', views: '2.5K' },
    { id: '2', title: 'Mastering AI Product Listings', duration: '8:30', thumbnail: '/images/tutorials/ai-listing.jpg', views: '1.8K' },
    { id: '3', title: 'Advanced Repricing Strategies', duration: '15:20', thumbnail: '/images/tutorials/repricing.jpg', views: '1.2K' },
    { id: '4', title: 'Multi-Store Management Tips', duration: '10:15', thumbnail: '/images/tutorials/multi-store.jpg', views: '950' },
  ];

  const filteredCategories = searchQuery
    ? categories.filter(cat =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cat.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : categories;

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="CodeRom Help Center & Knowledge Base"
        description="Browse CodeRom guides: getting started, AI listing, repricing, stock sync & more."
      />
      <Navbar />
    <div className="help-center">
      {/* Hero Section */}
      <section className="help-hero">
        <div className="hero-bg">
          <div className="hero-pattern" />
          <div className="hero-glow" />
        </div>
        <Container>
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
                        <h1>How can we help you?</h1>
            <p>Search our knowledge base or browse categories below</p>

            {/* Search Bar */}
            <motion.div
              className="search-wrapper"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="search-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search for articles, tutorials, guides..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button className="search-btn">Search</button>
            </motion.div>

            {/* Quick Links */}
            <div className="quick-links">
              <span>Popular:</span>
              <button onClick={() => setSearchQuery('connect ebay')}>Connect eBay</button>
              <button onClick={() => setSearchQuery('repricing')}>Repricing</button>
              <button onClick={() => setSearchQuery('AI listing')}>AI Listing</button>
              <button onClick={() => setSearchQuery('templates')}>Templates</button>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Categories Grid */}
      <section className="categories-section">
        <Container>
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Browse by Category</h2>
            <p>Find answers organized by topic</p>
          </motion.div>

          <Row className="g-4">
            {filteredCategories.map((category, index) => (
              <Col lg={3} md={4} sm={6} key={category.id}>
                {/* SEO: real <a href> so Googlebot can crawl help articles.
                    category.id is identical to the URL slug for all 16 categories. */}
                <a
                  href={`/help/${category.id}`}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}
                >
                  <motion.div
                    className="category-card"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                    style={{ '--category-color': category.color } as React.CSSProperties}
                  >
                    <div className="category-icon">{category.icon}</div>
                    <h3>{category.name}</h3>
                    <p>{category.description}</p>
                    <span className="article-count">{category.articleCount} articles</span>
                  </motion.div>
                </a>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Popular Articles */}
      <section className="articles-section">
        <Container>
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Popular Articles</h2>
            <p>Most viewed help articles this week</p>
          </motion.div>

          <Row className="g-4">
            {popularArticles.map((article, index) => (
              <Col lg={4} md={6} key={article.id}>
                {/* SEO: real <a href> so Googlebot can crawl help articles.
                    article.category is identical to the URL slug for all 16 articles. */}
                <a
                  href={`/help/${article.category}`}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}
                >
                  <motion.div
                    className="article-card"
                    id={article.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -5 }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="article-icon">{article.icon}</div>
                    <div className="article-content">
                      <span className="article-category">{article.category}</span>
                      <h4>{article.title}</h4>
                      <p>{article.excerpt}</p>
                      <div className="article-meta">
                        <span className="read-time">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          {article.readTime} read
                        </span>
                        <span className="read-more">Read article →</span>
                      </div>
                    </div>
                  </motion.div>
                </a>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Video Tutorials */}
      <section className="videos-section">
        <Container>
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Video Tutorials</h2>
            <p>Watch step-by-step guides</p>
          </motion.div>

          <Row className="g-4">
            {videoTutorials.map((video, index) => (
              <Col lg={3} md={6} key={video.id}>
                <motion.div
                  className="video-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="video-thumbnail">
                    <div className="thumbnail-placeholder">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    </div>
                    <span className="video-duration">{video.duration}</span>
                  </div>
                  <div className="video-info">
                    <h4>{video.title}</h4>
                    <span className="video-views">{video.views} views</span>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Still Need Help */}
      <section className="help-cta">
        <Container>
          <motion.div
            className="cta-wrapper"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Row className="align-items-center">
              <Col lg={8}>
                <h2>Still need help?</h2>
                <p>Our support team is available 24/7 to assist you with any questions.</p>
              </Col>
              <Col lg={4} className="text-lg-end">
                <div className="cta-buttons">
                  <a href="mailto:support@coderom.com" className="btn-primary">
                    Contact Support
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </a>
                </div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Article Modal */}
      <Modal
        isOpen={isArticleModalOpen}
        toggle={closeArticle}
        size="lg"
        centered
        className="article-modal"
      >
        <ModalBody className="p-0">
          {selectedArticle && (
            <div className="article-modal-content">
              <div className="article-modal-header">
                <div className="article-modal-icon">{selectedArticle.icon}</div>
                <div>
                  <span className="article-modal-category">{selectedArticle.category}</span>
                  <h2>{selectedArticle.title}</h2>
                  <div className="article-modal-meta">
                    <span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      {selectedArticle.readTime} read
                    </span>
                  </div>
                </div>
                <button className="article-modal-close" onClick={closeArticle}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="article-modal-body">
                <div
                  className="article-content-markdown"
                  dangerouslySetInnerHTML={{
                    __html: selectedArticle.content
                      ? selectedArticle.content
                          .replace(/^## (.*$)/gim, '<h2>$1</h2>')
                          .replace(/^### (.*$)/gim, '<h3>$1</h3>')
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\*(.*?)\*/g, '<em>$1</em>')
                          .replace(/`{3}([\s\S]*?)`{3}/g, '<pre><code>$1</code></pre>')
                          .replace(/`([^`]+)`/g, '<code>$1</code>')
                          .replace(/^\- (.*$)/gim, '<li>$1</li>')
                          .replace(/^(\d+)\. (.*$)/gim, '<li>$2</li>')
                          .replace(/(<li>[\s\S]*<\/li>)/g, '<ul>$1</ul>')
                          .replace(/\n\n/g, '</p><p>')
                          .replace(/\| (.*) \| (.*) \|/g, '<tr><td>$1</td><td>$2</td></tr>')
                      : ''
                  }}
                />
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      <style>{`
        .help-center {
          min-height: 100vh;
          background: #f8fafc;
        }

        .help-hero {
          position: relative;
          padding: 120px 0 80px;
          overflow: hidden;
        }

        .hero-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(30, 41, 59, 0.72) 0%, rgba(15, 23, 42, 0.55) 100%),
                      url('https://images.unsplash.com/photo-1497215842964-222b430dc094?w=1920&q=80') center/cover no-repeat;
        }

        .hero-pattern {
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle at 2px 2px, rgba(255,255,255,0.1) 1px, transparent 0);
          background-size: 40px 40px;
        }

        .hero-glow {
          position: absolute;
          top: -50%;
          right: -20%;
          width: 80%;
          height: 200%;
          background: radial-gradient(ellipse, rgba(249, 115, 22, 0.15) 0%, transparent 60%);
        }

        .hero-content {
          position: relative;
          z-index: 1;
          text-align: center;
          color: white;
          max-width: 700px;
          margin: 0 auto;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: rgba(255,255,255,0.7);
          text-decoration: none;
          font-size: 14px;
          margin-bottom: 32px;
          transition: color 0.2s;
        }

        .back-link:hover {
          color: white;
        }

        .help-hero h1 {
          font-size: 52px;
          font-weight: 800;
          margin-bottom: 16px;
        }

        .help-hero > .hero-content > p {
          font-size: 20px;
          opacity: 0.8;
          margin-bottom: 40px;
        }

        .search-wrapper {
          display: flex;
          align-items: center;
          background: white;
          border-radius: 16px;
          padding: 8px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          max-width: 600px;
          margin: 0 auto 24px;
        }

        .search-icon {
          padding: 0 16px;
          color: #9ca3af;
        }

        .search-wrapper input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 16px;
          padding: 12px 0;
          color: #1e293b;
        }

        .search-wrapper input::placeholder {
          color: #9ca3af;
        }

        .search-btn {
          padding: 12px 28px;
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .search-btn:hover {
          transform: scale(1.02);
          box-shadow: 0 8px 20px rgba(249, 115, 22, 0.4);
        }

        .quick-links {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .quick-links span {
          color: rgba(255,255,255,0.6);
          font-size: 14px;
        }

        .quick-links button {
          padding: 6px 14px;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          color: white;
          border-radius: 20px;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .quick-links button:hover {
          background: rgba(255,255,255,0.2);
          border-color: rgba(255,255,255,0.3);
        }

        .section-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .section-header h2 {
          font-size: 32px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 8px;
        }

        .section-header p {
          color: #64748b;
          font-size: 16px;
        }

        .categories-section {
          padding: 80px 0;
        }

        .category-card {
          background: white;
          border-radius: 16px;
          padding: 28px;
          cursor: pointer;
          border: 1px solid #e2e8f0;
          transition: all 0.3s;
          height: 100%;
        }

        .category-card:hover {
          border-color: var(--category-color);
        }

        .category-icon {
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(249, 115, 22, 0.1), rgba(251, 146, 60, 0.1));
          border-radius: 14px;
          color: var(--category-color, #f97316);
          margin-bottom: 20px;
        }

        .category-card h3 {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 8px;
        }

        .category-card p {
          font-size: 14px;
          color: #64748b;
          line-height: 1.5;
          margin-bottom: 16px;
        }

        .article-count {
          font-size: 13px;
          color: var(--category-color, #f97316);
          font-weight: 600;
        }

        .articles-section {
          padding: 0 0 80px;
        }

        .article-card {
          display: flex;
          gap: 20px;
          background: white;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #e2e8f0;
          transition: all 0.3s;
          cursor: pointer;
          height: 100%;
        }

        .article-card:hover {
          border-color: #f97316;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        }

        .article-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
          border-radius: 12px;
          font-size: 24px;
          flex-shrink: 0;
        }

        .article-content {
          flex: 1;
        }

        .article-category {
          display: inline-block;
          font-size: 12px;
          color: #f97316;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .article-card h4 {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 8px;
          line-height: 1.4;
        }

        .article-card p {
          font-size: 14px;
          color: #64748b;
          line-height: 1.5;
          margin-bottom: 12px;
        }

        .article-meta {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .read-time {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #94a3b8;
        }

        .read-more {
          font-size: 13px;
          color: #f97316;
          font-weight: 600;
        }

        .videos-section {
          padding: 80px 0;
          background: white;
        }

        .video-card {
          border-radius: 16px;
          overflow: hidden;
          background: #f8fafc;
          cursor: pointer;
          transition: all 0.3s;
        }

        .video-thumbnail {
          position: relative;
          aspect-ratio: 16/9;
          background: linear-gradient(135deg, #1e293b, #334155);
        }

        .thumbnail-placeholder {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          opacity: 0.7;
        }

        .video-card:hover .thumbnail-placeholder {
          opacity: 1;
        }

        .video-duration {
          position: absolute;
          bottom: 10px;
          right: 10px;
          padding: 4px 8px;
          background: rgba(0,0,0,0.8);
          color: white;
          font-size: 12px;
          border-radius: 4px;
          font-weight: 500;
        }

        .video-info {
          padding: 16px;
        }

        .video-info h4 {
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .video-views {
          font-size: 13px;
          color: #64748b;
        }

        .help-cta {
          padding: 80px 0;
        }

        .cta-wrapper {
          background: linear-gradient(135deg, #1e293b, #334155);
          border-radius: 24px;
          padding: 48px;
          color: white;
        }

        .cta-wrapper h2 {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .cta-wrapper p {
          font-size: 16px;
          opacity: 0.8;
        }

        .cta-buttons {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: white;
          text-decoration: none;
          font-weight: 600;
          border-radius: 12px;
          transition: all 0.3s;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(249, 115, 22, 0.4);
          color: white;
        }

        @media (max-width: 991px) {
          .help-hero h1 {
            font-size: 36px;
          }

          .cta-wrapper {
            text-align: center;
          }

          .cta-buttons {
            justify-content: center;
            margin-top: 24px;
          }
        }

        @media (max-width: 576px) {
          .help-hero {
            padding: 100px 0 60px;
          }

          .help-hero h1 {
            font-size: 28px;
          }

          .search-wrapper {
            flex-direction: column;
            gap: 12px;
            padding: 16px;
          }

          .search-icon {
            display: none;
          }

          .search-btn {
            width: 100%;
          }
        }

        /* Article Modal Styles */
        .article-modal .modal-content {
          border: none;
          border-radius: 20px;
          overflow: hidden;
        }

        .article-modal-content {
          max-height: 85vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .article-modal-header {
          display: flex;
          align-items: flex-start;
          gap: 20px;
          padding: 32px;
          background: linear-gradient(135deg, #1e293b, #334155);
          color: white;
          position: relative;
        }

        .article-modal-icon {
          width: 64px;
          height: 64px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.1);
          border-radius: 16px;
          font-size: 32px;
          flex-shrink: 0;
        }

        .article-modal-header h2 {
          font-size: 24px;
          font-weight: 700;
          margin: 8px 0;
          line-height: 1.3;
        }

        .article-modal-category {
          display: inline-block;
          padding: 4px 12px;
          background: rgba(249, 115, 22, 0.2);
          color: #fb923c;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .article-modal-meta {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-top: 8px;
          opacity: 0.8;
          font-size: 14px;
        }

        .article-modal-meta span {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .article-modal-close {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.1);
          border: none;
          border-radius: 10px;
          color: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .article-modal-close:hover {
          background: rgba(255,255,255,0.2);
        }

        .article-modal-body {
          padding: 32px;
          overflow-y: auto;
          flex: 1;
          max-height: 50vh;
        }

        .article-content-markdown {
          color: #334155;
          line-height: 1.8;
          font-size: 15px;
        }

        .article-content-markdown h2 {
          font-size: 22px;
          font-weight: 700;
          color: #1e293b;
          margin: 32px 0 16px;
          padding-bottom: 8px;
          border-bottom: 2px solid #e2e8f0;
        }

        .article-content-markdown h2:first-child {
          margin-top: 0;
        }

        .article-content-markdown h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 24px 0 12px;
        }

        .article-content-markdown p {
          margin-bottom: 16px;
        }

        .article-content-markdown strong {
          color: #1e293b;
          font-weight: 600;
        }

        .article-content-markdown code {
          background: #f1f5f9;
          padding: 2px 8px;
          border-radius: 4px;
          font-family: 'Fira Code', monospace;
          font-size: 13px;
          color: #e11d48;
        }

        .article-content-markdown pre {
          background: #1e293b;
          padding: 20px;
          border-radius: 12px;
          overflow-x: auto;
          margin: 20px 0;
        }

        .article-content-markdown pre code {
          background: none;
          color: #e2e8f0;
          padding: 0;
        }

        .article-content-markdown li {
          margin-bottom: 8px;
          padding-left: 8px;
        }

        .article-content-markdown ul {
          list-style-type: disc;
          padding-left: 24px;
          margin: 16px 0;
        }

        .article-content-markdown table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }

        .article-content-markdown td {
          padding: 12px;
          border: 1px solid #e2e8f0;
        }

        .article-content-markdown tr:first-child td {
          background: #f8fafc;
          font-weight: 600;
        }
      `}</style>
    </div>
    <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpCenter;
